// Escalas clínicas y campos estructurados para la evolución por sistema.
//
// Traslada a la evolución el "detalle" del PAE Detallado: las escalas del set
// esencial de turno (Glasgow, RASS, EVA, CPOT, CAM-ICU, Braden, LPP, PAM,
// SpO₂/FiO₂) más algunos campos estructurados (pupilas, signos, diuresis…).

// Catálogos de piel compartidos con el mapa del examen físico (máxima
// coherencia: la LPP de la evolución habla las MISMAS zonas y coberturas que
// la foto de ingreso). pielLpp.js es puro y sin imports: no hay ciclo.
import { ZONAS_PRESION, COBERTURAS_LESION } from './pielLpp';
//
// Módulo puro y autocontenido: las definiciones y el puntaje viven aquí para no
// acoplar la UI de Escalas. Cada escala expone `score(vals)` →
// { texto, label, nivel } | null. Ojo: Downton dejó de ser "una copia más" —
// desde la auditoría del 2026-08-16 este archivo es su FUENTE ÚNICA y las otras
// dos superficies (la calculadora de Escalas y el PAE Detallado) importan de
// acá el catálogo y la aritmética. Tres copias en desacuerdo eran el bug.

// ── Ítems de escalas multi-fila (suma de puntajes) ────────────────────────────
const GLASGOW_ITEMS = [
  { k: 'ocular', label: 'Apertura ocular',  opts: [{ v: 4, l: 'Espontánea' }, { v: 3, l: 'Al estímulo verbal' }, { v: 2, l: 'Al dolor' }, { v: 1, l: 'Ausente' }] },
  { k: 'verbal', label: 'Respuesta verbal', opts: [{ v: 5, l: 'Orientado' }, { v: 4, l: 'Confuso' }, { v: 3, l: 'Palabras inapropiadas' }, { v: 2, l: 'Sonidos incomprensibles' }, { v: 1, l: 'Ausente' }, { v: 'T', l: 'No evaluable (T)' }] },
  { k: 'motora', label: 'Respuesta motora', opts: [{ v: 6, l: 'Obedece órdenes' }, { v: 5, l: 'Localiza dolor' }, { v: 4, l: 'Retira al dolor' }, { v: 3, l: 'Flexión anormal' }, { v: 2, l: 'Extensión anormal' }, { v: 1, l: 'Ausente' }] },
];

const CPOT_ITEMS = [
  { k: 'facial',     label: 'Expresión facial',           opts: [{ v: 0, l: 'Relajada' }, { v: 1, l: 'Tensa' }, { v: 2, l: 'Mueca de dolor' }] },
  { k: 'movim',      label: 'Movimientos corporales',     opts: [{ v: 0, l: 'Ausentes' }, { v: 1, l: 'De protección' }, { v: 2, l: 'Agitación' }] },
  { k: 'tension',    label: 'Tensión muscular',           opts: [{ v: 0, l: 'Relajado' }, { v: 1, l: 'Tenso / rígido' }, { v: 2, l: 'Muy rígido' }] },
  { k: 'ventilador', label: 'Ventilador / vocalización',  opts: [{ v: 0, l: 'Tolera / no vocaliza' }, { v: 1, l: 'Alarma / gemidos' }, { v: 2, l: 'Lucha / llanto' }] },
];

const BRADEN_ITEMS = [
  { k: 'percepcion', label: 'Percepción sensorial',     opts: [{ v: 1, l: 'Completamente limitada' }, { v: 2, l: 'Muy limitada' }, { v: 3, l: 'Levemente limitada' }, { v: 4, l: 'Sin alteración' }] },
  { k: 'humedad',    label: 'Humedad de la piel',       opts: [{ v: 1, l: 'Constantemente húmeda' }, { v: 2, l: 'Muy húmeda' }, { v: 3, l: 'Ocasionalmente húmeda' }, { v: 4, l: 'Raramente húmeda' }] },
  { k: 'actividad',  label: 'Actividad',                opts: [{ v: 1, l: 'Encamado' }, { v: 2, l: 'En silla' }, { v: 3, l: 'Camina ocasionalmente' }, { v: 4, l: 'Camina frecuentemente' }] },
  { k: 'movilidad',  label: 'Movilidad',                opts: [{ v: 1, l: 'Completamente inmóvil' }, { v: 2, l: 'Muy limitada' }, { v: 3, l: 'Levemente limitada' }, { v: 4, l: 'Sin limitación' }] },
  { k: 'nutricion',  label: 'Nutrición',                opts: [{ v: 1, l: 'Muy pobre' }, { v: 2, l: 'Probablemente inadecuada' }, { v: 3, l: 'Adecuada' }, { v: 4, l: 'Excelente' }] },
  { k: 'friccion',   label: 'Fricción / cizallamiento', opts: [{ v: 1, l: 'Problema importante' }, { v: 2, l: 'Problema potencial' }, { v: 3, l: 'Sin problema aparente' }] },
];

const RASS_OPTS = [
  { v: '+4', l: '+4 · Combativo' }, { v: '+3', l: '+3 · Muy agitado' }, { v: '+2', l: '+2 · Agitado' }, { v: '+1', l: '+1 · Inquieto' },
  { v: '0', l: '0 · Alerta y tranquilo' },
  { v: '-1', l: '-1 · Somnoliento' }, { v: '-2', l: '-2 · Sedación leve' }, { v: '-3', l: '-3 · Sedación moderada' }, { v: '-4', l: '-4 · Sedación profunda' }, { v: '-5', l: '-5 · No responde' },
];

const CAMICU_OPTS = [{ v: 'Negativo', l: 'Negativo' }, { v: 'Positivo', l: 'Positivo' }, { v: 'No evaluable', l: 'No evaluable' }];

// Categorías de LPP con los nombres de la Orientación Técnica MINSAL (que
// traduce el sistema NPIAP) y descripciones del capítulo de Clasificación de
// la guía internacional (4.ª ed., 2026). Hasta el 2026-10-10 decía "Grado 1…4"
// y "No estadificable": los CÓDIGOS (g1…dtpi) no cambian, así que un valor
// guardado se sigue leyendo; solo cambia cómo se nombra. No clasificable y
// tejidos profundos son daño profundo o que no se ve: severos.
const LPP_OPTS = [
  { v: 'g1', l: 'Categoría I', desc: 'Piel intacta con eritema localizado que no blanquea.', nivel: 'leve' },
  { v: 'g2', l: 'Categoría II', desc: 'Pérdida parcial del espesor con dermis expuesta.', nivel: 'moderado' },
  { v: 'g3', l: 'Categoría III', desc: 'Pérdida total del espesor de la piel.', nivel: 'severo' },
  { v: 'g4', l: 'Categoría IV', desc: 'Pérdida total con fascia, músculo, tendón, ligamento, cartílago o hueso expuesto o palpable.', nivel: 'severo' },
  { v: 'ne', l: 'No clasificable', desc: 'Extensión no visible, tapada por esfacelo o escara.', nivel: 'severo' },
  { v: 'dtpi', l: 'Tejidos profundos', desc: 'Decoloración persistente que no blanquea, granate, morada o marrón, o ampolla con sangre.', nivel: 'severo' },
];

// GLOBIAD (Beeckman y cols., 2018): la categoría se decide por enrojecimiento
// persistente o pérdida de piel, y por la presencia de signos clínicos de
// infección. Antes decía "eritema leve": la intensidad no es criterio.
const DAI_OPTS = [
  { v: '1a', l: 'Categoría 1A', desc: 'Enrojecimiento persistente, sin signos clínicos de infección.', nivel: 'leve' },
  { v: '1b', l: 'Categoría 1B', desc: 'Enrojecimiento persistente, con signos clínicos de infección.', nivel: 'leve' },
  { v: '2a', l: 'Categoría 2A', desc: 'Pérdida de piel, sin signos clínicos de infección.', nivel: 'severo' },
  { v: '2b', l: 'Categoría 2B', desc: 'Pérdida de piel, con signos clínicos de infección.', nivel: 'severo' },
];

// Mottling score de Ait-Oufella (2011): se puntúa SOLO en las piernas, de la
// rodilla hacia la periferia, por la extensión del moteado. Las opciones 2 a 5
// describían antes ombligo, abdomen y extremidades superiores, que no existen
// en el instrumento: con moteado hasta la mitad del muslo (un 3 real) la
// enfermera elegía "2 · cara anterior del muslo" y sub-puntuaba, con el
// semáforo pintando leve donde correspondía severo. Y el 5 afirmaba "shock
// refractario", que es un diagnóstico médico: el score predice mortalidad, no
// diagnostica. Corregido contra la fuente en la auditoría clínica 2026-08-16.
const MOTTLING_OPTS = [
  { v: 0, l: '0 · Sin moteado', d: 'Piel sin moteado' },
  { v: 1, l: '1 · Centro de la rodilla', d: 'Área del tamaño de una moneda sobre la rodilla' },
  { v: 2, l: '2 · Hasta el borde de la rótula', d: 'No sobrepasa el borde superior de la rótula' },
  { v: 3, l: '3 · Mitad del muslo', d: 'No sobrepasa la mitad del muslo' },
  { v: 4, l: '4 · Hasta el pliegue inguinal', d: 'No sobrepasa el pliegue inguinal' },
  { v: 5, l: '5 · Sobre el pliegue inguinal', d: 'Sobrepasa el pliegue inguinal' },
];

const SAS_OPTS = [
  { v: 7, l: '7 · Agitación peligrosa' }, { v: 6, l: '6 · Muy agitado' }, { v: 5, l: '5 · Agitado' },
  { v: 4, l: '4 · Tranquilo y cooperador' }, { v: 3, l: '3 · Sedado' }, { v: 2, l: '2 · Muy sedado' }, { v: 1, l: '1 · No despertable' },
];

const FOUR_ITEMS = [
  { k: 'ojos',   label: 'E · Respuesta ocular',  opts: [{ v: 4, l: '4 · Seguimiento o parpadeo a la orden' }, { v: 3, l: '3 · Abiertos, sin seguimiento' }, { v: 2, l: '2 · Abre a la voz' }, { v: 1, l: '1 · Abre al dolor' }, { v: 0, l: '0 · Sin respuesta' }] },
  { k: 'motora', label: 'M · Respuesta motora',  opts: [{ v: 4, l: '4 · Señal con la mano' }, { v: 3, l: '3 · Localiza el dolor' }, { v: 2, l: '2 · Flexión al dolor' }, { v: 1, l: '1 · Extensión al dolor' }, { v: 0, l: '0 · Sin respuesta / mioclonías' }] },
  { k: 'tronco', label: 'B · Reflejos de tronco', opts: [{ v: 4, l: '4 · Pupilares y corneales presentes' }, { v: 3, l: '3 · Una pupila dilatada y fija' }, { v: 2, l: '2 · Pupilares o corneales ausentes' }, { v: 1, l: '1 · Pupilares y corneales ausentes' }, { v: 0, l: '0 · Sin reflejos' }] },
  { k: 'resp',   label: 'R · Respiración',       opts: [{ v: 4, l: '4 · No intubado, regular' }, { v: 3, l: '3 · No intubado, Cheyne-Stokes' }, { v: 2, l: '2 · No intubado, irregular' }, { v: 1, l: '1 · Sobre la frecuencia del ventilador' }, { v: 0, l: '0 · En frecuencia del ventilador o apnea' }] },
];

// Caídas: DOWNTON es el estándar de MiPAE (decisión de producto 2026-07-06,
// alineado con PAE Detallado). Morse se conserva definida para que los
// registros históricos que la usaron sigan renderizando y para hospitales que
// la pidan, pero ya no se ofrece en la evolución.
const MORSE_ITEMS = [
  { k: 'historia',     label: 'Historia de caídas (últimos 3 meses)',  opts: [{ v: 0, l: 'No' }, { v: 25, l: 'Sí' }] },
  { k: 'diagnostico',  label: 'Diagnóstico secundario',       opts: [{ v: 0, l: 'No' }, { v: 15, l: 'Sí' }] },
  { k: 'ayuda',        label: 'Ayuda para deambular',         opts: [{ v: 0, l: 'Ninguna, reposo o asistencia de enfermería' }, { v: 15, l: 'Muletas, bastón o andador' }, { v: 30, l: 'Se apoya en el mobiliario' }] },
  { k: 'terapiaEv',    label: 'Terapia EV / dispositivo EV',  opts: [{ v: 0, l: 'No' }, { v: 20, l: 'Sí' }] },
  { k: 'marcha',       label: 'Marcha',                       opts: [{ v: 0, l: 'Normal, reposo o silla de ruedas' }, { v: 10, l: 'Débil' }, { v: 20, l: 'Deteriorada' }] },
  { k: 'estadoMental', label: 'Estado mental',                opts: [{ v: 0, l: 'Consciente de sus limitaciones' }, { v: 15, l: 'Sobrestima u olvida limitaciones' }] },
];


// ── Puntajes (portados de Escalas / PAE Detallado) ────────────────────────────
function scoreItems(items, vals) {
  if (!items.every((i) => vals[i.k] !== undefined && vals[i.k] !== null)) return null;
  return items.reduce((s, i) => s + (Number(vals[i.k]) || 0), 0);
}

// Glasgow (GCS): el `nivel` interno (semáforo leve/moderado/severo) se
// conserva — sigue coloreando el chip/pista igual que antes. Lo que cambia es
// el TEXTO visible: "Leve/Moderado/Severo" es la clasificación de TEC, y
// aplicada a cualquier paciente (no solo trauma) confundía (feedback: "sacar
// de GSC el leve y todo eso"). Ahora se muestra lo que de verdad se registró:
// el desglose por componente (O/V/M) + el total, ej. "Glasgow 15/15 (O4 V5
// M6)" — sin etiqueta de gravedad.
const scoreGlasgow = (vals) => {
  const { ocular, verbal, motora } = vals;
  if (ocular === undefined || verbal === undefined || motora === undefined) return null;
  // El valor legado T registra que V no es evaluable. Esta función no recibe
  // la vía aérea: atribuirlo a TET inventaba ese dispositivo en pacientes TQT.
  // El motivo se documenta por separado; conservamos los componentes y la
  // aritmética del registro, sin reinterpretar la evaluación del profesional.
  const verbalNoEvaluable = verbal === 'T';
  const o = parseInt(ocular, 10);
  const m = parseInt(motora, 10);
  if (verbalNoEvaluable) {
    const total = o + m;
    const nivel = total <= 5 ? 'severo' : total <= 8 ? 'moderado' : 'leve';
    return { nivel, label: `${total}T/10`, texto: `Glasgow ${total}T/10 (O${o} M${m}, V no evaluable)` };
  }
  const v = parseInt(verbal, 10);
  const total = o + v + m;
  const nivel = total <= 8 ? 'severo' : total <= 12 ? 'moderado' : 'leve';
  return { nivel, label: `${total}/15`, texto: `Glasgow ${total}/15 (O${o} V${v} M${m})` };
};

// El CPOT es un tamizaje conductual DICOTÓMICO, no una escala de intensidad:
// la literatura valida UN corte (más de 2 = hay dolor) y describe 0 a 2 como
// "sin dolor o mínimo" (Gélinas 2006; validaciones en español, portugués,
// polaco y chino; PADIS 2018 de la SCCM). La banda "leve" con 1 o 2 puntos era
// invención de MiPAE, y de tipo equivocado: le atribuía INTENSIDAD a un
// instrumento que solo detecta PRESENCIA. Hacía daño real: contradecía el
// "dolor no evaluable" que la enfermera acababa de firmar, y dejaba por escrito
// un dolor sin tratar que Calidad audita contra ella (la app fabricando el
// hallazgo que después la acusa). Con `nivel: 'normal'` la clasificación no
// viaja a la prosa, así que el documento no afirma presencia NI ausencia:
// imprime "CPOT 1/8" y punto, que es como lo escribe el NORTE del fundador
// ("CPOT menor de 3 puntos"). El juicio de la enfermera viaja aparte, en el
// campo `dolor`, que es suyo. Auditoría clínica 2026-08-16.
const scoreCpot = (vals) => {
  const total = scoreItems(CPOT_ITEMS, vals);
  if (total === null) return null;
  const significativo = total >= 3;
  const clasificacion = significativo ? 'Dolor significativo' : 'Sin dolor significativo';
  // La referencia al corte es didáctica de la UI: `clasificacion` (lo único que
  // puede viajar a prosa) va sin ella, mismo trato que la conducta genérica.
  const pista = significativo ? clasificacion : `${clasificacion} (bajo el corte de 3)`;
  return {
    nivel: significativo ? 'severo' : 'normal',
    label: `${total}/8`, texto: `CPOT ${total}/8: ${pista}`, clasificacion,
  };
};

// Clasificación de Braden a partir del TOTAL, aparte del cálculo por ítems:
// el puntaje que la enfermera RECIBE del turno anterior (Bupa lo hace de
// noche) llega como número, no como ítems, y tiene que clasificarse con los
// mismos cortes. Fuente única para el score, la recepción y el tablero.
//
// Cortes de la Norma Técnica N° 234 del MINSAL (2023, página 16), adoptados el
// 2026-10-10 por decisión del fundador: riesgo alto con 12 o menos, moderado de
// 13 a 15 y bajo con 16 o más. La norma no tiene "riesgo muy alto" ni "sin
// riesgo": antes la app usaba la convención internacional (≤9 muy alto, 15 a 18
// bajo, ≥19 sin riesgo) y con ella un 15 salía "riesgo bajo" donde la norma
// chilena dice moderado. El `nivel` (el semáforo) ahora calza uno a uno con la
// clasificación: alto es severo, moderado es moderado y bajo es normal (la
// clasificación normal no viaja a la prosa, igual que antes con 15 a 23). Las
// notas ya escritas y los puntajes guardados no se tocan: el puntaje se
// conserva y se vuelve a clasificar al leer; lo que la persona escribió con los
// cortes anteriores queda como lo escribió. Solo para adultos: estos cortes no
// se extienden a escalas pediátricas ni neonatales.
export const REFERENCIA_CORTES_BRADEN = {
  fuente: 'MINSAL, Norma Técnica N° 234 de prevención de LPP (2023), página 16',
  corta: 'NT 234 del MINSAL',
  texto: '12 o menos riesgo alto, 13 a 15 moderado, 16 o más bajo',
};
export const clasificarBradenTotal = (total) => {
  const t = Number(total);
  if (!Number.isFinite(t) || t < 6 || t > 23) return null;
  const nivel = t <= 12 ? 'severo' : t <= 15 ? 'moderado' : 'normal';
  const desc = t <= 12 ? 'Riesgo alto' : t <= 15 ? 'Riesgo moderado' : 'Riesgo bajo';
  return { nivel, clasificacion: desc, label: `${t}/23` };
};

const scoreBraden = (vals) => {
  const total = scoreItems(BRADEN_ITEMS, vals);
  if (total === null) return null;
  const c = clasificarBradenTotal(total);
  return { nivel: c.nivel, label: c.label, texto: `Braden ${c.label}: ${c.clasificacion}`, clasificacion: c.clasificacion };
};

const scoreRass = (vals) => {
  if (!vals.rass) return null;
  const n = parseFloat(vals.rass);
  const opt = RASS_OPTS.find((o) => o.v === vals.rass);
  const nivel = n >= 2 ? 'severo' : n >= 1 ? 'moderado' : n === 0 ? 'normal' : n >= -2 ? 'leve' : 'info';
  const desc = opt ? opt.l.split('· ')[1] : vals.rass;
  return { nivel, label: `RASS ${vals.rass}`, texto: `RASS ${vals.rass}: ${desc}`, clasificacion: desc };
};

const scoreCamicu = (vals) => {
  if (!vals.cam_icu) return null;
  const nivel = vals.cam_icu === 'Positivo' ? 'severo' : vals.cam_icu === 'No evaluable' ? 'info' : 'normal';
  return { nivel, label: vals.cam_icu, texto: `CAM-ICU ${vals.cam_icu}` };
};

// CAM (sin ICU): el mismo tamizaje para el paciente que NO está en UCI, donde
// CAM-ICU no aplica (feedback de Tania Sánchez, jefa de UPC, 2026-08-08:
// "faltaría CAM"). Se ofrece junto a CAM-ICU y el enfermero usa la que
// corresponde a su unidad; no se elige por él porque la app no sabe en qué
// servicio está esa cama.
const scoreCam = (vals) => {
  if (!vals.cam) return null;
  const nivel = vals.cam === 'Positivo' ? 'severo' : vals.cam === 'No evaluable' ? 'info' : 'normal';
  return { nivel, label: vals.cam, texto: `CAM ${vals.cam}` };
};

const scoreEva = (vals) => {
  const n = vals.eva;
  if (n === undefined || n === null) return null;
  const nivel = n === 0 ? 'normal' : n <= 3 ? 'leve' : n <= 6 ? 'moderado' : 'severo';
  const desc = n === 0 ? 'Sin dolor' : n <= 3 ? 'Dolor leve' : n <= 6 ? 'Dolor moderado' : 'Dolor intenso';
  return { nivel, label: `${n}/10`, texto: `EVA ${n}/10: ${desc}`, clasificacion: desc };
};

const scoreLpp = (vals) => {
  if (!vals.lpp) return null;
  const s = LPP_OPTS.find((x) => x.v === vals.lpp);
  // "LPP categoría II" en la prosa, con la misma minúscula que la frase del
  // mapa de piel (pielLpp): un concepto, una forma de escribirlo.
  return s ? { nivel: s.nivel, label: s.l, texto: `LPP ${s.l.replace('Categoría', 'categoría')}: ${s.desc}`, clasificacion: s.desc } : null;
};

// PAM: la clasificación clínica ("Bajo meta") viaja siempre; la conducta
// genérica que MiPAE agrega tras el punto bajo meta ("evaluar volumen y DVA")
// es pista de la app, no dato del autor (mismo trato que Downton/ritmo
// diurético abajo: `clasificacion` la excluye, `texto` la conserva completa
// para quien ya la usaba — SYSTEM_PULIR:147 documenta la misma regla para el
// collage pegado a mano). Ola "El estándar Bupa" (2026-08-15).
const scorePam = (vals) => {
  const pas = parseFloat(vals.pas);
  const pad = parseFloat(vals.pad);
  if (isNaN(pas) || isNaN(pad)) return null;
  const pam = Math.round(pad + (pas - pad) / 3);
  const nivel = pam < 65 ? 'severo' : pam > 130 ? 'severo' : pam > 110 ? 'moderado' : 'normal';
  const clasificacion = pam < 65 ? 'Bajo meta' : pam > 130 ? 'Posible crisis hipertensiva' : pam > 110 ? 'Elevada' : 'En rango';
  const conducta = pam < 65 ? 'evaluar volumen y DVA' : null;
  const desc = conducta ? `${clasificacion}: ${conducta}` : clasificacion;
  return { nivel, label: `${pam} mmHg`, texto: `PAM ${pam} mmHg: ${desc}`, clasificacion };
};

const scoreSfo2 = (vals) => {
  const spo2 = parseFloat(vals.spo2);
  const fio2 = parseFloat(vals.fio2);
  if (isNaN(spo2) || isNaN(fio2) || fio2 <= 0) return null;
  const sfr = Math.round(spo2 / (fio2 / 100));
  const nivel = sfr > 315 ? 'normal' : sfr > 235 ? 'leve' : 'moderado';
  const desc = sfr > 315 ? 'Sin compromiso' : sfr > 235 ? 'SDRA leve equivalente' : 'Compromiso moderado-severo';
  return { nivel, label: String(sfr), texto: `SpO₂/FiO₂ ${sfr}: ${desc}`, clasificacion: desc };
};

const scoreFour = (vals) => {
  const total = scoreItems(FOUR_ITEMS, vals);
  if (total === null) return null;
  const nivel = total >= 13 ? 'normal' : total >= 8 ? 'leve' : total >= 4 ? 'moderado' : 'severo';
  const desc = total >= 13 ? 'Mínima alteración' : total >= 8 ? 'Alteración moderada' : total >= 4 ? 'Alteración severa' : total === 0 ? 'Compatible con muerte cerebral' : 'Coma profundo';
  return { nivel, label: `${total}/16`, texto: `FOUR ${total}/16: ${desc}`, clasificacion: desc };
};

const scoreSas = (vals) => {
  if (vals.sas === undefined || vals.sas === null) return null;
  const opt = SAS_OPTS.find((o) => o.v === vals.sas);
  const nivel = vals.sas >= 6 ? 'severo' : vals.sas === 5 ? 'moderado' : vals.sas === 4 ? 'normal' : vals.sas === 3 ? 'leve' : 'info';
  const desc = opt ? opt.l.split('· ')[1] : String(vals.sas);
  return { nivel, label: `SAS ${vals.sas}`, texto: `SAS ${vals.sas}: ${desc}`, clasificacion: desc };
};

const scoreMottling = (vals) => {
  if (vals.mottling === undefined || vals.mottling === null) return null;
  const n = vals.mottling;
  const opt = MOTTLING_OPTS[n];
  const desc = opt ? opt.d : '';
  return { nivel: n === 0 ? 'normal' : n <= 2 ? 'leve' : 'severo', label: `Mottling ${n}`, texto: `Mottling ${n}: ${desc}`, clasificacion: desc };
};

const scorePafi = (vals) => {
  const pao2 = parseFloat(vals.pao2);
  const fio2 = parseFloat(vals.fio2);
  if (isNaN(pao2) || isNaN(fio2) || fio2 <= 0) return null;
  const pafi = Math.round(pao2 / (fio2 / 100));
  const nivel = pafi > 300 ? 'normal' : pafi > 200 ? 'leve' : pafi > 100 ? 'moderado' : 'severo';
  // "Rango SDRA" y no "SDRA" a secas: la definición de Berlín exige además
  // inicio dentro de una semana de un insulto conocido, opacidades bilaterales,
  // falla no explicada por falla cardíaca y PEEP de al menos 5. El widget pide
  // solo PaO₂ y FiO₂, así que afirmar el diagnóstico dejaría a un paciente con
  // naricera documentado como "SDRA leve" en una nota de enfermería. Su hermano
  // scoreSfo2 ya decía "equivalente": misma prudencia, misma palabra.
  const desc = pafi > 300 ? 'Sin compromiso' : pafi > 200 ? 'Hipoxemia leve (rango SDRA leve)' : pafi > 100 ? 'Hipoxemia moderada (rango SDRA moderado)' : 'Hipoxemia severa (rango SDRA severo)';
  return { nivel, label: `${pafi} mmHg`, texto: `PaFi ${pafi} mmHg: ${desc}`, clasificacion: desc };
};

const scoreDai = (vals) => {
  if (!vals.dai) return null;
  const c = DAI_OPTS.find((x) => x.v === vals.dai);
  return c ? { nivel: c.nivel, label: c.l, texto: `DAI ${c.l}: ${c.desc}`, clasificacion: c.desc } : null;
};

// Ritmo diurético en ml/kg/h a partir de diuresis (ml), peso (kg) y horas. La
// conducta genérica que acompaña oliguria/poliuria ("avisar"/"vigilar
// volemia") es pista de la app: `clasificacion` la excluye (ola "El estándar
// Bupa", 2026-08-15), `texto` la sigue trayendo completa para quien ya la usa.
const scoreRitmoDiur = (vals) => {
  const d = parseFloat(vals.diuresis);
  const peso = parseFloat(vals.peso);
  const horas = parseFloat(vals.horas);
  if (isNaN(d) || isNaN(peso) || isNaN(horas) || peso <= 0 || horas <= 0) return null;
  const ritmo = d / (peso * horas);
  const r = ritmo.toFixed(2).replace('.', ',');
  const nivel = ritmo < 0.5 ? 'moderado' : ritmo <= 3 ? 'normal' : 'leve';
  const clasificacion = ritmo < 0.5 ? 'Oliguria (<0,5)' : ritmo <= 3 ? 'En rango (0,5–3)' : 'Poliuria (>3)';
  const conducta = ritmo < 0.5 ? 'avisar' : ritmo <= 3 ? null : 'vigilar volemia';
  const desc = conducta ? `${clasificacion}, ${conducta}` : clasificacion;
  // `prosa`: cómo se lee este score DENTRO de una oración (el prefijo de
  // `texto` arranca con "Diuresis" en mayúscula y a media oración quedaba
  // "Diuresis espontánea, Diuresis 100 ml": palabra repetida y capitalizada,
  // hallazgo del revisor del PR E). `texto` no cambia: es la etiqueta de la
  // UI y otras superficies la muestran tal cual.
  return {
    nivel, label: `${r} ml/kg/h`,
    texto: `Diuresis ${d} ml · ritmo ${r} ml/kg/h: ${desc}`, clasificacion,
    prosa: `diuresis ${d} ml, ritmo ${r} ml/kg/h`,
  };
};

// Downton: el estándar de caídas de MiPAE, y desde la auditoría clínica del
// 2026-08-16 la FUENTE ÚNICA de la escala en toda la app (MiPAE en turno, la
// calculadora de Escalas y el PAE Detallado importan de aquí; antes había tres
// catálogos paralelos que se contradecían entre sí y el mismo paciente daba
// "riesgo alto" en un módulo y "riesgo bajo" en otro).
//
// Fuente del papel: Escala de Riesgo de caídas (J. H. Downton), adaptación
// española de la Consejería de Sanidad — es el documento que se muestra cuando
// Calidad pregunta de dónde salió el número. Lo que dice el índice y MiPAE no
// respetaba:
//   · "Medicamentos" y "Deficiencias sensoriales" son filas ADITIVAS: cada
//     fármaco de riesgo y cada déficit suman su propio punto. MiPAE forzaba
//     selección ÚNICA, y así sedantes + diuréticos + hipotensores + déficit
//     visual daba 2 puntos ("riesgo bajo") en vez de los 4 del índice, con la
//     evolución escribiendo el falso negativo que Calidad audita tras una caída.
//   · La fila de medicamentos tiene SEIS categorías, no tres: faltaban
//     antiparkinsonianos, antidepresivos y "otros medicamentos" (la glosa de la
//     fuente: opiáceos, neurolépticos, antihistamínicos sedantes).
//   · El punto de la fila lo carga la palabra INSEGURA, no el bastón: en el
//     índice "Segura con ayuda" vale 0, igual que "Normal", y solo puntúan
//     "Insegura con o sin ayuda" e "Imposible". La escalera 1/2/3 de MiPAE era
//     invención, pero aplanar las cuatro opciones a 1 es la sobrecorrección
//     espejo: una escala que distingue textualmente "segura" de "insegura"
//     para darles el mismo punto no distingue nada, y empuja sobre el corte
//     ≥3 a un paciente que el papel deja en riesgo bajo (la auditoría del
//     2026-08-16 lo reprodujo: sedantes + diuréticos + marcha con ayuda daba
//     3 en MiPAE y 2 en el índice).
//   · No deambular NO es 0 puntos: en el índice es "Imposible" = 1. La opción
//     "Normal / encamado" mezclaba el 0 con el 1 en un mismo chip, así que el
//     mismo estado clínico puntuaba distinto según qué tocara la enfermera.
//
// `multi: true` marca las filas donde el renderer (EscalasSistema.jsx,
// EscalaWidget rama 'items') acepta más de una opción a la vez; SIN la
// bandera, el renderer sigue siendo el toggle excluyente de siempre
// (glasgow/cpot/braden/four/morse dependen de eso: guardan el puntaje numérico
// directo en `vals[k]` y `scoreItems` hace `Number(vals[k])`, así que jamás
// deben recibir un array).
export const DOWNTON_ITEMS = [
  { k: 'dt_caidas',       label: 'Caídas previas',         opts: [{ v: 'no', l: 'No' }, { v: 'si', l: 'Sí' }] },
  // Etiquetas en español de Chile: la fuente dice "Tranquilizantes-sedantes" e
  // "Hipotensores", que acá se nombran como se nombran en el pabellón.
  // "Otros" lleva su glosa en la etiqueta a propósito: sin ella la enfermera no
  // sabe qué cuenta y la fila puntúa de menos en silencio.
  { k: 'dt_medicamentos', label: 'Medicamentos de riesgo', multi: true, opts: [
    { v: 'ninguno',            l: 'Ninguno' },
    { v: 'sedantes',           l: 'Sedantes' },
    { v: 'diureticos',         l: 'Diuréticos' },
    { v: 'antihipertensivos',  l: 'Antihipertensivos' },
    { v: 'antiparkinsonianos', l: 'Antiparkinsonianos' },
    { v: 'antidepresivos',     l: 'Antidepresivos' },
    { v: 'otros',              l: 'Otros (opiáceos, neurolépticos, antihistamínicos)' },
  ] },
  { k: 'dt_sensorial',    label: 'Déficit sensorial',      multi: true, opts: [{ v: 'ninguno', l: 'Ninguno' }, { v: 'visual', l: 'Visual' }, { v: 'auditivo', l: 'Auditivo' }, { v: 'extremidades', l: 'Extremidades' }] },
  { k: 'dt_mental',       label: 'Estado mental',          opts: [{ v: 'orientado', l: 'Orientado' }, { v: 'confuso', l: 'Confuso' }] },
  // El nombre de la fila es el de la fuente ("Deambulación"), y sus opciones
  // también. "Encamado / no deambula" existe como chip propio junto a
  // "Imposible" porque es la palabra que la enfermera busca: son el MISMO
  // estado clínico y valen lo mismo (1), así que tocar uno u otro no cambia el
  // puntaje. La fila es excluyente, no se pueden marcar los dos.
  { k: 'dt_marcha',       label: 'Deambulación',           opts: [
    { v: 'normal',    l: 'Normal' },
    { v: 'con_ayuda', l: 'Segura con ayuda' },
    { v: 'insegura',  l: 'Insegura con o sin ayuda' },
    { v: 'imposible', l: 'Imposible' },
    { v: 'encamado',  l: 'Encamado / no deambula' },
  ] },
];

export const DOWNTON_PUNTOS = {
  dt_caidas:       { no: 0, si: 1 },
  dt_medicamentos: { ninguno: 0, sedantes: 1, diureticos: 1, antihipertensivos: 1, antiparkinsonianos: 1, antidepresivos: 1, otros: 1 },
  dt_sensorial:    { ninguno: 0, visual: 1, auditivo: 1, extremidades: 1 },
  dt_mental:       { orientado: 0, confuso: 1 },
  // Puntúa la INSEGURIDAD, no la ayuda técnica: caminar seguro con bastón o
  // andador es 0 en el índice, igual que la marcha normal. Encamado vale lo
  // mismo que "Imposible" (1) porque son el mismo estado clínico.
  dt_marcha:       { normal: 0, con_ayuda: 0, insegura: 1, imposible: 1, encamado: 1 },
};

// Retrocompatibilidad sin migrar: un registro viejo guardó `dt_medicamentos`/
// `dt_sensorial` como STRING único (selección excluyente de antes de esta
// ola). Se lee como array de un elemento, así que su puntaje no cambia.
// Datos viejos se leen con adapters, jamás se migran (invariante del repo).
export const comoArrayMultiEscala = (v) => (Array.isArray(v) ? v : v === undefined || v === null ? [] : [v]);

// Toggle de una fila `multi`: array acumulativo con 'ninguno' EXCLUYENTE en
// ambos sentidos (mismo contrato que el catálogo de "Ninguno" del resto de la
// app). Vive acá, junto al catálogo, para que las tres superficies que pintan
// Downton compartan la MISMA interacción (EscalasSistema, la calculadora de
// Escalas y el PAE Detallado).
// El tercer parámetro `k` es la fila del catálogo, y con él el toggle SANEA lo
// que arrastra: un draft del PAE Detallado guardado antes de esta ola trae el
// puntaje como string ("1"), que ningún chip pinta encendido pero que sí suma;
// tocar entonces "Sedantes" dejaba ["1","sedantes"] = 2 puntos por UNA clase de
// fármaco, en silencio y justo en la evidencia del NANDA 00155. En cuanto la
// enfermera responde la fila con el catálogo, el resto del legado se va: ella
// está contestando ahora, no editando lo viejo. Sin `k` el saneo se omite (el
// legado se sigue LEYENDO con su adapter, jamás se migra).
export function toggleOpcionMulti(valorActual, v, k) {
  const arr = comoArrayMultiEscala(valorActual);
  if (v === 'ninguno') return arr.includes('ninguno') ? undefined : ['ninguno'];
  const catalogo = k ? DOWNTON_PUNTOS[k] : null;
  const sinNinguno = arr.filter((x) => x !== 'ninguno')
    .filter((x) => !catalogo || Object.prototype.hasOwnProperty.call(catalogo, x));
  const next = sinNinguno.includes(v) ? sinNinguno.filter((x) => x !== v) : [...sinNinguno, v];
  return next.length ? next : undefined;
}

// Puntaje de UNA opción, con el adapter del legado del PAE Detallado: esa copia
// guardaba el PUNTAJE como string ("0".."3") en vez del valor del catálogo. Se
// LEE, jamás se migra. Cualquier positivo vale 1 punto, que es lo que dice el
// índice real (la escalera 1/2/3 de la marcha era invención de MiPAE, así que
// un "3" viejo de "Imposible" hoy se lee como el 1 que siempre debió ser).
// Un valor que no es del catálogo NI del legado devuelve `null`, y eso anula
// el total entero. Precedente #642 (`fraseLesion`): un jsonb adulterado no
// imprime. En una escala cuya gracia es ser auditable, "no sé leer este dato"
// tiene que ser silencio y no "Riesgo bajo de caídas", que es una afirmación
// clínica que nadie hizo.
const puntosOpcionDownton = (k, v) => {
  const tabla = DOWNTON_PUNTOS[k] || {};
  if (Object.prototype.hasOwnProperty.call(tabla, v)) return tabla[v];
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v) > 0 ? 1 : 0;
  return null;
};

// Total crudo de la escala (number | null si falta alguna fila). Se expone
// aparte del score porque el PAE Detallado necesita el número pelado para su
// evidencia, y así usa EXACTAMENTE la misma aritmética que MiPAE en turno.
export const totalDownton = (vals) => {
  // Una fila `multi` respondida es un array CON al menos un elemento: un
  // array vacío no cuenta como respuesta (si contara, el puntaje quedaría de
  // menos en silencio ante una fila que en verdad no se contestó).
  const respondida = (item) => {
    const v = vals?.[item.k];
    if (item.multi) return comoArrayMultiEscala(v).length > 0;
    return v !== undefined && v !== null && v !== '';
  };
  if (!DOWNTON_ITEMS.every(respondida)) return null;
  // `null` de cualquier opción contagia al total: mejor no dar número que dar
  // uno que no reproduce el papel.
  return DOWNTON_ITEMS.reduce((s, i) => {
    if (s === null) return null;
    const puntos = i.multi
      ? comoArrayMultiEscala(vals[i.k]).reduce((ss, el) => {
          const p = puntosOpcionDownton(i.k, el);
          return ss === null || p === null ? null : ss + p;
        }, 0)
      : puntosOpcionDownton(i.k, vals[i.k]);
    return puntos === null ? null : s + puntos;
  }, 0);
};

// Concordancia: la evolución es el producto final y no puede imprimir
// "Downton 1 puntos". Compartida con las otras superficies de la escala.
export const textoPuntos = (n) => (n === 1 ? '1 punto' : `${n} puntos`);

// La conducta genérica tras el corte de riesgo alto ("activar medidas de
// prevención") es pista de la app, no dato del autor — `clasificacion` la
// excluye (ola "El estándar Bupa", 2026-08-15; SYSTEM_PULIR:147 la cita
// textual como ejemplo de coletilla omitible al integrar a prosa), `texto`
// sigue trayéndola completa para quien ya la usaba. Desde la auditoría del
// 2026-08-16 la REFERENCIA AL CORTE ("≥3") corre la misma suerte, igual que en
// scoreCpot: es didáctica de la UI, no dato constatado, así que vive en `texto`
// y no en lo único que puede viajar a prosa.
// Mismo criterio que clasificarBradenTotal: el corte del índice (≥3 = alto)
// a partir del total, para el puntaje recibido y el tablero.
export const clasificarDowntonTotal = (total) => {
  const t = Number(total);
  if (!Number.isFinite(t) || t < 0 || t > 11) return null;
  const alto = t >= 3;
  return { nivel: alto ? 'moderado' : 'normal', clasificacion: alto ? 'Riesgo alto de caídas' : 'Riesgo bajo de caídas', label: textoPuntos(t) };
};

const scoreDownton = (vals) => {
  const total = totalDownton(vals);
  if (total === null) return null;
  const alto = total >= 3;
  const nivel = alto ? 'moderado' : 'normal';
  const clasificacion = alto ? 'Riesgo alto de caídas' : 'Riesgo bajo de caídas';
  const pista = alto ? `${clasificacion} (≥3)` : `${clasificacion} (<3)`;
  const conducta = alto ? 'activar medidas de prevención' : null;
  const desc = conducta ? `${pista}: ${conducta}` : pista;
  const label = textoPuntos(total);
  return { nivel, label, texto: `Downton ${label}: ${desc}`, clasificacion };
};

// Morse: se conserva para registros históricos (ver nota sobre Downton arriba).
// Ya no se ofrece en ningún sistema (ESCALAS_POR_SISTEMA), así que hoy nunca
// llega a compilarSistemaDetalle — `clasificacion` se agrega solo por
// consistencia con el resto de la familia, sin caso vivo que la ejercite.
const scoreMorse = (vals) => {
  const total = scoreItems(MORSE_ITEMS, vals);
  if (total === null) return null;
  const nivel = total <= 24 ? 'normal' : total <= 44 ? 'leve' : 'moderado';
  const clasificacion = total <= 24 ? 'Riesgo bajo de caídas' : total <= 44 ? 'Riesgo moderado de caídas' : 'Riesgo alto de caídas';
  const conducta = total <= 24 ? null : total <= 44 ? 'medidas estándar' : 'medidas reforzadas, avisar';
  const desc = conducta ? `${clasificacion}: ${conducta}` : clasificacion;
  return { nivel, label: `${total}/125`, texto: `Morse ${total}/125: ${desc}`, clasificacion };
};

// ── Definición de cada escala (UI-agnóstica) ──────────────────────────────────
// tipo: 'items' (filas con opciones) · 'single' (un select) · 'eva' (0–10) ·
//       'calc' (inputs numéricos que se calculan).
export const ESCALAS_DEF = {
  glasgow: { id: 'glasgow', nombre: 'Glasgow (GCS)', tipo: 'items', items: GLASGOW_ITEMS, score: scoreGlasgow },
  rass:    { id: 'rass',    nombre: 'RASS',          tipo: 'single', campo: 'rass',    opciones: RASS_OPTS,   score: scoreRass },
  eva:     { id: 'eva',     nombre: 'EVA dolor',     tipo: 'eva',    campo: 'eva',     score: scoreEva },
  cpot:    { id: 'cpot',    nombre: 'CPOT',          tipo: 'items',  items: CPOT_ITEMS, score: scoreCpot },
  cam_icu: { id: 'cam_icu', nombre: 'CAM-ICU',       tipo: 'single', campo: 'cam_icu', opciones: CAMICU_OPTS, score: scoreCamicu },
  cam:     { id: 'cam',     nombre: 'CAM',           tipo: 'single', campo: 'cam',     opciones: CAMICU_OPTS, score: scoreCam },
  braden:  { id: 'braden',  nombre: 'Braden',        tipo: 'items',  items: BRADEN_ITEMS, score: scoreBraden },
  lpp:     { id: 'lpp',     nombre: 'LPP',           tipo: 'single', campo: 'lpp',     opciones: LPP_OPTS,    score: scoreLpp },
  pam:     { id: 'pam',     nombre: 'PAM',           tipo: 'calc',   inputs: [{ k: 'pas', label: 'PAS', unidad: 'mmHg' }, { k: 'pad', label: 'PAD', unidad: 'mmHg' }], score: scorePam },
  sfo2:    { id: 'sfo2',    nombre: 'SpO₂/FiO₂',     tipo: 'calc',   inputs: [{ k: 'spo2', label: 'SpO₂', unidad: '%' }, { k: 'fio2', label: 'FiO₂', unidad: '%' }], score: scoreSfo2 },
  four:    { id: 'four',    nombre: 'FOUR',          tipo: 'items',  items: FOUR_ITEMS,    score: scoreFour },
  sas:     { id: 'sas',     nombre: 'SAS',           tipo: 'single', campo: 'sas',     opciones: SAS_OPTS,     score: scoreSas },
  mottling:{ id: 'mottling',nombre: 'Mottling',      tipo: 'single', campo: 'mottling',opciones: MOTTLING_OPTS, score: scoreMottling },
  pafi:    { id: 'pafi',    nombre: 'PaFi',          tipo: 'calc',   inputs: [{ k: 'pao2', label: 'PaO₂', unidad: 'mmHg' }, { k: 'fio2', label: 'FiO₂', unidad: '%' }], score: scorePafi },
  dai:     { id: 'dai',     nombre: 'DAI',           tipo: 'single', campo: 'dai',     opciones: DAI_OPTS,     score: scoreDai },
  ritmo_diur: { id: 'ritmo_diur', nombre: 'Ritmo diurético', tipo: 'calc', inputs: [{ k: 'diuresis', label: 'Diuresis', unidad: 'ml' }, { k: 'peso', label: 'Peso', unidad: 'kg' }, { k: 'horas', label: 'Horas', unidad: 'h' }], score: scoreRitmoDiur },
  morse:   { id: 'morse',   nombre: 'Morse (riesgo de caídas)', tipo: 'items', items: MORSE_ITEMS, score: scoreMorse },
  downton: { id: 'downton', nombre: 'Downton (riesgo de caídas)', tipo: 'items', items: DOWNTON_ITEMS, score: scoreDownton },
};

// ── Cuándo una escala AMERITA ofrecerse (dictado del fundador, 2026-09-29) ──
// "Que se desplieguen las escalas solo si lo amerita: si el paciente está
// vigil, Glasgow 15, no necesitamos RASS, ni CPOT, ni CAM." Son dos preguntas
// distintas y por eso viven en dos claves:
//  · `dependeDe`: cuándo la escala es VÁLIDA. Fuera de eso se contradice con
//    lo que la autora firmó (Glasgow bajo sedación, EVA con dolor no
//    evaluable) y la calculadora rápida lo avisa (escalaContradiceCampo).
//  · `amerita`: cuándo vale la pena OFRECERLA. Fuera de eso no se pregunta,
//    pero guardarla no contradice nada (un RASS 0 en un paciente vigil es
//    innecesario, no falso), así que no hay aviso.
// Las dos usan el mismo lenguaje de cumpleDependencia, incluida la forma
// `{ todas: [...] }` (Y), que nació aquí.
const SEDADO = { campo: 'sedacion', en: ['Con sedación'] };
const CON_DVA = { campo: 'dvaSoporte', en: ['Con drogas vasoactivas'] };
// Los valores de gases se ven con gases declarados, o directo en la vía aérea
// artificial (ver `gasesDia` en ventilación).
const GASES_A_LA_VISTA = [{ campo: 'gasesDia', en: ['Con gases del día'] }, { campo: 'viaAerea', en: ['Vía aérea artificial'] }];
const CONCIENCIA_BAJA = ['Sopor profundo', 'Coma'];
// Motivo para buscar delirium: lo marcó, está sedado (el despertar), tiende al
// sueño sin llegar al sopor profundo (el hipoactivo), está desorientado o su
// conexión fluctúa (el rasgo cardinal). En sopor profundo o coma no se puede
// evaluar: ahí no hay motivo que ofrecer.
const MOTIVO_DELIRIUM = [
  { campo: 'delirium', en: ['Sí'] },
  SEDADO,
  { campo: 'conciencia', en: ['Somnoliento', 'Sopor superficial'] },
  { campo: 'orientacion', en: ['Desorientado en tiempo', 'Desorientado en espacio', 'Desorientado en tiempo y espacio'] },
  { campo: 'conexion', en: ['Parcialmente conectado'] },
];
// Tamizaje, no consecuencia: con "No" también se ofrece (así se documenta el
// negativo), pero solo cuando hay motivo. "No evaluable" ya dice por qué no
// se tamizó.
const AMERITA_CAM = { todas: [{ campo: 'delirium', en: ['Sí', 'No'] }, MOTIVO_DELIRIUM] };

// Set esencial de turno: qué escalas se ofrecen en cada sistema. Cada entrada
// es un id (string) o un objeto { id, dependeDe, amerita } para escalas
// condicionales que solo aparecen según un campo del sistema (ver arriba y
// cumpleDependencia).
export const ESCALAS_POR_SISTEMA = {
  neuro: [
    { id: 'glasgow', grupo: 'conciencia', dependeDe: { campo: 'sedacion', noEn: ['Con sedación'] } },
    // RASS mide sedación y agitación: se ofrece al sedado y cuando se busca
    // delirium (es el paso 1 del CAM-ICU y separa el delirium hiperactivo del
    // hipoactivo). En el vigil sin sedación sobra: el Glasgow ya lo dice.
    { id: 'rass', grupo: 'conciencia', amerita: [SEDADO, AMERITA_CAM] },
    { id: 'sas', grupo: 'conciencia', dependeDe: { campo: 'sedacion', en: ['Con sedación'] } },
    // FOUR es del paciente SIN sedación que no puede comunicarse (dictado del
    // fundador, 2026-09-29: "el FOUR es en no sedados que no pueden
    // comunicarse"): sopor profundo, coma o sin conexión con el medio. Bajo
    // sedación se invalida igual que el Glasgow (es escala de conciencia, y la
    // profundidad del sedado la miden RASS/SAS), por eso la sedación va en
    // `dependeDe` y la calculadora avisa si se guarda encima. Antes (16-jul)
    // salía también con sedación; esa parte se retiró, la del coma sin sedar
    // sigue.
    { id: 'four', grupo: 'conciencia', dependeDe: { campo: 'sedacion', noEn: ['Con sedación'] },
      amerita: [
        { campo: 'conciencia', en: CONCIENCIA_BAJA },
        { campo: 'conexion', en: ['No conecta con el medio'] },
      ] },
    // Tamizaje, no consecuencia: CAM y CAM-ICU se ofrecen apenas se toca el
    // delirium, porque son la herramienta para DESCUBRIRLO. Colgarlas de
    // "delirium: Sí" pedía el examen después de tener el diagnóstico, y con
    // "No" o "No evaluable" no se desplegaban nunca. Desde 2026-09-29 además
    // exigen MOTIVO (AMERITA_CAM): al vigil orientado no se le ofrecen.
    // CAM (sin ICU) necesita conversar con el paciente, así que al sedado se
    // le ofrece solo CAM-ICU, que está hecha para el que no habla.
    { id: 'cam_icu', grupo: 'delirium', dependeDe: { campo: 'delirium' }, amerita: AMERITA_CAM },
    { id: 'cam',     grupo: 'delirium', dependeDe: { campo: 'delirium' },
      amerita: { todas: [AMERITA_CAM, { campo: 'sedacion', noEn: ['Con sedación'] }] } },
    // El dolor siempre se objetiva con escala, con CUALQUIER respuesta (no
    // solo "Con dolor"): EVA 0 o CPOT <3 documentan la ausencia (regla del
    // fundador, 2026-07-14). Desde 2026-09-29 se ofrece LA que corresponde,
    // no las dos (fijado por test: con dolor respondido siempre queda una):
    //  · EVA es dolor AUTORREPORTADO: con "No evaluable" se contradice con lo
    //    firmado (feedback de Tania Sánchez, jefa de UPC, 2026-08-08), y solo
    //    amerita en quien puede contarlo: sin sedación, o con sedación ligera
    //    (despierto, colabora), y no en sopor profundo ni coma.
    //  · CPOT es conductual, para el que NO puede contarlo: dolor no
    //    evaluable, sedado, o en sopor profundo o coma.
    { id: 'eva', grupo: 'dolor', dependeDe: { campo: 'dolor', en: ['Sin dolor', 'Con dolor'] },
      amerita: { todas: [
        [{ campo: 'sedacion', noEn: ['Con sedación'] }, { campo: 'objetivoSedacion', en: ['Sedación ligera'] }],
        { campo: 'conciencia', noEn: CONCIENCIA_BAJA },
      ] } },
    { id: 'cpot', grupo: 'dolor', dependeDe: { campo: 'dolor' },
      amerita: [{ campo: 'dolor', en: ['No evaluable'] }, SEDADO, { campo: 'conciencia', en: CONCIENCIA_BAJA }] },
    { id: 'downton', grupo: 'movilidad' },
  ],
  // Mismo criterio en los demás sistemas (2026-09-29, "sigue encontrando lo
  // mismo en los otros sistemas"): el paciente estable no se mide dos veces.
  hemodinamia:  [
    // "Normotenso" ya lo dice la presión: la PAM calculada amerita cuando la
    // presión está alterada o se titulan drogas contra ella.
    { id: 'pam', grupo: 'estado', amerita: [
      { campo: 'presion', en: ['Hipotenso', 'Hipertenso'] },
      CON_DVA,
    ] },
    // El moteado se objetiva cuando hay algo que objetivar: hipoperfusión,
    // piel que no es tibia y rosada, o soporte vasoactivo.
    { id: 'mottling', grupo: 'perfusion', amerita: [
      { campo: 'perfusion', en: ['Hipoperfusión periférica'] },
      { campo: 'piel', en: ['Fría', 'Pálida', 'Moteada', 'Cianótica'] },
      CON_DVA,
    ] },
  ],
  ventilacion:  [
    // SpO₂/FiO₂ solo dice algo con oxígeno: en aire ambiente la FiO₂ es 21 y
    // basta con la SpO₂.
    { id: 'sfo2', grupo: 'gases', amerita: [
      { campo: 'soporte', en: ['Oxigenoterapia', 'CNAF', 'VNI'] },
      { campo: 'viaAerea', en: ['Vía aérea artificial'] },
    ] },
    // PaFi necesita gases arteriales: con soporte alto (CNAF, VNI, vía aérea
    // artificial) o cuando se declararon gases del día.
    { id: 'pafi', grupo: 'gases', amerita: [
      { campo: 'soporte', en: ['CNAF', 'VNI'] },
      { campo: 'viaAerea', en: ['Vía aérea artificial'] },
      { campo: 'gasesDia', en: ['Con gases del día'] },
    ] },
  ],
  // El ritmo diurético pide diuresis medida: con catéter urinario (se mide
  // hora a hora) o cuando la diuresis está alterada y hay que cuantificarla.
  renal:        [{ id: 'ritmo_diur', grupo: 'diuresis', amerita: [
    { campo: 'diuresisEstado', en: ['Oliguria', 'Poliuria'] },
    { campo: 'diuresisVia', en: ['Catéter urinario permanente'] },
  ] }],
  tegumentario: [
    // Braden en TODO paciente: es el riesgo de LPP y la basal que audita
    // Calidad (ancla del piloto Bupa). No lleva `amerita`.
    { id: 'braden', grupo: 'piel' },
    { id: 'lpp', grupo: 'piel', dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] } },
    { id: 'dai', grupo: 'piel', dependeDe: { campo: 'integridad', en: ['DAI'] } },
  ],
};

// ── Dónde vive cada escala ───────────────────────────────────────────────────
// Derivado de ESCALAS_POR_SISTEMA, cero catálogo paralelo. Si una escala se
// ofrece en dos sistemas gana el primero declarado (hoy ninguna lo hace).
// Una escala que NO está acá no tiene dónde escribir su línea, así que no se
// puede guardar en un paciente: Morse es el caso vivo (podada el 2026-07-06,
// Downton es el estándar de MiPAE) y debe seguir devolviendo null.
const SISTEMA_POR_ESCALA = Object.entries(ESCALAS_POR_SISTEMA).reduce((acc, [sisId, lista]) => {
  for (const e of lista) {
    const id = typeof e === 'string' ? e : e.id;
    if (!(id in acc)) acc[id] = sisId;
  }
  return acc;
}, {});

export function sistemaDeEscala(escalaId) {
  return SISTEMA_POR_ESCALA[escalaId] || null;
}

// Campos estructurados por sistema (signos / observaciones rápidas).
// tipo: 'num' (input numérico con unidad, con `bandas` de interpretación
// didáctica) · 'select' (opciones, con `notas` por opción).
//   bandas: [{ hasta, texto, nivel }] — la primera cuyo valor ≤ hasta gana.
//   notas:  { [opcion]: { texto, nivel } }.
export const CAMPOS_POR_SISTEMA = {
  neuro:       [
    // La RAMA va primero (feedback del fundador, 2026-07-14): "si está sedado,
    // todo lo de arriba como que se invalida". Responder sedación decide qué
    // se pregunta después — sin sedación se valora conciencia cualitativa;
    // con sedación, el objetivo + drogas CON DOSIS + titulación.
    { k: 'sedacion', grupo: 'conciencia', label: 'Sedación', tipo: 'select', compactar: true, rama: true, opciones: ['Sin sedación', 'Con sedación'],
      notas: {
        'Sin sedación': { nivel: 'normal', texto: 'Valora conciencia con Glasgow; si buscas delirium, suma RASS y CAM.' },
        'Con sedación': { nivel: 'info',   texto: 'Monitoriza profundidad con SAS/RASS; evalúa despertar diario.' },
      },
      // "Con sedación" suelto entre comas es jerga de formulario; "sedado" se
      // lee como evolución y generiza (sedada). "Sin sedación" ya se lee sola.
      prose: { 'Con sedación': 'sedado' } },
    // Estado de conciencia cualitativo — el continuo clínico clásico. Se
    // invalida bajo sedación (ahí la profundidad se objetiva con RASS/SAS).
    { k: 'conciencia', grupo: 'conciencia', label: 'Estado de conciencia', tipo: 'select', compactar: true, dependeDe: { campo: 'sedacion', noEn: ['Con sedación'] },
      opciones: ['Vigil', 'Somnoliento', 'Sopor superficial', 'Sopor profundo', 'Coma'],
      notas: {
        'Vigil':             { nivel: 'normal',   texto: 'Despierta espontáneamente: esperado.' },
        'Somnoliento':       { nivel: 'leve',     texto: 'Tiende al sueño, despierta al llamado: vigilar evolución.' },
        'Sopor superficial': { nivel: 'moderado', texto: 'Despierta solo a estímulo verbal enérgico o táctil: objetívalo con Glasgow.' },
        'Sopor profundo':    { nivel: 'moderado', texto: 'Responde solo a estímulo doloroso: objetívalo con Glasgow; avisa si es nuevo.' },
        'Coma':              { nivel: 'severo',   texto: 'Sin respuesta a estímulos: objetívalo con Glasgow, avisar.' },
      },
      // "Sopor superficial" suelto entre comas queda cojo — con la preposición
      // se lee como evolución ("en sopor superficial").
      prose: { 'Sopor superficial': 'en sopor superficial', 'Sopor profundo': 'en sopor profundo', 'Coma': 'en coma' } },
    { k: 'conexion', grupo: 'conciencia', label: 'Conexión con el medio', tipo: 'select', compactar: true, dependeDe: { campo: 'sedacion', noEn: ['Con sedación'] },
      opciones: ['Conectado con el medio', 'Parcialmente conectado', 'No conecta con el medio', 'No evaluable'],
      notas: {
        'Conectado con el medio':   { nivel: 'normal',   texto: 'Interactúa y responde: esperado.' },
        'Parcialmente conectado':   { nivel: 'leve',     texto: 'Respuesta fluctuante: vigilar.' },
        'No conecta con el medio':  { nivel: 'moderado', texto: 'Sin interacción: correlacionar con sedación / conciencia.' },
        'No evaluable':             { nivel: 'info',     texto: 'No se pudo valorar: reevaluar en el turno.' },
      },
      prose: { 'No evaluable': 'conexión con el medio no evaluable' } },
    { k: 'orientacion', grupo: 'conciencia', label: 'Orientación', tipo: 'select', compactar: true, dependeDe: { campo: 'sedacion', noEn: ['Con sedación'] },
      opciones: ['Orientado en tiempo y espacio', 'Desorientado en tiempo', 'Desorientado en espacio', 'Desorientado en tiempo y espacio', 'No evaluable'],
      // Solo "No evaluable" es ambigua suelta entre comas (¿qué cosa no se
      // pudo evaluar?) — el resto ya dice "orientado/desorientado".
      prose: { 'No evaluable': 'orientación no evaluable' } },
    // Obedece órdenes: visible también bajo sedación — en el despertar (ventana,
    // destete) es EL dato que objetiva la respuesta cortical (feedback del
    // fundador: "obedece 1 de 5, por ejemplo para extubar, clave").
    { k: 'ordenes', grupo: 'conciencia', label: 'Obedece órdenes', tipo: 'select', compactar: true,
      opciones: ['Obedece órdenes simples', 'Obedece algunas órdenes', 'No obedece órdenes'],
      notas: {
        'Obedece órdenes simples': { nivel: 'normal',   texto: 'Respuesta cortical conservada.' },
        'Obedece algunas órdenes': { nivel: 'leve',     texto: 'Cuantifícalo: ¿cuántas de 5 órdenes simples?: clave para el despertar.' },
        'No obedece órdenes':      { nivel: 'moderado', texto: 'Correlacionar con sedación / estado de conciencia.' },
      } },
    { k: 'ordenesDe5', grupo: 'conciencia', label: 'Órdenes obedecidas', tipo: 'num', unidad: 'de 5', dependeDe: { campo: 'ordenes', en: ['Obedece algunas órdenes'] },
      // La frase "obedece algunas órdenes" se REEMPLAZA por este conteo en la
      // prosa (ver esOrdenesRedundantePorConteo) — el número es el dato.
      proseFmt: 'obedece {v} de 5 órdenes simples',
      bandas: [
        { hasta: 3, texto: '<4/5: aún no apoya criterio de despertar', nivel: 'info' },
        { hasta: 5, texto: '≥4/5: apoya criterio de despertar (p. ej. para extubación)', nivel: 'normal' },
        { hasta: Infinity, texto: 'El máximo son 5 órdenes', nivel: 'info' },
      ] },
    // Objetivo de sedación: profundidad buscada, en catálogo (antes texto
    // libre — las tres profundidades cubren el uso real y la prosa queda
    // autocontenida). Un draft viejo con texto libre sigue imprimiendo tal cual.
    { k: 'objetivoSedacion', grupo: 'conciencia', label: 'Objetivo de sedación', tipo: 'select', compactar: true, dependeDe: { campo: 'sedacion', en: ['Con sedación'] },
      opciones: ['Sedación ligera', 'Sedación moderada', 'Sedación profunda'],
      notas: {
        'Sedación ligera':   { nivel: 'info', texto: 'RASS 0 a −2: despertar precoz, reevalúa por turno.' },
        'Sedación moderada': { nivel: 'info', texto: 'RASS −3: profundidad intermedia.' },
        'Sedación profunda': { nivel: 'info', texto: 'RASS −4 a −5: BNM / protección; reevalúa la indicación a diario.' },
      },
      prose: {
        'Sedación ligera':   'con objetivo de sedación ligera',
        'Sedación moderada': 'con objetivo de sedación moderada',
        'Sedación profunda': 'con objetivo de sedación profunda',
      } },
    { k: 'sedoanalgesia', grupo: 'conciencia', label: 'Sedoanalgesia', tipo: 'multi', dependeDe: { campo: 'sedacion', en: ['Con sedación'] },
      opciones: ['Propofol', 'Midazolam', 'Dexmedetomidina', 'Fentanilo', 'Remifentanilo', 'Ketamina', 'Esketamina', 'Morfina'],
      nota: { nivel: 'info', texto: 'En sedoanalgesia: vigila profundidad, hemodinamia y sitio de infusión.' } },
    // Dosis y tendencia POR droga de sedación (misma decisión que la DVA,
    // mismo patrón porDroga: propofol subiendo + fentanilo bajando es el caso
    // real, y una tendencia global mentía). Los viejos sedoDosis/sedoTendencia
    // se retiraron del catálogo (drafts los pierden en silencio, vida útil de
    // horas).
    { k: 'sedoDetalle', grupo: 'conciencia', label: 'Dosis y tendencia por droga', tipo: 'porDroga', fuente: 'sedoanalgesia', dependeDe: { campo: 'sedoanalgesia' } },
    // BIS: profundidad anestésica objetivada por monitor (dictado 2026-08-01,
    // "si tiene BIS, agregar valor"). Solo si el paciente lo tiene: el campo
    // vacío no pregunta nada.
    { k: 'bis', grupo: 'conciencia', label: 'BIS (si tiene monitor)', tipo: 'num', dependeDe: { campo: 'sedacion', en: ['Con sedación'] },
      proseFmt: 'BIS {v}',
      pista: 'Índice biespectral: objetiva la profundidad de la sedación cuando hay monitor.',
      bandas: [
        { hasta: 39, texto: 'Sedación muy profunda: correlaciona con el objetivo indicado', nivel: 'moderado' },
        { hasta: 60, texto: 'Rango habitual de sedación profunda (40 a 60)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Sobre 60: sedación superficial; en BNM es de riesgo, avisa', nivel: 'moderado' },
      ] },
    { k: 'bnm', grupo: 'conciencia', label: 'Bloqueo neuromuscular', tipo: 'multi', dependeDe: { campo: 'sedacion', en: ['Con sedación'] },
      opciones: ['Rocuronio', 'Cisatracurio', 'Vecuronio'],
      nota: { nivel: 'moderado', texto: 'En BNM: requiere sedación profunda asegurada; protege córneas y vigila TOF.' } },
    // TOF: la profundidad del bloqueo objetivada (dictado 2026-08-01, "TOF
    // también para bloqueo"). Respuestas al tren de cuatro del estimulador.
    { k: 'tof', grupo: 'conciencia', label: 'TOF', tipo: 'select', compactar: true, dependeDe: { campo: 'bnm' },
      opciones: ['0/4', '1/4', '2/4', '3/4', '4/4'],
      proseFmt: 'TOF {v}',
      pista: 'Tren de cuatro: cuántas respuestas de 4 da el estimulador. El objetivo habitual de BNM continuo es 1 a 2 de 4.',
      notas: {
        '0/4': { nivel: 'moderado', texto: 'Bloqueo muy profundo: sin respuestas, correlaciona con la indicación.' },
        '4/4': { nivel: 'info', texto: 'Sin bloqueo efectivo: 4 respuestas de 4.' },
      } },
    // Delirium → se objetiva con CAM-ICU.
    { k: 'delirium', grupo: 'delirium', label: 'Delirium', tipo: 'select', compactar: true, opciones: ['No', 'Sí', 'No evaluable'],
      notas: {
        'No':          { nivel: 'normal',   texto: 'Sin delirium: reevaluar por turno.' },
        'Sí':          { nivel: 'moderado', texto: 'Objetívalo con CAM-ICU; revisa causas y medidas no farmacológicas.' },
        'No evaluable':{ nivel: 'info',     texto: 'RASS muy bajo / no colabora: reevaluar.' },
      },
      // Las tres opciones son ambiguas solas entre comas ("No" ¿de qué?) —
      // con sustantivo quedan autocontenidas.
      prose: { 'No': 'sin delirium', 'Sí': 'con delirium', 'No evaluable': 'delirium no evaluable' } },
    // Dolor → objetivar ubicación, intensidad (EVA/CPOT) y manejo.
    { k: 'dolor', grupo: 'dolor', label: 'Dolor', tipo: 'select', compactar: true, rama: true, opciones: ['Sin dolor', 'Con dolor', 'No evaluable'],
      notas: {
        // El dolor SIEMPRE se objetiva con escala (regla del fundador,
        // 2026-07-14): EVA 0 o CPOT <3 documentan la AUSENCIA de dolor —
        // "sin dolor" a secas es opinión, con escala es dato.
        'Sin dolor':    { nivel: 'normal', texto: 'Objetívalo igual: EVA 0 (o CPOT <3) documenta la ausencia de dolor.' },
        'Con dolor':    { nivel: 'leve', texto: 'Objetiva intensidad con EVA (o CPOT si no comunica) y registra manejo.' },
        'No evaluable': { nivel: 'info', texto: 'Usa CPOT en el paciente no comunicante.' },
      },
      // "Sin dolor"/"Con dolor" ya se leen solas; solo "No evaluable" es ambigua.
      prose: { 'No evaluable': 'dolor no evaluable' } },
    { k: 'dolorUbicacion', grupo: 'dolor', label: 'Ubicación del dolor', tipo: 'text', dependeDe: { campo: 'dolor', en: ['Con dolor'] }, placeholder: 'Ej: zona quirúrgica, lumbar, torácico' },
    { k: 'dolorManejo', grupo: 'dolor', label: 'Manejo del dolor', tipo: 'text', dependeDe: { campo: 'dolor', en: ['Con dolor'] }, placeholder: 'Ej: paracetamol horario, opioide SOS, medidas no farmacológicas' },
    // Movilidad indicada.
    { k: 'reposo', grupo: 'movilidad', label: 'Reposo', tipo: 'select', compactar: true, opciones: ['Reposo absoluto', 'Reposo relativo', 'Reposo con kinesioterapia', 'Deambula'] },
    // Contención mecánica: requiere indicación médica vigente — se registra
    // aparte de "reposo" porque cambia la vigilancia (perfusión distal).
    { k: 'contencion', grupo: 'movilidad', label: 'Contención', tipo: 'select', compactar: true, opciones: ['Sin contención', 'Con contención'],
      notas: { 'Con contención': { nivel: 'moderado', texto: 'Exige indicación médica vigente; control seriado de perfusión distal y piel (al menos c/2 h), con liberación periódica y reevaluación de la indicación.' } } },
    { k: 'contencionZona', grupo: 'movilidad', label: 'Zona de contención', tipo: 'multi', dependeDe: { campo: 'contencion', en: ['Con contención'] }, opciones: ['Muñeca derecha', 'Muñeca izquierda', 'Tobillo derecho', 'Tobillo izquierdo', 'Tórax'] },
    { k: 'contencionMotivo', grupo: 'movilidad', label: 'Motivo de contención', tipo: 'select', compactar: true, dependeDe: { campo: 'contencion', en: ['Con contención'] }, opciones: ['Agitación psicomotora', 'Riesgo de retiro de dispositivos por el paciente', 'Riesgo de caída', 'Indicación médica'],
      // Las cuatro opciones son ambiguas solas ("Indicación médica" ¿de qué?) —
      // con "contención por" delante quedan autocontenidas.
      prose: {
        'Agitación psicomotora':                 'contención por agitación psicomotora',
        'Riesgo de retiro de dispositivos por el paciente': 'contención por riesgo de que el paciente se retire dispositivos',
        'Riesgo de caída':                        'contención por riesgo de caída',
        'Indicación médica':                      'contención por indicación médica',
      } },
    // Examen físico.
    { k: 'pupilas', grupo: 'examen', label: 'Pupilas', tipo: 'select', opciones: ['Isocóricas', 'Anisocóricas', 'Mióticas', 'Midriáticas'],
      notas: {
        'Isocóricas':   { nivel: 'normal',   texto: 'Simétricas: esperado.' },
        'Anisocóricas': { nivel: 'severo',   texto: 'Asimetría: descartar foco / HTIC, avisar.' },
        'Mióticas':     { nivel: 'leve',     texto: 'Puntiformes: opioides o lesión pontina.' },
        'Midriáticas':  { nivel: 'moderado', texto: 'Dilatadas: descartar HTIC o daño anóxico.' },
      } },
    { k: 'reactividad', grupo: 'examen', label: 'Reactividad', tipo: 'select', opciones: ['Fotorreactivas', 'Hiporreactivas', 'Arreactivas'],
      // Sin `prose` salía "reactividad fotorreactivas" (label singular +
      // adjetivo plural, discordante). El adjetivo solo basta: siempre habla
      // de las pupilas y se encadena tras "pupilas isocóricas".
      prose: { 'Fotorreactivas': 'fotorreactivas', 'Hiporreactivas': 'hiporreactivas', 'Arreactivas': 'arreactivas' },
      notas: {
        'Fotorreactivas': { nivel: 'normal', texto: 'Reactivas a la luz: esperado.' },
        'Hiporreactivas': { nivel: 'leve',   texto: 'Respuesta lenta: vigilar.' },
        'Arreactivas':    { nivel: 'severo', texto: 'Sin respuesta: signo grave, avisar.' },
      } },
    // Diámetro pupilar en mm — complementa el examen cualitativo de arriba.
    { k: 'pupilaDerechaMm', grupo: 'examen', label: 'Pupila derecha', tipo: 'num', unidad: 'mm', bandas: [
      { hasta: 1.9, texto: 'Miótica (<2 mm)', nivel: 'leve' },
      { hasta: 5, texto: 'Normal (2 a 5 mm)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Midriática (>5 mm)', nivel: 'moderado' },
    ] },
    { k: 'pupilaIzquierdaMm', grupo: 'examen', label: 'Pupila izquierda', tipo: 'num', unidad: 'mm', bandas: [
      { hasta: 1.9, texto: 'Miótica (<2 mm)', nivel: 'leve' },
      { hasta: 5, texto: 'Normal (2 a 5 mm)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Midriática (>5 mm)', nivel: 'moderado' },
    ] },
    { k: 'deficitLocalizacion', grupo: 'examen', label: 'Déficit motor', tipo: 'select', compactar: true, opciones: ['Sin déficit motor', 'Hemicuerpo derecho', 'Hemicuerpo izquierdo', 'Ambas extremidades superiores', 'Ambas extremidades inferiores', 'Generalizado'],
      // Solo "Sin déficit motor" ya se lee autocontenida; el resto es ambiguo
      // suelto ("Hemicuerpo derecho" ¿de qué?) — con "déficit motor" delante sí.
      prose: {
        'Hemicuerpo derecho':               'déficit motor de hemicuerpo derecho',
        'Hemicuerpo izquierdo':              'déficit motor de hemicuerpo izquierdo',
        'Ambas extremidades superiores':     'déficit motor de ambas extremidades superiores',
        'Ambas extremidades inferiores':     'déficit motor de ambas extremidades inferiores',
        'Generalizado':                      'déficit motor generalizado',
      } },
    // ── DVE: la rama completa SE MUDÓ al examen físico (segmento "Cabeza y
    // cara", pedido del fundador 2026-08-10): el detalle del drenaje (cero,
    // altura, oscilación, débito, aspecto del LCR) se constata mirando el
    // circuito EN la visita, así que vive en CAMPOS_EXAMEN.cabeza y llega al
    // neuro de la evolución por el puente del examen, como pupilas. El chip
    // "Drenaje ventricular (DVE)" de hallazgos sobrevive (puentes de
    // recepción). Un draft con los campos dve* viejos simplemente deja de
    // imprimirlos: los registros viven horas, no días (mismo precedente de la
    // poda de la visita, 30-jul). No restaurar la rama aquí.
    // ── Terapia osmótica (rama, mismo dictado): SHT o manitol con objetivo de
    // natremia es manejo neuro de rutina en UPC y no tenía dónde vivir — "se
    // inicia SHT al 3% a 30 ml/hr con objetivo de natremia de 150" quedaba
    // solo en una nota libre.
    // Solo en UCI (`servicios`, ver campoEnServicio), decisión del fundador 2026-09-29.
    { k: 'osmoterapia', grupo: 'osmoterapia', label: 'Terapia osmótica', tipo: 'select', compactar: true, rama: true, servicios: ['uci'],
      opciones: ['Sin terapia osmótica', 'Suero hipertónico (SHT)', 'Manitol'],
      prose: { 'Suero hipertónico (SHT)': 'en terapia con suero hipertónico', 'Manitol': 'en terapia con manitol' },
      notas: {
        'Suero hipertónico (SHT)': { nivel: 'moderado', texto: 'Natremia seriada según indicación; idealmente por vía central (riesgo de flebitis por osmolaridad).' },
        'Manitol':                 { nivel: 'moderado', texto: 'Vigila diuresis osmótica, volemia y función renal; natremia y osmolaridad seriadas.' },
      } },
    { k: 'oshtConcentracion', grupo: 'osmoterapia', label: 'Concentración', tipo: 'select', compactar: true, dependeDe: { campo: 'osmoterapia', en: ['Suero hipertónico (SHT)'] },
      opciones: ['SHT al 3%', 'SHT al 7,5%', 'SHT al 10%'] },
    { k: 'osmoVelocidad', grupo: 'osmoterapia', label: 'Velocidad de infusión', tipo: 'num', unidad: 'ml/hr', dependeDe: { campo: 'osmoterapia', en: ['Suero hipertónico (SHT)', 'Manitol'] },
      proseFmt: 'a {v} ml/hr' },
    { k: 'osmoNatremiaObjetivo', grupo: 'osmoterapia', label: 'Natremia objetivo', tipo: 'num', unidad: 'mEq/L', dependeDe: { campo: 'osmoterapia', en: ['Suero hipertónico (SHT)', 'Manitol'] },
      proseFmt: 'con objetivo de natremia de {v} mEq/L',
      pista: 'La meta que fijó el equipo (habitualmente 145 a 155 en HTIC): contra ella se titula la infusión y el control de laboratorio.' },
    { k: 'osmoNatremiaControl', grupo: 'osmoterapia', label: 'Última natremia', tipo: 'num', unidad: 'mEq/L', dependeDe: { campo: 'osmoterapia', en: ['Suero hipertónico (SHT)', 'Manitol'] },
      proseFmt: 'última natremia {v} mEq/L',
      bandas: [
        { hasta: 134, texto: 'Hiponatremia: en el paciente neurocrítico es de riesgo, avisa', nivel: 'moderado' },
        { hasta: 145, texto: 'Normonatremia', nivel: 'normal' },
        { hasta: 155, texto: 'Rango habitual de osmoterapia: compara con el objetivo indicado', nivel: 'info' },
        { hasta: Infinity, texto: 'Sobre 155: hipernatremia severa, avisa', nivel: 'severo' },
      ] },
  ],
  // Hemodinamia rediseñada con la lógica de ramas de neuro (feedback del
  // fundador, 2026-07-16): estado que se mantiene, ritmo que pide manejo si no
  // es sinusal, drogas como rama con dosis y tendencia POR droga, temperatura
  // con su manejo, examen físico de perfusión, microhemodinamia avanzada
  // opt-in con explicación sencilla por parámetro, y el soporte extracorpóreo
  // mencionado superficialmente (el detalle de la TRR vive en Renal; aquí
  // importa su efecto en la hemodinamia). Shock y marcapasos estructurado se
  // RETIRARON a pedido (el ritmo cubre el marcapasos; un draft viejo con esas
  // claves compila sin crash y sin emitirlas, mismo patrón que la tendencia).
  hemodinamia: [
    // ── Estado hemodinámico ──
    { k: 'presion', grupo: 'estado', label: 'Presión', tipo: 'select', compactar: true, opciones: ['Hipotenso', 'Normotenso', 'Hipertenso'],
      notas: {
        'Hipotenso':  { nivel: 'moderado', texto: 'PA bajo la meta: evalúa volumen y drogas, avisa.' },
        'Normotenso': { nivel: 'normal',   texto: 'PA en rango (PAM 65 o más).' },
        'Hipertenso': { nivel: 'moderado', texto: 'PA elevada: vigila síntomas y registra el manejo.' },
      } },
    { k: 'pamObjetivo', grupo: 'estado', label: 'PAM objetivo', tipo: 'num', unidad: 'mmHg',
      pista: 'La meta de presión arterial media indicada para este paciente. Lo habitual es 65 o más.' },
    { k: 'fc', grupo: 'estado', label: 'Frecuencia', tipo: 'select', compactar: true, opciones: ['Bradicárdico', 'Normocárdico', 'Taquicárdico'],
      notas: {
        'Bradicárdico':  { nivel: 'leve',     texto: 'FC bajo 60: correlaciona con la clínica y los fármacos.' },
        'Normocárdico':  { nivel: 'normal',   texto: 'FC en rango (60 a 100).' },
        'Taquicárdico':  { nivel: 'moderado', texto: 'FC sobre 100: piensa en dolor, fiebre o hipovolemia.' },
      } },
    // Ritmo cardíaco: el trazado que el monitor muestra, más allá de la FC.
    { k: 'ritmo', grupo: 'estado', label: 'Ritmo', tipo: 'select', opciones: ['Sinusal', 'Fibrilación auricular', 'Flutter auricular', 'Ritmo de marcapasos', 'Extrasístoles frecuentes'],
      notas: {
        'Sinusal':                  { nivel: 'normal',   texto: 'Ritmo regular, esperado.' },
        'Fibrilación auricular':    { nivel: 'moderado', texto: 'Controla la respuesta ventricular y la anticoagulación según indicación.' },
        'Flutter auricular':        { nivel: 'moderado', texto: 'Controla la respuesta ventricular y la anticoagulación según indicación.' },
        'Ritmo de marcapasos':      { nivel: 'info',     texto: 'Verifica captura y umbral por turno.' },
        'Extrasístoles frecuentes': { nivel: 'leve',     texto: 'Correlaciona con los electrolitos (potasio y magnesio).' },
      },
      // Sin `prose`, el label del campo (no es compactar) se anteponía al
      // valor y "Ritmo de marcapasos" imprimía "ritmo ritmo de marcapasos"
      // (palabra duplicada); las arritmias quedan con la preposición clínica
      // ("en fibrilación auricular", como se dicta una evolución real).
      prose: {
        'Fibrilación auricular':    'en fibrilación auricular',
        'Flutter auricular':        'en flutter auricular',
        'Ritmo de marcapasos':      'en ritmo de marcapasos',
        'Extrasístoles frecuentes': 'con extrasístoles frecuentes',
      } },
    // Un ritmo que no es sinusal pide contexto: ¿está en manejo o la respuesta
    // ya es adecuada? (feedback del fundador: "FA con respuesta ventricular
    // adecuada", el trazado solo no cuenta la historia).
    { k: 'ritmoManejo', grupo: 'estado', label: 'Manejo del ritmo', tipo: 'text', proseFmt: '{v}',
      dependeDe: { campo: 'ritmo', en: ['Fibrilación auricular', 'Flutter auricular', 'Ritmo de marcapasos', 'Extrasístoles frecuentes'] },
      placeholder: 'Ej: FA con respuesta ventricular adecuada, en manejo con amiodarona' },
    { k: 'temp', grupo: 'estado', label: 'Temperatura', tipo: 'select', compactar: true, opciones: ['Hipotermia', 'Afebril', 'Febrícula', 'Fiebre'],
      notas: {
        'Hipotermia': { nivel: 'moderado', texto: 'Bajo 36 °C: abriga y busca la causa.' },
        'Afebril':    { nivel: 'normal',   texto: 'Temperatura normal.' },
        'Febrícula':  { nivel: 'leve',     texto: '37,5 a 38 °C: vigilar la curva.' },
        'Fiebre':     { nivel: 'moderado', texto: 'Sobre 38 °C: cultivos según indicación y registra el manejo.' },
      } },
    // La temperatura alterada pide su manejo (feedback del fundador: "fiebre,
    // en manejo con?").
    { k: 'tempManejo', grupo: 'estado', label: 'Manejo de la temperatura', tipo: 'text', proseFmt: 'en manejo con {v}',
      dependeDe: { campo: 'temp', en: ['Hipotermia', 'Febrícula', 'Fiebre'] },
      placeholder: 'Ej: paracetamol horario, medidas físicas, manta térmica' },
    // ── Drogas vasoactivas (rama, como la sedación en neuro) ──
    { k: 'dvaSoporte', grupo: 'drogas', label: 'Drogas vasoactivas', tipo: 'select', compactar: true, rama: true,
      opciones: ['Sin drogas vasoactivas', 'Con drogas vasoactivas'],
      notas: {
        'Sin drogas vasoactivas': { nivel: 'normal',   texto: 'Sin soporte vasoactivo.' },
        'Con drogas vasoactivas': { nivel: 'moderado', texto: 'Marca cuáles y registra dosis y tendencia de cada una: ese es el dato que la entrega necesita.' },
      } },
    // Presores/inótropos primero (el uso clásico de "DVA"); antihipertensivos y
    // vasodilatadores EV después: se titulan en el mismo bombeo/vigilancia
    // estrecha y el enfermero los busca en el mismo campo al armar la evolución.
    { k: 'dva', grupo: 'drogas', label: 'Cuáles', tipo: 'multi', dependeDe: { campo: 'dvaSoporte', en: ['Con drogas vasoactivas'] },
      opciones: [
        'Noradrenalina', 'Adrenalina', 'Dopamina', 'Dobutamina', 'Vasopresina', 'Milrinona',
        'Labetalol', 'Urapidilo', 'Nitroglicerina', 'Nitroprusiato', 'Esmolol',
      ],
      // proseFmt: "cuáles: noradrenalina…" era jerga de formulario; la prosa
      // dice "con noradrenalina y vasopresina" (y la rama genérica "con drogas
      // vasoactivas" se omite cuando la lista ya lo prueba, ver esCampoRedundante).
      proseFmt: 'con {v}',
      nota: { nivel: 'moderado', texto: 'En drogas vasoactivas: vigila perfusión, dosis y sitio de infusión (idealmente por CVC).' } },
    // El objetivo de la terapia: contra qué meta se titula (se relaciona con la
    // PAM objetivo del estado, pero acá es la meta de LAS DROGAS).
    { k: 'dvaObjetivo', grupo: 'drogas', label: 'Objetivo de las drogas', tipo: 'text', dependeDe: { campo: 'dvaSoporte', en: ['Con drogas vasoactivas'] },
      placeholder: 'Ej: PAM sobre 65, PAS bajo 140' },
    // Dosis y tendencia POR droga (feedback del fundador: "una por cada uno y
    // que se haga relación entre ambos"): cada droga marcada arriba despliega
    // su dosis y si va subiendo o bajando. Reemplaza a los viejos dvaDosis y
    // dvaTendencia globales (una sola tendencia mentía con dos drogas en
    // direcciones opuestas, que es el caso clínico interesante).
    { k: 'dvaDetalle', grupo: 'drogas', label: 'Dosis y tendencia por droga', tipo: 'porDroga', fuente: 'dva', dependeDe: { campo: 'dva' } },
    // ── Examen físico y perfusión ──
    { k: 'perfusion', grupo: 'perfusion', label: 'Perfusión', tipo: 'select', compactar: true, opciones: ['Bien perfundido', 'Hipoperfusión periférica'],
      notas: {
        'Bien perfundido':          { nivel: 'normal',   texto: 'Piel tibia, llene conservado, pulsos presentes.' },
        'Hipoperfusión periférica': { nivel: 'moderado', texto: 'Frialdad, llene lento o moteado: correlaciona con presión y lactato, avisa.' },
      },
      prose: { 'Hipoperfusión periférica': 'con hipoperfusión periférica' } },
    { k: 'piel', grupo: 'perfusion', label: 'Piel', tipo: 'select', opciones: ['Tibia y rosada', 'Fría', 'Pálida', 'Moteada', 'Cianótica'],
      notas: {
        'Tibia y rosada': { nivel: 'normal',   texto: 'Buena perfusión periférica.' },
        'Fría':           { nivel: 'leve',     texto: 'Extremidades frías: vigila la perfusión.' },
        'Pálida':         { nivel: 'leve',     texto: 'Palidez: piensa en anemia o hipoperfusión.' },
        'Moteada':        { nivel: 'moderado', texto: 'Moteado (mottling): hipoperfusión, avisa. Objetívalo con la escala de abajo.' },
        'Cianótica':      { nivel: 'severo',   texto: 'Cianosis: hipoxemia o shock, avisa.' },
      } },
    { k: 'pulsos', grupo: 'perfusion', label: 'Pulsos', tipo: 'select', compactar: true,
      opciones: ['Pulsos presentes y simétricos', 'Pulsos disminuidos', 'Pulsos asimétricos', 'Pulso ausente en alguna extremidad'],
      notas: {
        'Pulsos presentes y simétricos':      { nivel: 'normal',   texto: 'Esperado.' },
        'Pulsos disminuidos':                 { nivel: 'leve',     texto: 'Compara ambos lados y correlaciona con la perfusión.' },
        'Pulsos asimétricos':                 { nivel: 'moderado', texto: 'Diferencia entre lados: registra cuál y avisa.' },
        'Pulso ausente en alguna extremidad': { nivel: 'severo',   texto: 'Urgencia vascular: avisa de inmediato y registra la extremidad.' },
      } },
    { k: 'edema', grupo: 'perfusion', label: 'Edema', tipo: 'select', compactar: true, opciones: ['Sin edema', 'Edema leve', 'Edema marcado', 'Anasarca'],
      notas: {
        'Sin edema':     { nivel: 'normal',   texto: 'Sin edema.' },
        'Edema leve':    { nivel: 'leve',     texto: 'Edema leve: correlaciona con el balance.' },
        'Edema marcado': { nivel: 'moderado', texto: 'Edema marcado: revisa balance y función renal y cardíaca.' },
        'Anasarca':      { nivel: 'moderado', texto: 'Edema generalizado: avisa.' },
      } },
    { k: 'llenado', grupo: 'perfusion', label: 'Llene capilar', tipo: 'num', unidad: 's', bandas: [
      { hasta: 2, texto: 'Normal (2 s o menos)', nivel: 'normal' },
      { hasta: 3, texto: 'Límite (3 s)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Enlentecido (más de 3 s): hipoperfusión', nivel: 'moderado' },
    ] },
    // ── Microhemodinamia avanzada (rama opt-in: UCI con monitoreo invasivo) ──
    // Cada parámetro trae su explicación sencilla (`pista`), visible antes de
    // ingresar el valor: la duda se resuelve ahí mismo, sin salir del flujo.
    // Solo en UCI (`servicios`, ver campoEnServicio), decisión del fundador 2026-09-29.
    { k: 'monitoreoAvanzado', grupo: 'micro', label: 'Monitoreo avanzado', tipo: 'select', compactar: true, rama: true, servicios: ['uci'],
      opciones: ['Sin monitoreo avanzado', 'Con monitoreo avanzado'],
      notas: {
        'Con monitoreo avanzado': { nivel: 'info', texto: 'PiCCO, Swan-Ganz o similar: registra los valores que tu unidad monitoriza. Cada campo trae su explicación.' },
      },
      prose: {
        'Sin monitoreo avanzado': 'sin monitoreo hemodinámico avanzado',
        'Con monitoreo avanzado': 'con monitoreo hemodinámico avanzado',
      } },
    { k: 'lactato', grupo: 'micro', label: 'Lactato', tipo: 'num', unidad: 'mmol/L', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'Marcador de perfusión tisular: cuando el tejido no recibe suficiente oxígeno, el lactato sube. Normal bajo 2.',
      bandas: [
        { hasta: 1.9, texto: 'Normal (bajo 2)', nivel: 'normal' },
        { hasta: 4, texto: 'Elevado (2 a 4): vigila la perfusión y la tendencia', nivel: 'leve' },
        { hasta: Infinity, texto: 'Hiperlactatemia (sobre 4): hipoperfusión, avisa', nivel: 'severo' },
      ] },
    { k: 'pvc', grupo: 'micro', label: 'PVC', tipo: 'num', unidad: 'mmHg', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'Presión venosa central: estima la precarga del corazón derecho. Se mide por el CVC. Habitual entre 2 y 12.',
      bandas: [
        { hasta: 1.9, texto: 'Baja (bajo 2): valora la volemia', nivel: 'leve' },
        { hasta: 12, texto: 'Rango habitual (2 a 12 según soporte)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Elevada (sobre 12): piensa en sobrecarga o falla derecha', nivel: 'leve' },
      ] },
    { k: 'gastoCardiaco', grupo: 'micro', label: 'Gasto cardíaco (GC)', tipo: 'num', unidad: 'L/min', proseFmt: 'gasto cardíaco {v} L/min', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'Cuánta sangre bombea el corazón por minuto. Normal entre 4 y 8.',
      bandas: [
        { hasta: 3.9, texto: 'Bajo (menos de 4): correlaciona con perfusión y presión', nivel: 'moderado' },
        { hasta: 8, texto: 'Normal (4 a 8)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Elevado (sobre 8): estados hiperdinámicos, como la sepsis', nivel: 'leve' },
      ] },
    { k: 'indiceCardiaco', grupo: 'micro', label: 'Índice cardíaco (IC)', tipo: 'num', unidad: 'L/min/m²', proseFmt: 'índice cardíaco {v} L/min/m²', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'El gasto cardíaco ajustado al tamaño del paciente (superficie corporal). Normal entre 2,5 y 4.',
      bandas: [
        { hasta: 2.1, texto: 'Bajo (menos de 2,2): riesgo de bajo débito, avisa', nivel: 'moderado' },
        { hasta: 2.4, texto: 'Límite (2,2 a 2,4): vigila la tendencia', nivel: 'leve' },
        { hasta: 4, texto: 'Normal (2,5 a 4)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Elevado (sobre 4): estados hiperdinámicos', nivel: 'leve' },
      ] },
    { k: 'evlwi', grupo: 'micro', label: 'EVLWI', tipo: 'num', unidad: 'ml/kg', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'Agua pulmonar extravascular: cuantifica el edema pulmonar. Normal hasta 7.',
      bandas: [
        { hasta: 7, texto: 'Normal (hasta 7)', nivel: 'normal' },
        { hasta: 10, texto: 'Elevado (8 a 10): vigila el balance', nivel: 'leve' },
        { hasta: Infinity, texto: 'Muy elevado (sobre 10): edema pulmonar significativo', nivel: 'moderado' },
      ] },
    { k: 'svcO2', grupo: 'micro', label: 'SvcO₂', tipo: 'num', unidad: '%', dependeDe: { campo: 'monitoreoAvanzado', en: ['Con monitoreo avanzado'] },
      pista: 'Saturación venosa central: cuánto oxígeno vuelve al corazón después de pasar por los tejidos. Refleja el equilibrio entre aporte y consumo. Normal entre 65 y 80.',
      bandas: [
        { hasta: 64, texto: 'Baja (bajo 65): el tejido extrae más oxígeno del habitual, correlaciona con GC y hemoglobina', nivel: 'moderado' },
        { hasta: 80, texto: 'Normal (65 a 80)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Alta (sobre 80): puede verse en sepsis o sedación profunda', nivel: 'leve' },
      ] },
    // ── Soporte extracorpóreo (mención superficial, por su efecto HDN) ──
    // Solo en UCI (`servicios`, ver campoEnServicio), decisión del fundador 2026-09-29.
    { k: 'soporteExtracorporeo', grupo: 'soporteExtra', label: 'Soporte extracorpóreo', tipo: 'select', compactar: true, servicios: ['uci'],
      opciones: ['Sin soporte extracorpóreo', 'TRR continua', 'Hemodiálisis intermitente', 'ECMO veno-venoso', 'ECMO veno-arterial'],
      notas: {
        'TRR continua':             { nivel: 'info',     texto: 'Registra aquí solo su efecto hemodinámico (volemia, presión). El detalle de la terapia va en Renal.' },
        'Hemodiálisis intermitente':{ nivel: 'info',     texto: 'Registra aquí solo su efecto hemodinámico (volemia, presión). El detalle de la terapia va en Renal.' },
        'ECMO veno-venoso':         { nivel: 'moderado', texto: 'Soporte respiratorio extracorpóreo: vigila flujos y sitios de cánulas.' },
        'ECMO veno-arterial':       { nivel: 'moderado', texto: 'Soporte circulatorio extracorpóreo: vigila flujos, perfusión distal y sitios de cánulas.' },
      },
      prose: {
        'TRR continua':              'en TRR continua',
        'Hemodiálisis intermitente': 'en hemodiálisis intermitente',
        'ECMO veno-venoso':          'en ECMO veno-venoso',
        'ECMO veno-arterial':        'en ECMO veno-arterial',
      } },
  ],
  // Ventilación reestructurada con la VÍA AÉREA como rama raíz (dictado del
  // fundador, 2026-07-16): nativa (oxigenoterapia / CNAF / VNI, cada una con
  // su contexto, sus parámetros con explicación y su "¿funciona?") o
  // artificial (TOT o TQT, con la VMI centralizada en volumen control /
  // presión control / espontáneo y los parámetros que cada modo pide).
  ventilacion: [
    { k: 'viaAerea', grupo: 'soporte', label: 'Vía aérea', tipo: 'select', compactar: true, rama: true,
      opciones: ['Vía aérea nativa', 'Vía aérea artificial'],
      notas: {
        'Vía aérea nativa':     { nivel: 'normal', texto: 'Respira por su propia vía aérea, con o sin apoyo de oxígeno.' },
        'Vía aérea artificial': { nivel: 'info',   texto: 'TOT o traqueostomía: registra el dispositivo y su ventilación.' },
      },
      prose: { 'Vía aérea nativa': 'con vía aérea nativa', 'Vía aérea artificial': 'con vía aérea artificial' } },
    // ── Vía aérea nativa: el soporte define qué parámetros aplican ──
    // (visible mientras NO se declare vía artificial: los drafts previos a la
    // rama siguen mostrando e imprimiendo su soporte tal cual).
    { k: 'soporte', grupo: 'soporte', label: 'Soporte', tipo: 'select', compactar: true, dependeDe: { campo: 'viaAerea', noEn: ['Vía aérea artificial'] },
      opciones: ['Aire ambiente', 'Oxigenoterapia', 'CNAF', 'VNI'],
      notas: {
        'Aire ambiente':  { nivel: 'normal',   texto: 'Sin requerimiento de O₂.' },
        'Oxigenoterapia': { nivel: 'leve',     texto: 'Registra dispositivo, litros y saturación.' },
        'CNAF':           { nivel: 'moderado', texto: 'Alto flujo: registra contexto, flujo, FiO₂, temperatura y respuesta.' },
        'VNI':            { nivel: 'moderado', texto: 'Registra contexto, modo, presiones y respuesta. Cada parámetro trae su explicación.' },
      },
      // El soporte suelto entre comas no dice la relación del paciente con él;
      // con la preposición se lee como evolución ("en CNAF, flujo 50...").
      // "VMI" ya no es opción (vive bajo vía aérea artificial), pero conserva
      // su prosa para que un draft viejo siga imprimiendo "en VMI".
      prose: {
        'Aire ambiente':  'ventilando en aire ambiente',
        'Oxigenoterapia': 'con oxigenoterapia',
        'CNAF':           'en CNAF',
        'VNI':            'en VNI',
        'VMI':            'en VMI',
      } },
    // Oxigenoterapia convencional
    { k: 'o2Interfaz', grupo: 'soporte', label: 'Dispositivo', tipo: 'select', dependeDe: { campo: 'soporte', en: ['Oxigenoterapia'] },
      opciones: ['Naricera', 'Mascarilla simple', 'Venturi', 'Mascarilla de alto flujo', 'Mascarilla de reservorio'],
      // Sin `prose` salía "dispositivo naricera", jerga de formulario; con la
      // preposición se encadena natural tras "con oxigenoterapia".
      prose: {
        'Naricera':                 'por naricera',
        'Mascarilla simple':        'por mascarilla simple',
        'Venturi':                  'por mascarilla Venturi',
        'Mascarilla de alto flujo': 'por mascarilla de alto flujo',
        'Mascarilla de reservorio': 'por mascarilla de reservorio',
      } },
    // proseFmt: sin ella salía "litros 2 L/min" (label y unidad repiten la idea).
    { k: 'o2Flujo', grupo: 'soporte', label: 'Litros', tipo: 'num', unidad: 'L/min', dependeDe: { campo: 'soporte', en: ['Oxigenoterapia'] }, proseFmt: 'a {v} L/min' },
    // La FiO₂ en oxigenoterapia convencional es una estimación, no un dato del
    // equipo (pedido del fundador: decirlo, no esconderlo).
    { k: 'o2Fio2', grupo: 'soporte', label: 'FiO₂ estimada', tipo: 'num', unidad: '%', dependeDe: { campo: 'soporte', en: ['Oxigenoterapia'] },
      pista: 'Estimativa, no exacta: con naricera o mascarilla la FiO₂ real depende de cuánto aire inspira el paciente.' },
    { k: 'o2Tendencia', grupo: 'soporte', label: 'Tendencia', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['Oxigenoterapia'] },
      opciones: ['Escalando', 'Estable', 'Desescalando'],
      prose: { 'Escalando': 'escalando requerimientos de O₂', 'Estable': 'con requerimientos de O₂ estables', 'Desescalando': 'desescalando requerimientos de O₂' } },
    // Cánula nasal de alto flujo
    { k: 'cnafContexto', grupo: 'soporte', label: 'Contexto de la CNAF', tipo: 'text', proseFmt: 'por {v}', dependeDe: { campo: 'soporte', en: ['CNAF'] },
      placeholder: 'Ej: weaning de VMI, insuficiencia respiratoria hipoxémica' },
    { k: 'cnafFlujo', grupo: 'soporte', label: 'Flujo CNAF', tipo: 'num', unidad: 'L/min', proseFmt: 'flujo {v} L/min', dependeDe: { campo: 'soporte', en: ['CNAF'] } },
    { k: 'cnafFio2', grupo: 'soporte', label: 'FiO₂', tipo: 'num', unidad: '%', dependeDe: { campo: 'soporte', en: ['CNAF'] } },
    { k: 'cnafTemp', grupo: 'soporte', label: 'Temperatura', tipo: 'num', unidad: '°C', dependeDe: { campo: 'soporte', en: ['CNAF'] },
      pista: 'Temperatura del gas humidificado, habitualmente 31 a 37 °C.' },
    { k: 'cnafRespuesta', grupo: 'soporte', label: '¿Funciona?', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['CNAF'] },
      opciones: ['Buena respuesta', 'Respuesta parcial', 'Sin respuesta'],
      notas: {
        'Buena respuesta':   { nivel: 'normal',   texto: 'Menos trabajo respiratorio y mejor saturación.' },
        'Respuesta parcial': { nivel: 'leve',     texto: 'Reevalúa en 1 a 2 horas: FR, saturación y trabajo respiratorio.' },
        'Sin respuesta':     { nivel: 'moderado', texto: 'Considera escalar el soporte, avisa.' },
      },
      prose: { 'Buena respuesta': 'con buena respuesta', 'Respuesta parcial': 'con respuesta parcial', 'Sin respuesta': 'sin respuesta al soporte' } },
    { k: 'cnafTendencia', grupo: 'soporte', label: 'Tendencia', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['CNAF'] }, opciones: ['Escalando', 'Estable', 'Desescalando'],
      // "Estable" solo no dice de qué soporte; con "CNAF" delante sí.
      prose: { 'Escalando': 'escalando requerimientos', 'Estable': 'con requerimientos estables', 'Desescalando': 'desescalando requerimientos' } },
    // Ventilación no invasiva: cada parámetro con su explicación sencilla
    // (mismo trato didáctico que la microhemodinamia).
    { k: 'vniContexto', grupo: 'soporte', label: 'Contexto de la VNI', tipo: 'text', proseFmt: 'por {v}', dependeDe: { campo: 'soporte', en: ['VNI'] },
      placeholder: 'Ej: EPOC reagudizado, edema pulmonar agudo, apoyo nocturno' },
    { k: 'vniModo', grupo: 'soporte', label: 'Modo VNI', tipo: 'select', dependeDe: { campo: 'soporte', en: ['VNI'] }, opciones: ['CPAP', 'S/T', 'AVAPS', 'Otro modo'],
      // "modo VNI CPAP" repetía el dispositivo que el soporte ya dijo ("en
      // VNI, modo VNI CPAP"); la prosa deja solo "modo CPAP". BiPAP legado
      // conserva su prosa (drafts viejos).
      prose: { 'CPAP': 'modo CPAP', 'BiPAP': 'modo BiPAP', 'S/T': 'modo S/T', 'AVAPS': 'modo AVAPS', 'Otro modo': 'en otro modo de VNI' },
      notas: {
        'CPAP':  { nivel: 'info', texto: 'Una sola presión continua: mantiene la vía aérea abierta (típico en edema pulmonar y apnea).' },
        'S/T':   { nivel: 'info', texto: 'Espontáneo con respaldo: apoya cada inspiración y asegura una frecuencia mínima.' },
        'AVAPS': { nivel: 'info', texto: 'Ajusta la presión automáticamente para asegurar un volumen objetivo.' },
      } },
    { k: 'vniModoOtro', grupo: 'soporte', label: '¿Cuál modo?', tipo: 'text', proseFmt: 'modo {v}', dependeDe: { campo: 'vniModo', en: ['Otro modo'] }, placeholder: 'Ej: PAC, iVAPS' },
    // En CPAP no hay presión inspiratoria: IPAP, PS, tiempo de subida y tiempo
    // inspiratorio se ocultan (2026-09-29). Sin modo respondido siguen a la
    // vista, como siempre.
    { k: 'vniIpap', grupo: 'soporte', label: 'IPAP', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vniModo', noEn: ['CPAP'] },
      pista: 'Presión inspiratoria: la ayuda que el equipo entrega al inspirar.' },
    { k: 'vniEpap', grupo: 'soporte', label: 'EPAP', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'soporte', en: ['VNI'] },
      pista: 'Presión espiratoria (el PEEP de la VNI): mantiene la vía aérea abierta al final de la espiración.' },
    { k: 'vniPs', grupo: 'soporte', label: 'Presión de soporte', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vniModo', noEn: ['CPAP'] },
      pista: 'La diferencia entre IPAP y EPAP: el empuje real de cada inspiración.', proseFmt: 'PS {v} cmH₂O' },
    { k: 'vniFio2', grupo: 'soporte', label: 'FiO₂', tipo: 'num', unidad: '%', dependeDe: { campo: 'soporte', en: ['VNI'] } },
    { k: 'vniSubida', grupo: 'soporte', label: 'Tiempo de subida', tipo: 'text', dependeDe: { campo: 'vniModo', noEn: ['CPAP'] },
      placeholder: 'Ej: 2 (o 200 ms)', pista: 'Qué tan rápido sube la presión al inspirar: se ajusta a la comodidad del paciente.' },
    { k: 'vniTinsp', grupo: 'soporte', label: 'Tiempo inspiratorio', tipo: 'num', unidad: 's', dependeDe: { campo: 'vniModo', noEn: ['CPAP'] },
      pista: 'Cuánto dura cada inspiración entregada por el equipo.' },
    { k: 'vniPmax', grupo: 'soporte', label: 'Pmax', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'soporte', en: ['VNI'] },
      pista: 'Presión máxima de seguridad configurada en el equipo.' },
    { k: 'vniFuga', grupo: 'soporte', label: 'Fuga', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['VNI'] }, opciones: ['Sin fuga', 'Fuga leve', 'Fuga significativa'],
      notas: { 'Fuga significativa': { nivel: 'moderado', texto: 'Reajusta la interfaz: la fuga compromete la eficacia.' } } },
    { k: 'vniAcople', grupo: 'soporte', label: 'Acople', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['VNI'] }, opciones: ['Bien acoplado', 'Mal acoplado'],
      notas: { 'Mal acoplado': { nivel: 'moderado', texto: 'Asincronía: revisa interfaz y parámetros, avisa.' } } },
    { k: 'vniRespuesta', grupo: 'soporte', label: '¿Funciona?', tipo: 'select', compactar: true, dependeDe: { campo: 'soporte', en: ['VNI'] },
      opciones: ['Buena respuesta', 'Respuesta parcial', 'Sin respuesta'],
      notas: {
        'Buena respuesta':   { nivel: 'normal',   texto: 'Menos trabajo respiratorio y mejor intercambio.' },
        'Respuesta parcial': { nivel: 'leve',     texto: 'Reevalúa en 1 a 2 horas con gases si están indicados.' },
        'Sin respuesta':     { nivel: 'moderado', texto: 'Considera escalar el soporte, avisa.' },
      },
      prose: { 'Buena respuesta': 'con buena respuesta', 'Respuesta parcial': 'con respuesta parcial', 'Sin respuesta': 'sin respuesta al soporte' } },
    // ── Vía aérea artificial: TOT o traqueostomía ──
    { k: 'viaArtificial', grupo: 'soporte', label: 'Dispositivo', tipo: 'select', compactar: true, rama: true, dependeDe: { campo: 'viaAerea', en: ['Vía aérea artificial'] },
      opciones: ['TOT', 'TQT'],
      prose: { 'TOT': 'con tubo orotraqueal', 'TQT': 'con traqueostomía' } },
    // TQT: cómo ventila hoy (VMI, filtro o camino a decanular).
    { k: 'tqtSoporte', grupo: 'soporte', label: 'La TQT está', tipo: 'select', compactar: true, dependeDe: { campo: 'viaArtificial', en: ['TQT'] },
      opciones: ['En VMI', 'Con filtro humidificador', 'En plan de decanulación'],
      notas: {
        'Con filtro humidificador': { nivel: 'normal', texto: 'Ventila espontáneo por la cánula: vigila secreciones y permeabilidad.' },
        'En plan de decanulación':  { nivel: 'normal', texto: 'Registra tolerancia a la oclusión y manejo de secreciones.' },
      },
      prose: { 'En VMI': 'en VMI', 'Con filtro humidificador': 'ventilando con filtro humidificador', 'En plan de decanulación': 'en plan de decanulación' } },
    { k: 'traqueoDia', grupo: 'soporte', label: 'Día de traqueostomía', tipo: 'num', unidad: 'día', dependeDe: { campo: 'viaArtificial', en: ['TQT'] }, proseFmt: 'día {v} de traqueostomía' },
    { k: 'traqueoTipo', grupo: 'soporte', label: 'Tipo de traqueostomía', tipo: 'select', dependeDe: { campo: 'viaArtificial', en: ['TQT'] }, opciones: ['Percutánea', 'Quirúrgica'],
      prose: { 'Percutánea': 'percutánea', 'Quirúrgica': 'quirúrgica' } },
    { k: 'traqueoCanula', grupo: 'soporte', label: 'Cánula de traqueostomía', tipo: 'select', dependeDe: { campo: 'viaArtificial', en: ['TQT'] }, opciones: ['Con balón', 'Sin balón', 'Fenestrada', 'Metálica'],
      prose: { 'Con balón': 'cánula con balón', 'Sin balón': 'cánula sin balón', 'Fenestrada': 'cánula fenestrada', 'Metálica': 'cánula metálica' } },
    // TOT: número, fijación, cuff y posición corroborada (el detalle que hoy
    // solo cabía en nota libre).
    { k: 'totTubo', grupo: 'soporte', label: 'Nº de tubo', tipo: 'num', dependeDe: { campo: 'viaArtificial', en: ['TOT'] }, proseFmt: 'número {v}' },
    { k: 'totFijacion', grupo: 'soporte', label: 'Fijación', tipo: 'num', unidad: 'cm', dependeDe: { campo: 'viaArtificial', en: ['TOT'] },
      pista: 'A cuántos centímetros queda fijo en la comisura labial.', proseFmt: 'fijo a {v} cm en comisura' },
    { k: 'totCuff', grupo: 'soporte', label: 'Cuff', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'viaArtificial', en: ['TOT'] },
      pista: 'La presión del balón que sella la tráquea. Adecuada entre 20 y 30.',
      bandas: [
      { hasta: 19.9, texto: 'Cuff bajo (menos de 20): riesgo de fuga y microaspiración', nivel: 'leve' },
      { hasta: 30, texto: 'Cuff adecuado (20 a 30)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Cuff alto (sobre 30): riesgo de isquemia traqueal', nivel: 'moderado' },
    ] },
    { k: 'totRx', grupo: 'soporte', label: 'Posición corroborada', tipo: 'select', compactar: true, dependeDe: { campo: 'viaArtificial', en: ['TOT'] },
      opciones: ['Corroborada con radiografía', 'Radiografía pendiente'],
      notas: { 'Radiografía pendiente': { nivel: 'info', texto: 'Gestiona la radiografía de tórax para confirmar la posición del tubo.' } },
      prose: { 'Corroborada con radiografía': 'posición corroborada con radiografía', 'Radiografía pendiente': 'radiografía de posición pendiente' } },
    { k: 'totCarina', grupo: 'soporte', label: 'Distancia a carina', tipo: 'num', unidad: 'cm', dependeDe: { campo: 'totRx', en: ['Corroborada con radiografía'] },
      pista: 'La punta del tubo debe quedar 3 a 5 cm sobre la carina.', proseFmt: 'a {v} cm de la carina',
      bandas: [
        { hasta: 2.9, texto: 'Muy cerca de la carina (menos de 3 cm): riesgo de monobronquial, avisa', nivel: 'moderado' },
        { hasta: 5, texto: 'En posición (3 a 5 cm)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Alto (sobre 5 cm): vigila riesgo de extubación accidental', nivel: 'leve' },
      ] },
    // ── VMI: aplica con TOT, o con TQT que está "En VMI" (dependencia OR) ──
    // Modo centralizado en los tres que se usan de verdad; otros modos se
    // consultan por texto sin agrandar el catálogo.
    { k: 'vmiModo', grupo: 'soporte', label: 'Modo VMI', tipo: 'select', rama: true,
      dependeDe: [{ campo: 'viaArtificial', en: ['TOT'] }, { campo: 'tqtSoporte', en: ['En VMI'] }],
      opciones: ['Volumen control', 'Presión control', 'Espontáneo', 'Otro modo'],
      notas: {
        'Volumen control': { nivel: 'info', texto: 'El equipo asegura el volumen (Vt) y la presión depende del pulmón.' },
        'Presión control': { nivel: 'info', texto: 'El equipo asegura la presión y el volumen depende del pulmón.' },
        'Espontáneo':      { nivel: 'info', texto: 'El paciente gatilla y el equipo apoya con presión de soporte (camino al weaning).' },
      },
      // Prosa de los modos legacy (VCV/PCV/SIMV/PSV/APRV): drafts viejos
      // siguen imprimiendo su modo aunque ya no sea opción del catálogo.
      prose: {
        'Volumen control': 'modo volumen control', 'Presión control': 'modo presión control', 'Espontáneo': 'en espontáneo', 'Otro modo': 'en otro modo ventilatorio',
        'VCV': 'modo VCV', 'PCV': 'modo PCV', 'SIMV': 'modo SIMV', 'PSV': 'modo PSV', 'APRV': 'modo APRV',
      } },
    { k: 'vmiModoOtro', grupo: 'soporte', label: '¿Cuál modo?', tipo: 'text', proseFmt: 'modo {v}', dependeDe: { campo: 'vmiModo', en: ['Otro modo'] }, placeholder: 'Ej: APRV, SIMV, VAFO' },
    // Parámetros comunes a todo modo.
    { k: 'vmiPeep', grupo: 'soporte', label: 'PEEP', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vmiModo' },
      pista: 'Presión positiva al final de la espiración: mantiene los alvéolos abiertos.' },
    // PS solo tiene sentido donde el paciente gatilla (presión control lo usa
    // como nivel de presión programado; se pide igual, como dictó el fundador).
    // En pantalla "Presión de soporte" con todas sus letras (así lo nombra la
    // unidad, 2026-09-29); en el documento sigue "PS 12 cmH₂O", como se escribe.
    { k: 'vmiPs', grupo: 'soporte', label: 'Presión de soporte', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vmiModo', en: ['Presión control', 'Espontáneo', 'Otro modo'] },
      pista: 'Presión de soporte sobre el PEEP: el empuje de cada inspiración.', proseFmt: 'PS {v} cmH₂O' },
    { k: 'vmiPmax', grupo: 'soporte', label: 'Pmax', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vmiModo' },
      pista: 'Presión máxima (peak) que alcanza la vía aérea en cada ciclo.',
      bandas: [
        { hasta: 30, texto: 'En rango (30 o menos)', nivel: 'normal' },
        { hasta: 35, texto: 'Alta (31 a 35): vigila la tendencia', nivel: 'leve' },
        { hasta: Infinity, texto: 'Muy alta (sobre 35): riesgo de barotrauma, avisa', nivel: 'moderado' },
      ] },
    { k: 'vmiPmedia', grupo: 'soporte', label: 'Pmedia', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vmiModo' },
      pista: 'Presión media de la vía aérea en todo el ciclo: se relaciona con la oxigenación.' },
    { k: 'vmiFio2', grupo: 'soporte', label: 'FiO₂', tipo: 'num', unidad: '%', dependeDe: { campo: 'vmiModo' } },
    { k: 'vmiVt', grupo: 'soporte', label: 'Vt', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'vmiModo' },
      pista: 'Volumen corriente: el aire de cada inspiración. Protector entre 6 y 8 ml por kilo de peso ideal.' },
    { k: 'vmiVtKg', grupo: 'soporte', label: 'Vt/kg', tipo: 'num', unidad: 'ml/kg', dependeDe: { campo: 'vmiModo' },
      pista: '¿El Vt está en rango protector? Divide el volumen por el peso ideal del paciente.',
      bandas: [
      { hasta: 5.9, texto: 'Bajo (menos de 6): vigilar volumen minuto', nivel: 'leve' },
      { hasta: 8, texto: 'Protector (6 a 8 ml/kg)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Alto (sobre 8): riesgo de volutrauma', nivel: 'moderado' },
    ] },
    { k: 'vmiVm', grupo: 'soporte', label: 'Volumen minuto', tipo: 'num', unidad: 'L/min', dependeDe: { campo: 'vmiModo' },
      pista: 'Todo el aire movilizado en un minuto (Vt por FR). Habitual entre 5 y 10.' },
    { k: 'vmiFr', grupo: 'soporte', label: 'FR', tipo: 'num', unidad: 'rpm', dependeDe: { campo: 'vmiModo' } },
    // Mecánica opcional (volumen control, donde la presión cuenta la historia).
    { k: 'vmiCompliance', grupo: 'soporte', label: 'Compliance', tipo: 'num', unidad: 'ml/cmH₂O', dependeDe: { campo: 'vmiModo', en: ['Volumen control'] },
      pista: 'Distensibilidad del pulmón: cuánto volumen entra por cada cmH₂O. Más baja significa pulmón más rígido.' },
    { k: 'vmiDriving', grupo: 'soporte', label: 'Driving pressure', tipo: 'num', unidad: 'cmH₂O', dependeDe: { campo: 'vmiModo', en: ['Volumen control'] },
      pista: 'Presión de distensión (plateau menos PEEP): el estrés que recibe el pulmón en cada ciclo.',
      bandas: [
        { hasta: 14, texto: 'Protectora (menos de 15)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Alta (15 o más): ajustar Vt o PEEP, avisa', nivel: 'moderado' },
      ] },
    // El objetivo de la VMI: hacia dónde va esta ventilación.
    { k: 'vmiObjetivo', grupo: 'soporte', label: 'Objetivo de la VMI', tipo: 'select', compactar: true, dependeDe: { campo: 'vmiModo' },
      opciones: ['Ventilación protectora', 'Plan de weaning', 'Soporte total'],
      prose: { 'Ventilación protectora': 'con objetivo de ventilación protectora', 'Plan de weaning': 'en plan de weaning', 'Soporte total': 'en soporte ventilatorio total' } },
    { k: 'ventCambios', grupo: 'soporte', label: 'Cambios en el turno', tipo: 'text',
      dependeDe: [{ campo: 'soporte', en: ['Oxigenoterapia', 'CNAF', 'VNI'] }, { campo: 'vmiModo' }],
      placeholder: 'Ej: se baja PEEP 10→8; desescala FiO₂ 50→40%' },
    // Prono: coexiste con VMI (típico), pero se registra aparte del soporte
    // porque la tolerancia (cutánea/ocular) es un dato clínico propio.
    // El prono se pregunta cuando hay soporte que lo justifique (vía aérea
    // artificial, CNAF o VNI): al paciente en aire ambiente u oxígeno simple no
    // se le pregunta si está en prono (2026-09-29, "solo si lo amerita").
    { k: 'pronoEstado', grupo: 'mecanica', label: 'Prono', tipo: 'select', compactar: true, opciones: ['No en prono', 'En prono', 'Recién supinado'],
      dependeDe: [{ campo: 'viaAerea', en: ['Vía aérea artificial'] }, { campo: 'soporte', en: ['CNAF', 'VNI'] }],
      // "No en prono" ya se lee autocontenida; las otras dos son ambiguas
      // solas ("En prono" ¿en qué?) — con la frase completa quedan claras.
      prose: { 'En prono': 'en posición prono', 'Recién supinado': 'recién supinado tras prono' } },
    // proseFmt: "horas en prono 16 h" repetía la hora en label y unidad.
    { k: 'pronoHoras', grupo: 'mecanica', label: 'Horas en prono', tipo: 'num', unidad: 'h', dependeDe: { campo: 'pronoEstado', en: ['En prono'] }, proseFmt: 'completando {v} h de prono' },
    { k: 'pronoTolerancia', grupo: 'mecanica', label: 'Tolerancia al prono', tipo: 'select', compactar: true, dependeDe: { campo: 'pronoEstado', en: ['En prono'] }, opciones: ['Buena tolerancia al prono', 'Desaturación en prono', 'Complicación cutánea u ocular'],
      notas: {
        'Desaturación en prono':            { nivel: 'moderado', texto: 'Evaluar reposicionamiento o retorno a supino, avisar.' },
        'Complicación cutánea u ocular':    { nivel: 'moderado', texto: 'Revisar puntos de apoyo y protección ocular, avisar.' },
      },
      // "Complicación cutánea u ocular" no menciona el prono — el resto ya lo
      // dice ("...al prono"/"...en prono") y queda autocontenido.
      prose: { 'Complicación cutánea u ocular': 'complicación cutánea u ocular por el prono' } },
    // Acople del paciente con la VMI (bien acoplado es dato de todo modo).
    { k: 'vmiAcople', grupo: 'soporte', label: 'Acople', tipo: 'select', compactar: true, dependeDe: { campo: 'vmiModo' }, opciones: ['Adaptado', 'Asincronía', 'Lucha con el ventilador'],
      notas: { 'Asincronía': { nivel: 'moderado', texto: 'Revisa sedación y parámetros.' }, 'Lucha con el ventilador': { nivel: 'moderado', texto: 'Evalúa sedación y dolor, avisa.' } } },
    // Patrón y oxigenación
    // Mecánica respiratoria y disnea (el examen del patrón, en palabras del
    // fundador: "regular, irregular").
    { k: 'patron', grupo: 'mecanica', label: 'Mecánica respiratoria', tipo: 'select', compactar: true, opciones: ['Eupneico', 'Respiración irregular', 'Taquipneico', 'Polipnea', 'Bradipneico', 'Disnea', 'Uso de musculatura accesoria'],
      notas: {
        'Eupneico':     { nivel: 'normal',   texto: 'Mecánica regular, FR normal (12 a 20 rpm).' },
        'Respiración irregular': { nivel: 'moderado', texto: 'Patrón irregular: descríbelo y correlaciona con lo neurológico.' },
        'Taquipneico':  { nivel: 'leve',     texto: 'FR elevada: vigilar.' },
        'Polipnea':     { nivel: 'leve',     texto: 'Rápida y superficial: vigilar fatiga.' },
        'Bradipneico':  { nivel: 'moderado', texto: 'FR baja: vigilar depresión respiratoria.' },
        'Disnea':       { nivel: 'moderado', texto: 'Dificultad respiratoria: O₂ según indicación, avisa.' },
        'Uso de musculatura accesoria': { nivel: 'moderado', texto: 'Trabajo respiratorio aumentado: avisa.' },
      },
      prose: { 'Respiración irregular': 'con respiración irregular', 'Disnea': 'con disnea' } },
    { k: 'spo2', grupo: 'mecanica', label: 'SpO₂', tipo: 'num', unidad: '%', bandas: [
      { hasta: 89, texto: 'Hipoxemia (<90)', nivel: 'severo' },
      { hasta: 94, texto: 'Hipoxemia leve (90 a 94)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Normal (≥95)', nivel: 'normal' },
    ] },
    // Meta de saturación: contra qué se titula el O₂ (en el retenedor de CO₂
    // la meta indicada suele ser 88 a 92%, no "lo más alto posible").
    { k: 'satMeta', grupo: 'mecanica', label: 'Meta de saturación', tipo: 'num', unidad: '%',
      pista: 'La meta indicada para este paciente. En retenedores de CO₂ suele ser 88 a 92%.', proseFmt: 'meta de saturación {v}%' },
    { k: 'auscultacion', grupo: 'mecanica', label: 'Auscultación', tipo: 'text', placeholder: 'Ej: MP+ simétrico, crépitos bibasales, sibilancias' },
    // KTR (kinesiterapia respiratoria) del turno — grupo 'soporte' aunque se
    // registra junto a secreciones, porque es una intervención sobre el soporte.
    { k: 'ktr', grupo: 'mecanica', label: 'KTR', tipo: 'select', compactar: true, opciones: ['KTR no realizada', 'KTR realizada, buena tolerancia', 'KTR realizada, mal tolerada'],
      notas: { 'KTR realizada, mal tolerada': { nivel: 'leve', texto: 'Fraccionar sesiones; coordinar con kinesiología.' } },
      // La coma interna de la opción inyectaba una cláusula huérfana en la
      // oración del grupo ('…, KTR realizada, mal tolerada, …') — prose la
      // reemplaza por la forma sin coma, autocontenida.
      prose: { 'KTR realizada, buena tolerancia': 'KTR realizada con buena tolerancia', 'KTR realizada, mal tolerada': 'KTR realizada y mal tolerada' } },
    // Secreciones
    { k: 'secrecionCantidad', grupo: 'secreciones', label: 'Cantidad', tipo: 'select', compactar: true, opciones: ['Sin secreciones', 'Escasas', 'Moderadas', 'Abundantes'],
      // "Escasas"/"Moderadas"/"Abundantes" solas no dicen de qué; "Sin
      // secreciones" ya se lee autocontenida (queda con el default compactar).
      prose: { 'Escasas': 'secreciones escasas', 'Moderadas': 'secreciones moderadas', 'Abundantes': 'secreciones abundantes' } },
    { k: 'secreciones', grupo: 'secreciones', label: 'Aspecto', tipo: 'select', dependeDe: { campo: 'secrecionCantidad', en: ['Escasas', 'Moderadas', 'Abundantes'] }, opciones: ['Claras', 'Blanquecinas', 'Purulentas', 'Hemoptoicas'],
      // Sin `prose` salía "aspecto claras" (label masculino + adjetivo
      // femenino, discordancia) — con la frase completa concuerda y se
      // encadena tras "secreciones escasas".
      prose: {
        'Claras':       'de aspecto claro',
        'Blanquecinas': 'de aspecto blanquecino',
        'Purulentas':   'de aspecto purulento',
        'Hemoptoicas':  'de aspecto hemoptoico',
      },
      notas: {
        'Claras':       { nivel: 'normal',   texto: 'Aspecto esperado.' },
        'Blanquecinas': { nivel: 'leve',     texto: 'Mucosas: vigilar.' },
        'Purulentas':   { nivel: 'moderado', texto: 'Amarillas / verdes: sugiere infección.' },
        'Hemoptoicas':  { nivel: 'moderado', texto: 'Con sangre: avisar, descartar sangrado.' },
      } },
    { k: 'manejoSecreciones', grupo: 'secreciones', label: 'Manejo', tipo: 'select', compactar: true, dependeDe: { campo: 'secrecionCantidad', en: ['Escasas', 'Moderadas', 'Abundantes'] }, opciones: ['Expectora', 'No logra expectorar', 'Requiere aspiración'],
      notas: { 'Requiere aspiración': { nivel: 'leve', texto: 'Aspiración según necesidad, técnica estéril.' } } },
    // Gases arteriales: detrás de su puerta, igual que el laboratorio de hemato
    // (2026-09-29, "solo si lo amerita"): al paciente sin gases del día no se
    // le muestran cuatro casillas vacías. En la vía aérea artificial los gases
    // son de rutina, así que ahí van directo, sin la pregunta.
    { k: 'gasesDia', grupo: 'gases', label: 'Gases arteriales', tipo: 'select', compactar: true, rama: true,
      dependeDe: { campo: 'viaAerea', noEn: ['Vía aérea artificial'] },
      opciones: ['Sin gases del día', 'Con gases del día'],
      prose: { 'Sin gases del día': 'sin gases arteriales del día', 'Con gases del día': 'con gases arteriales del día' } },
    { k: 'ph', grupo: 'gases', label: 'pH', tipo: 'num', dependeDe: GASES_A_LA_VISTA, bandas: [
      { hasta: 7.34, texto: 'Acidemia (<7,35)', nivel: 'moderado' },
      { hasta: 7.45, texto: 'Normal (7,35 a 7,45)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Alcalemia (>7,45)', nivel: 'moderado' },
    ] },
    { k: 'pco2', grupo: 'gases', label: 'pCO₂', tipo: 'num', dependeDe: GASES_A_LA_VISTA, unidad: 'mmHg', bandas: [
      { hasta: 34.9, texto: 'Hipocapnia (<35)', nivel: 'leve' },
      { hasta: 45, texto: 'Normal (35 a 45)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Hipercapnia (>45)', nivel: 'leve' },
    ] },
    { k: 'hco3', grupo: 'gases', label: 'HCO₃', tipo: 'num', dependeDe: GASES_A_LA_VISTA, unidad: 'mmol/L', bandas: [
      { hasta: 21.9, texto: 'Bajo (<22): acidosis metab.?', nivel: 'leve' },
      { hasta: 26, texto: 'Normal (22 a 26)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Alto (>26): alcalosis metab.?', nivel: 'leve' },
    ] },
    { k: 'eb', grupo: 'gases', label: 'EB', tipo: 'num', dependeDe: GASES_A_LA_VISTA, unidad: 'mmol/L', bandas: [
      { hasta: -2, texto: 'Déficit de base (≤−2)', nivel: 'leve' },
      { hasta: 2, texto: 'Normal (−2 a +2)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Exceso de base (>+2)', nivel: 'leve' },
    ] },
  ],
  gastro:      [
    // Régimen y nutrición — el tipo define qué detalle aplica.
    // Revisado con el pedido de las nutricionistas (dictado del fundador,
    // 2026-09-29, "las nutris se sienten ofendidas"): consistencias en su
    // orden de progresión y con el líquido, características del régimen,
    // ingesta, modo de administración de la enteral, residuo solo cuando la
    // tolerancia lo pide, y la parenteral con su nombre completo, su vía y sin
    // síntomas digestivos que no le aplican.
    // "Parenteral" a secas era el valor antiguo: se sigue leyendo en todo lo
    // que depende de él (drafts de un turno en curso), jamás se migra.
    { k: 'regimen', grupo: 'regimen', label: 'Régimen', tipo: 'select', compactar: true, opciones: ['Régimen cero', 'Vía oral', 'Nutrición enteral', 'Nutrición parenteral'],
      notas: {
        'Régimen cero':         { nivel: 'leve', texto: 'Verifica indicación y aporte/hidratación EV.' },
        'Nutrición enteral':    { nivel: 'info', texto: 'Registra fórmula, modo de administración y aporte; vigila la tolerancia.' },
        'Nutrición parenteral': { nivel: 'info', texto: 'Vía dedicada, vigila glicemia y sitio de infusión.' },
        'Parenteral':           { nivel: 'info', texto: 'Vía dedicada, vigila glicemia y sitio de infusión.' },
      },
      prose: { 'Parenteral': 'nutrición parenteral' } },
    // Consistencia en el orden en que se progresa (hídrico → común).
    { k: 'regimenOral', grupo: 'regimen', label: 'Consistencia', tipo: 'select', dependeDe: { campo: 'regimen', en: ['Vía oral'] }, opciones: ['Hídrico', 'Líquido', 'Papilla', 'Blando', 'Liviano', 'Común'],
      // Sin `prose` salía "tipo de régimen liviano" — redundante junto a "vía
      // oral" (regimen). Con `prose` queda "régimen liviano", autocontenido.
      prose: {
        'Hídrico': 'régimen hídrico', 'Líquido': 'régimen líquido', 'Papilla': 'régimen papilla',
        'Blando': 'régimen blando', 'Liviano': 'régimen liviano', 'Común': 'régimen común',
      } },
    // Características del régimen (las que indica nutrición además de la
    // consistencia). Catálogo en BORRADOR para revisar con las nutricionistas.
    { k: 'regimenCaracteristicas', grupo: 'regimen', label: 'Características', tipo: 'multi', dependeDe: { campo: 'regimen', en: ['Vía oral'] },
      opciones: ['Hiposódico', 'Diabético', 'Hipograso', 'Hiperproteico', 'Hipoproteico', 'Bajo en potasio', 'Sin lactosa', 'Sin gluten', 'Sin residuos'],
      proseFmt: '{v}' },
    // Ingesta: cuánto come de lo que se le sirve (dictado del fundador,
    // 2026-09-29: "no tenemos ingesta, así como buena ingesta o mala"). Los
    // cortes por porcentaje del plato son didáctica, no se preguntan.
    { k: 'ingesta', grupo: 'regimen', label: 'Ingesta', tipo: 'select', compactar: true, dependeDe: { campo: 'regimen', en: ['Vía oral'] },
      opciones: ['Buena ingesta', 'Ingesta regular', 'Mala ingesta', 'Rechazo alimentario'],
      notas: {
        'Buena ingesta':       { nivel: 'normal',   texto: 'Consume la mayor parte de lo servido (sobre 75%).' },
        'Ingesta regular':     { nivel: 'leve',     texto: 'Consume alrededor de la mitad (50 a 75%): vigila si se repite.' },
        'Mala ingesta':        { nivel: 'moderado', texto: 'Consume menos de la mitad de lo servido: avisa a nutrición.' },
        'Rechazo alimentario': { nivel: 'moderado', texto: 'No recibe la alimentación: avisa a nutrición y al equipo.' },
      },
      prose: {
        'Buena ingesta': 'con buena ingesta', 'Ingesta regular': 'con ingesta regular',
        'Mala ingesta': 'con mala ingesta', 'Rechazo alimentario': 'con rechazo alimentario',
      } },
    { k: 'neVia', grupo: 'regimen', label: 'Vía enteral', tipo: 'select', dependeDe: { campo: 'regimen', en: ['Nutrición enteral'] }, opciones: ['SNG', 'SOG', 'SNY', 'Gastrostomía', 'Yeyunostomía'],
      prose: { 'SNG': 'por SNG', 'SOG': 'por SOG', 'SNY': 'por SNY', 'Gastrostomía': 'por gastrostomía', 'Yeyunostomía': 'por yeyunostomía' } },
    // SNG de descompresión: distinta de la SNG de alimentación (`neVia`) — no
    // depende del régimen, puede coexistir con cualquiera (o con régimen cero).
    { k: 'sngDescompresion', grupo: 'regimen', label: 'SNG de descompresión', tipo: 'select', compactar: true, opciones: ['Sin SNG de descompresión', 'SNG a caída libre', 'SNG clampeada'] },
    { k: 'sngDebito', grupo: 'regimen', label: 'Débito SNG', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'sngDescompresion', en: ['SNG a caída libre'] } },
    { k: 'sngAspecto', grupo: 'regimen', label: 'Aspecto del débito', tipo: 'select', compactar: true, dependeDe: { campo: 'sngDescompresion', en: ['SNG a caída libre'] }, opciones: ['Bilioso', 'Gástrico claro', 'Hemático', 'Fecaloídeo'],
      notas: {
        'Hemático':    { nivel: 'moderado', texto: 'Cuantificar y avisar.' },
        'Fecaloídeo':  { nivel: 'severo',   texto: 'Sugiere obstrucción intestinal: avisar de inmediato.' },
      },
      // Ninguna opción dice "débito" por sí sola — ambiguas sueltas.
      prose: {
        'Bilioso':       'débito bilioso',
        'Gástrico claro':'débito gástrico claro',
        'Hemático':      'débito hemático',
        'Fecaloídeo':    'débito fecaloídeo',
      } },
    { k: 'neFormula', grupo: 'regimen', label: 'Fórmula', tipo: 'text', dependeDe: { campo: 'regimen', en: ['Nutrición enteral'] }, placeholder: 'Ej: polimérica hiperproteica, peptídica, específica para diabético' },
    // Cómo se administra: el flujo solo tiene sentido en la continua o
    // cíclica; la intermitente se registra por bolo.
    { k: 'neModo', grupo: 'regimen', label: 'Administración', tipo: 'select', compactar: true, dependeDe: { campo: 'regimen', en: ['Nutrición enteral'] },
      opciones: ['Continua', 'Intermitente', 'Cíclica'],
      prose: { 'Continua': 'en infusión continua', 'Intermitente': 'intermitente en bolos', 'Cíclica': 'en infusión cíclica' } },
    // Sin modo respondido el flujo sigue a la vista (así se registraba antes).
    { k: 'neFlujo', grupo: 'regimen', label: 'Flujo', tipo: 'num', unidad: 'ml/h', dependeDe: { campo: 'neModo', noEn: ['Intermitente'] } },
    { k: 'neVolumenBolo', grupo: 'regimen', label: 'Volumen por bolo', tipo: 'num', unidad: 'ml', proseFmt: '{v} ml por bolo', dependeDe: { campo: 'neModo', en: ['Intermitente'] } },
    { k: 'neFrecuencia', grupo: 'regimen', label: 'Frecuencia', tipo: 'select', compactar: true, dependeDe: { campo: 'neModo', en: ['Intermitente'] },
      opciones: ['Cada 3 horas', 'Cada 4 horas', 'Cada 6 horas'],
      prose: { 'Cada 3 horas': 'cada 3 horas', 'Cada 4 horas': 'cada 4 horas', 'Cada 6 horas': 'cada 6 horas' } },
    // "Dosis trófica" y "Progresando" eran los valores antiguos: se siguen
    // imprimiendo con la frase nueva.
    { k: 'neMeta', grupo: 'regimen', label: 'Aporte', tipo: 'select', compactar: true, dependeDe: { campo: 'regimen', en: ['Nutrición enteral'] }, opciones: ['Trófica', 'En progresión', 'En meta'],
      prose: {
        'Trófica': 'NE trófica', 'En progresión': 'NE en progresión hacia la meta', 'En meta': 'NE en meta',
        'Dosis trófica': 'NE trófica', 'Progresando': 'NE en progresión hacia la meta',
      } },
    { k: 'npVia', grupo: 'regimen', label: 'Vía', tipo: 'select', compactar: true, dependeDe: { campo: 'regimen', en: ['Nutrición parenteral', 'Parenteral'] },
      opciones: ['Central', 'Periférica'],
      notas: { 'Periférica': { nivel: 'leve', texto: 'La vía periférica tolera menor osmolaridad: vigila flebitis en el sitio.' } },
      prose: { 'Central': 'por vía central', 'Periférica': 'por vía periférica' } },
    { k: 'npSolucion', grupo: 'regimen', label: 'Solución', tipo: 'text', dependeDe: { campo: 'regimen', en: ['Nutrición parenteral', 'Parenteral'] }, placeholder: 'Ej: bolsa 3 en 1 estándar, fórmula magistral' },
    { k: 'npFlujo', grupo: 'regimen', label: 'Flujo', tipo: 'num', unidad: 'ml/h', dependeDe: { campo: 'regimen', en: ['Nutrición parenteral', 'Parenteral'] } },
    // Tolerancia al régimen (dictado del fundador, 2026-07-16): bien o mal, y
    // si mal, los síntomas de catálogo + el manejo específico. Aplica a la
    // alimentación por el tubo digestivo (oral o enteral): los síntomas son
    // digestivos y a la parenteral no le aplican (2026-09-29).
    { k: 'tolerancia', grupo: 'regimen', label: 'Tolerancia', tipo: 'select', compactar: true, rama: true,
      dependeDe: { campo: 'regimen', en: ['Vía oral', 'Nutrición enteral'] },
      opciones: ['Bien tolerado', 'Mal tolerado'],
      notas: {
        'Bien tolerado': { nivel: 'normal',   texto: 'Sin síntomas digestivos con la alimentación.' },
        'Mal tolerado':  { nivel: 'moderado', texto: 'Marca los síntomas y registra el manejo: ese es el dato que la entrega necesita.' },
      } },
    { k: 'toleranciaSintomas', grupo: 'regimen', label: 'Síntomas', tipo: 'multi', dependeDe: { campo: 'tolerancia', en: ['Mal tolerado'] },
      opciones: ['Náuseas', 'Vómitos', 'Distensión', 'Dolor abdominal', 'Residuo elevado', 'Diarrea'],
      proseFmt: 'con {v}' },
    // El residuo gástrico ya no se pide de rutina (las guías actuales no
    // recomiendan medirlo en todo paciente con enteral): aparece cuando la
    // tolerancia marca "Residuo elevado", para cuantificarlo.
    { k: 'residuo', grupo: 'regimen', label: 'Residuo', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'toleranciaSintomas', en: ['Residuo elevado'] }, bandas: [
      { hasta: 250, texto: 'Tolera (≤250)', nivel: 'normal' },
      { hasta: 500, texto: 'Vigilar (251 a 500)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Intolerancia (>500): evaluar NE', nivel: 'moderado' },
    ] },
    { k: 'toleranciaManejo', grupo: 'regimen', label: 'Manejo', tipo: 'text', proseFmt: 'en manejo con {v}', dependeDe: { campo: 'tolerancia', en: ['Mal tolerado'] },
      placeholder: 'Ej: antiemético según indicación, se fracciona régimen, se baja flujo de NE' },
    { k: 'protector', grupo: 'regimen', label: 'Protector gástrico', tipo: 'select', compactar: true, opciones: ['Sin protector', 'Omeprazol', 'Esomeprazol', 'Pantoprazol', 'Ranitidina'],
      // El fármaco solo entre comas no dice su rol ("…, omeprazol, …") y "sin
      // protector" no dice de qué — con la frase completa quedan autocontenidos.
      prose: {
        'Sin protector': 'sin protector gástrico',
        'Omeprazol':     'protección gástrica con omeprazol',
        'Esomeprazol':   'protección gástrica con esomeprazol',
        'Pantoprazol':   'protección gástrica con pantoprazol',
        'Ranitidina':    'protección gástrica con ranitidina',
      } },
    // Metabolismo / glicemia.
    { k: 'hgt', grupo: 'glicemia', label: 'HGT', tipo: 'num', unidad: 'mg/dL', bandas: [
      { hasta: 53, texto: 'Hipoglicemia severa (<54)', nivel: 'severo' },
      { hasta: 69, texto: 'Hipoglicemia (<70)', nivel: 'moderado' },
      { hasta: 140, texto: 'Normal (70 a 140)', nivel: 'normal' },
      { hasta: 180, texto: 'Elevada (141 a 180)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Hiperglicemia (>180)', nivel: 'moderado' },
    ] },
    // El seriado dice CADA CUÁNTO (dictado del fundador, 2026-07-16): la
    // frecuencia es el dato que ordena el turno (a qué hora toca el próximo).
    { k: 'hgtControl', grupo: 'glicemia', label: 'Control HGT', tipo: 'select', compactar: true,
      opciones: ['Sin control de HGT', 'HGT por turno', 'HGT cada 6 horas', 'HGT cada 4 horas', 'HGT pre comidas'],
      notas: {
        'HGT cada 4 horas': { nivel: 'info', texto: 'El seriado estricto es típico del paciente neurocrítico o con BIC de insulina.' },
        'HGT pre comidas':  { nivel: 'info', texto: 'Coordina el control con el horario de alimentación.' },
      },
      // Todas las opciones nuevas ya dicen HGT; los valores legados de drafts
      // viejos ("Sin control", "HGT seriado") conservan su prosa.
      prose: { 'Sin control': 'sin control de HGT' } },
    { k: 'glicemiaObjetivo', grupo: 'glicemia', label: 'Objetivo de glicemia', tipo: 'text',
      // Visible si hay control declarado O insulina en curso (cualquiera de
      // los dos justifica una meta) — dependencia OR con las opciones
      // POSITIVAS explícitas ("Sin control de HGT" no pide meta). El valor
      // legado "HGT seriado" también abre la meta.
      dependeDe: [
        { campo: 'hgtControl', en: ['HGT por turno', 'HGT cada 6 horas', 'HGT cada 4 horas', 'HGT pre comidas', 'HGT seriado'] },
        { campo: 'insulina', en: ['Cristalina SOS', 'Cristalina en BIC', 'Esquema SC', 'Basal / bolo'] },
      ],
      placeholder: 'Ej: 140 a 180 mg/dL' },
    { k: 'insulina', grupo: 'glicemia', label: 'Insulina', tipo: 'select', compactar: true, opciones: ['Sin insulina', 'Cristalina SOS', 'Cristalina en BIC', 'Esquema SC', 'Basal / bolo'],
      notas: { 'Cristalina en BIC': { nivel: 'moderado', texto: 'BIC insulina: HGT seriado y vigilar hipoglicemia.' } },
      // "Cristalina SOS"/"Esquema SC" sueltas no dicen que hablan de insulina;
      // "Sin insulina" ya se lee autocontenida (queda con el default compactar).
      prose: {
        'Cristalina SOS':    'insulina cristalina SOS',
        'Cristalina en BIC': 'insulina cristalina en BIC',
        'Esquema SC':        'insulina en esquema SC',
        'Basal / bolo':      'insulina basal / bolo',
      } },
    // Cuál insulina y cómo (dictado del fundador): el esquema/basal necesita
    // nombre y dosis para que la entrega sirva ("basal + esquema" sin la
    // glargina nocturna es media historia).
    { k: 'insulinaCual', grupo: 'glicemia', label: '¿Con cuál?', tipo: 'text', proseFmt: 'con {v}',
      dependeDe: { campo: 'insulina', en: ['Esquema SC', 'Basal / bolo'] },
      placeholder: 'Ej: cristalina según esquema; glargina 20 U nocturna' },
    // Tránsito y abdomen.
    { k: 'deposiciones', grupo: 'digestivo', label: 'Deposiciones', tipo: 'select', compactar: true, opciones: ['Presentes', 'Ausentes'],
      notas: { 'Ausentes': { nivel: 'leve', texto: 'Registrar días sin tránsito; evaluar manejo.' } },
      prose: { 'Presentes': 'deposiciones presentes', 'Ausentes': 'sin deposiciones' } },
    { k: 'depositosDias', grupo: 'digestivo', label: 'Días sin tránsito', tipo: 'num', unidad: 'días', proseFmt: '{v} días sin tránsito', dependeDe: { campo: 'deposiciones', en: ['Ausentes'] } },
    // Deposiciones negativas piden su manejo (dictado del fundador): el dato
    // útil no es solo la ausencia, sino qué se está haciendo al respecto.
    { k: 'depoManejo', grupo: 'digestivo', label: 'Manejo', tipo: 'text', proseFmt: 'en manejo con {v}', dependeDe: { campo: 'deposiciones', en: ['Ausentes'] },
      placeholder: 'Ej: lactulosa según indicación, se vigila tránsito' },
    { k: 'bristol', grupo: 'digestivo', label: 'Bristol', tipo: 'cards', dependeDe: { campo: 'deposiciones', en: ['Presentes'] },
      opciones: [
        { v: 'Tipo 1', emoji: '🪨', desc: 'Trozos duros separados' },
        { v: 'Tipo 2', emoji: '🌰', desc: 'Salchicha grumosa' },
        { v: 'Tipo 3', emoji: '🌭', desc: 'Salchicha con grietas' },
        { v: 'Tipo 4', emoji: '🐍', desc: 'Lisa y blanda: ideal' },
        { v: 'Tipo 5', emoji: '🫐', desc: 'Trozos blandos con bordes' },
        { v: 'Tipo 6', emoji: '💧', desc: 'Blandas esponjosas' },
        { v: 'Tipo 7', emoji: '🌊', desc: 'Completamente líquidas' },
      ],
      notas: {
        'Tipo 1': { nivel: 'moderado', texto: 'Estreñimiento severo.' },
        'Tipo 2': { nivel: 'leve',     texto: 'Estreñimiento leve.' },
        'Tipo 3': { nivel: 'normal',   texto: 'Normal: tendencia firme.' },
        'Tipo 4': { nivel: 'normal',   texto: 'Normal: ideal.' },
        'Tipo 5': { nivel: 'leve',     texto: 'Tendencia a diarrea.' },
        'Tipo 6': { nivel: 'moderado', texto: 'Diarrea leve.' },
        'Tipo 7': { nivel: 'severo',   texto: 'Diarrea severa: evaluar C. difficile, tolerancia NE y fármacos diarreogénicos.' },
      },
      // Feedback de uso real: en la PROSA de la evolución, el número de escala
      // ("Bristol Tipo 6") no dice nada clínico por sí solo — lo que se lee y
      // se usa es la CARACTERÍSTICA de la deposición. El tickeo (cards, arriba)
      // sí conserva "Tipo N" + emoji + descripción corta: eso no cambia.
      prose: {
        'Tipo 1': 'deposiciones duras, en trozos separados',
        'Tipo 2': 'deposiciones duras, grumosas',
        'Tipo 3': 'deposiciones formadas, de superficie agrietada',
        'Tipo 4': 'deposiciones formadas, blandas',
        'Tipo 5': 'deposiciones semiformadas, en trozos blandos',
        'Tipo 6': 'deposiciones disgregadas, pastosas',
        'Tipo 7': 'deposiciones líquidas',
      } },
    { k: 'abdomen', grupo: 'digestivo', label: 'Abdomen', tipo: 'select', opciones: ['Blando, depresible', 'Distendido', 'Doloroso', 'Timpanizado'],
      notas: {
        'Blando, depresible': { nivel: 'normal',   texto: 'Examen esperado.' },
        'Distendido':         { nivel: 'leve',     texto: 'Vigilar tránsito y tolerancia.' },
        'Doloroso':           { nivel: 'moderado', texto: 'Avisar: caracterizar dolor.' },
        'Timpanizado':        { nivel: 'leve',     texto: 'Meteorismo: vigilar.' },
      } },
    // RHA al examen (dictado del fundador): el abdomen se ausculta, no solo se
    // palpa — "abdomen blando, depresible, RHA presentes" es la línea real.
    { k: 'rha', grupo: 'digestivo', label: 'Ruidos hidroaéreos', tipo: 'select', compactar: true,
      opciones: ['RHA presentes', 'RHA aumentados', 'RHA disminuidos', 'RHA ausentes'],
      notas: {
        'RHA presentes':   { nivel: 'normal',   texto: 'Auscultación esperada.' },
        'RHA aumentados':  { nivel: 'leve',     texto: 'Correlaciona con tránsito acelerado o diarrea.' },
        'RHA disminuidos': { nivel: 'leve',     texto: 'Correlaciona con tránsito y tolerancia.' },
        'RHA ausentes':    { nivel: 'moderado', texto: 'Silencio abdominal: correlaciona con el tránsito, avisa.' },
      } },
    // Ostomías: la isquemia del estoma es una urgencia — se vigila aparte del
    // abdomen general.
    { k: 'ostomia', grupo: 'digestivo', label: 'Ostomía', tipo: 'select', compactar: true, opciones: ['Sin ostomía', 'Colostomía', 'Ileostomía', 'Urostomía'] },
    { k: 'ostomiaAspecto', grupo: 'digestivo', label: 'Aspecto del estoma', tipo: 'select', compactar: true, dependeDe: { campo: 'ostomia', en: ['Colostomía', 'Ileostomía', 'Urostomía'] }, opciones: ['Estoma rosado y vital', 'Estoma pálido', 'Estoma edematoso', 'Estoma oscuro / isquémico'],
      notas: { 'Estoma oscuro / isquémico': { nivel: 'severo', texto: 'Isquemia del estoma: avisar de inmediato.' } } },
    { k: 'ostomiaDebito', grupo: 'digestivo', label: 'Débito de ostomía', tipo: 'select', compactar: true, dependeDe: { campo: 'ostomia', en: ['Colostomía', 'Ileostomía', 'Urostomía'] }, opciones: ['Débito escaso', 'Débito moderado', 'Débito abundante', 'Débito líquido abundante'] },
  ],
  // Renal rediseñado por ramas (dictado del fundador, 2026-07-16, su área:
  // diálisis): diuresis cualitativa primero (el ritmo calculado queda como
  // escala opcional), vía con el catálogo real, función renal como rama con
  // su porqué, y la TRR como rama propia con la profundidad que la terapia
  // merece (intermitente vs continua, catéter lineal/invertido y la
  // anticoagulación del circuito SIEMPRE preguntada). Peso y tendencia de
  // peso se retiraron (el balance queda como opcional; drafts viejos
  // degradan en silencio).
  renal:       [
    // Diuresis: lo cualitativo primero; el ritmo exacto es la escala opcional.
    { k: 'diuresisEstado', grupo: 'diuresis', label: 'Diuresis', tipo: 'select', compactar: true,
      opciones: ['Diuresis adecuada', 'Oliguria', 'Poliuria', 'Anuria'],
      notas: {
        'Diuresis adecuada': { nivel: 'normal',   texto: 'Débito conservado (0,5 a 3 ml/kg/h).' },
        'Oliguria':          { nivel: 'moderado', texto: 'Bajo 0,5 ml/kg/h: objetívala con la escala de ritmo diurético, avisa.' },
        'Poliuria':          { nivel: 'leve',     texto: 'Sobre 3 ml/kg/h: vigila volemia y electrolitos.' },
        'Anuria':            { nivel: 'severo',   texto: 'Sin diuresis: avisa, evalúa función renal y obstrucción.' },
      } },
    { k: 'diuresisVia', grupo: 'diuresis', label: 'Vía', tipo: 'select', compactar: true,
      opciones: ['Catéter urinario permanente', 'Espontánea (chata, pato o pañal)', 'Sondeo intermitente', 'Urostomía'],
      // Todas ambiguas sueltas ("Sondeo intermitente" ¿de qué?); los valores
      // legados de drafts viejos (CUP / Foley, Chata / pato) conservan su prosa.
      prose: {
        'Catéter urinario permanente':      'diuresis por catéter urinario permanente',
        'Espontánea (chata, pato o pañal)': 'diuresis espontánea',
        'Sondeo intermitente':              'diuresis por sondeo intermitente',
        'Urostomía':                        'diuresis por urostomía',
        'CUP / Foley':  'diuresis por CUP / Foley',
        'Espontánea':   'diuresis espontánea',
        'Chata / pato': 'diuresis en chata / pato',
      } },
    // El total y el ritmo (ml/kg/h) se calculan en la escala "Ritmo diurético".
    { k: 'balance', grupo: 'diuresis', label: 'Balance', tipo: 'num', unidad: 'ml' },
    // En anuria no hay orina que describir (2026-09-29).
    { k: 'orina', grupo: 'diuresis', label: 'Orina', tipo: 'select', dependeDe: { campo: 'diuresisEstado', noEn: ['Anuria'] }, opciones: ['Clara', 'Amarilla', 'Concentrada', 'Coluria', 'Hematúrica', 'Piúrica'],
      notas: {
        'Clara':       { nivel: 'normal',   texto: 'Diluida: buena hidratación o poliuria.' },
        'Amarilla':    { nivel: 'normal',   texto: 'Aspecto normal.' },
        'Concentrada': { nivel: 'leve',     texto: 'Oscura: sugiere hipovolemia / deshidratación.' },
        'Coluria':     { nivel: 'moderado', texto: 'Color té: bilirrubina (colestasis).' },
        'Hematúrica':  { nivel: 'moderado', texto: 'Con sangre: descartar trauma / ITU / sonda.' },
        'Piúrica':     { nivel: 'moderado', texto: 'Turbia / con pus: sugiere infección.' },
      } },
    // Función renal como rama: ¿estable? y si no, el PORQUÉ (el patrón de
    // laboratorio orienta la causa). Valores legados (Estable, Creatinina al
    // alza, AKI / oliguria, En diálisis) conservan su prosa en drafts viejos.
    { k: 'funcionRenal', grupo: 'funcionRenal', label: 'Función renal', tipo: 'select', compactar: true, rama: true,
      opciones: ['Función renal estable', 'Deterioro de función renal'],
      notas: {
        'Función renal estable':      { nivel: 'normal',   texto: 'Mantener vigilancia de diuresis y laboratorio.' },
        'Deterioro de función renal': { nivel: 'moderado', texto: 'Registra el patrón: creatinina al alza o BUN y creatinina disociados.' },
      },
      prose: { 'Estable': 'función renal estable', 'Deterioro de función renal': 'con deterioro de función renal' } },
    { k: 'frPatron', grupo: 'funcionRenal', label: '¿Por qué?', tipo: 'select', compactar: true,
      dependeDe: { campo: 'funcionRenal', en: ['Deterioro de función renal'] },
      opciones: ['Creatinina al alza', 'BUN y creatinina disociados'],
      notas: {
        'Creatinina al alza':          { nivel: 'moderado', texto: 'Vigila diuresis, balance y nefrotóxicos; revisa ajuste de fármacos.' },
        'BUN y creatinina disociados': { nivel: 'moderado', texto: 'BUN sube más que la creatinina: piensa en causa prerrenal o sangrado digestivo.' },
      },
      prose: { 'BUN y creatinina disociados': 'con BUN y creatinina disociados' } },
    // Estado hidroelectrolítico: permite declarar explícitamente el negativo
    // clínicamente relevante, o desplegar las alteraciones. La sigla HLE se
    // retiró del texto (2026-07-11: confundía al leer el documento); drafts
    // con el valor viejo dejan de imprimir esta línea (vida útil ≤48 h).
    { k: 'hle', grupo: 'funcionRenal', label: 'Estado hidroelectrolítico', tipo: 'select', compactar: true, opciones: ['Sin trastornos hidroelectrolíticos', 'Con trastornos hidroelectrolíticos'],
      notas: {
        'Sin trastornos hidroelectrolíticos': { nivel: 'normal',   texto: 'Electrolitos en rango: mantener control.' },
        'Con trastornos hidroelectrolíticos': { nivel: 'moderado', texto: 'Detalla la alteración y la corrección administrada.' },
      } },
    { k: 'hidroelectrolitico', grupo: 'funcionRenal', label: 'Alteración hidroelectrolítica', tipo: 'multi', dependeDe: { campo: 'hle', en: ['Con trastornos hidroelectrolíticos'] }, opciones: ['Hiperkalemia', 'Hipokalemia', 'Hiponatremia', 'Hipernatremia', 'Hipocalcemia', 'Hipomagnesemia', 'Acidosis metabólica'],
      nota: { nivel: 'moderado', texto: 'Registra corrección administrada y control posterior.' } },
    { k: 'heCorreccion', grupo: 'funcionRenal', label: 'Corrección administrada', tipo: 'text', dependeDe: { campo: 'hle', en: ['Con trastornos hidroelectrolíticos'] }, placeholder: 'Ej: gluconato de calcio, insulina + SG, bicarbonato' },
    // ── Terapia de reemplazo renal (rama propia; reemplaza a la cadena vieja
    // funcionRenal "En diálisis" → dialisis → sesión) ──
    { k: 'trr', grupo: 'trr', label: 'Terapia de reemplazo renal', tipo: 'select', compactar: true, rama: true,
      opciones: ['Sin terapia de reemplazo renal', 'Hemodiálisis intermitente', 'Terapia continua (TRRC)', 'Peritoneodiálisis'],
      prose: {
        'Hemodiálisis intermitente': 'en hemodiálisis intermitente',
        'Terapia continua (TRRC)':   'en TRR continua',
        'Peritoneodiálisis':         'en peritoneodiálisis',
      } },
    // Hemodiálisis intermitente: la sesión del turno con sus números.
    { k: 'hdSesionHoy', grupo: 'trr', label: 'Sesión de HD', tipo: 'select', compactar: true, dependeDe: { campo: 'trr', en: ['Hemodiálisis intermitente'] }, opciones: ['Sin sesión hoy', 'Sesión realizada hoy'],
      // "Sin sesión hoy" suelta no dice sesión de qué.
      prose: { 'Sin sesión hoy': 'sin sesión de HD hoy', 'Sesión realizada hoy': 'sesión de HD realizada hoy' } },
    { k: 'hdiQb', grupo: 'trr', label: 'Qb', tipo: 'num', unidad: 'ml/min', dependeDe: { campo: 'hdSesionHoy', en: ['Sesión realizada hoy'] },
      pista: 'Flujo de sangre por el circuito.', proseFmt: 'Qb {v} ml/min' },
    // Qt y BH final con la unidad fija, como Qb y UF (2026-09-29, "dejar fijo
    // los apellidos"). Un valor viejo escrito como texto ("4 horas") se sigue
    // imprimiendo tal cual (ver camposProse, tipo num).
    { k: 'hdiQt', grupo: 'trr', label: 'Qt (tiempo de terapia)', tipo: 'num', unidad: 'h', dependeDe: { campo: 'hdSesionHoy', en: ['Sesión realizada hoy'] },
      pista: 'Cuánto duró la sesión.', proseFmt: 'Qt {v} horas' },
    { k: 'hdiUfObjetivo', grupo: 'trr', label: 'UF objetivo', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'hdSesionHoy', en: ['Sesión realizada hoy'] },
      pista: 'Cuánto volumen se busca retirar en la sesión.', proseFmt: 'UF objetivo {v} ml' },
    { k: 'hdiBhFinal', grupo: 'trr', label: 'BH final', tipo: 'num', unidad: 'ml', dependeDe: { campo: 'hdSesionHoy', en: ['Sesión realizada hoy'] },
      pista: 'El balance hídrico con que cerró la sesión (negativo si se retiró volumen).', proseFmt: 'BH final {v} ml' },
    { k: 'hdTolerancia', grupo: 'trr', label: 'Tolerancia a la HD', tipo: 'select', compactar: true, dependeDe: { campo: 'hdSesionHoy', en: ['Sesión realizada hoy'] }, opciones: ['Bien tolerada', 'Hipotensión durante la sesión', 'Calambres durante la sesión', 'Sesión suspendida'],
      notas: {
        'Hipotensión durante la sesión': { nivel: 'moderado', texto: 'Vigila perfusión y volemia post-sesión.' },
        'Sesión suspendida':             { nivel: 'moderado', texto: 'Registra el motivo y coordina la reprogramación.' },
      },
      // Todas ambiguas solas ("Bien tolerada" ¿qué cosa?); con "HD" delante
      // quedan autocontenidas.
      prose: {
        'Bien tolerada':                  'HD bien tolerada',
        'Hipotensión durante la sesión':   'HD con hipotensión durante la sesión',
        'Calambres durante la sesión':     'HD con calambres durante la sesión',
        'Sesión suspendida':               'HD suspendida',
      } },
    // Terapia continua: la modalidad dice el mecanismo (didáctica del área).
    { k: 'trrcModalidad', grupo: 'trr', label: 'Modalidad', tipo: 'select', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      opciones: ['HDVVC', 'HFVVC', 'HDFVVC', 'Ultrafiltración aislada'],
      notas: {
        'HDVVC':                  { nivel: 'info', texto: 'Hemodiálisis veno-venosa continua: depura por difusión (dializado a contracorriente).' },
        'HFVVC':                  { nivel: 'info', texto: 'Hemofiltración veno-venosa continua: depura por convección (con reposición).' },
        'HDFVVC':                 { nivel: 'info', texto: 'Hemodiafiltración veno-venosa continua: difusión y convección juntas (dializado + reposición).' },
        'Ultrafiltración aislada':{ nivel: 'info', texto: 'Solo retira volumen, sin depuración.' },
      },
      prose: { 'HDVVC': 'en HDVVC', 'HFVVC': 'en HFVVC', 'HDFVVC': 'en HDFVVC', 'Ultrafiltración aislada': 'en ultrafiltración aislada' } },
    { k: 'trrcFiltroExtra', grupo: 'trr', label: 'Filtro adicional', tipo: 'text', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      placeholder: 'Ej: CytoSorb, oXiris (removedor de citoquinas)' },
    { k: 'trrcQb', grupo: 'trr', label: 'Qb', tipo: 'num', unidad: 'ml/min', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      pista: 'Flujo de sangre por el circuito.', proseFmt: 'Qb {v} ml/min' },
    { k: 'trrcQd', grupo: 'trr', label: 'Qd', tipo: 'num', unidad: 'ml/h', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      pista: 'Flujo de dializado (la parte difusiva).', proseFmt: 'Qd {v} ml/h' },
    { k: 'trrcQs', grupo: 'trr', label: 'Qs (reposición)', tipo: 'num', unidad: 'ml/h', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      pista: 'Flujo de solución de sustitución (la parte convectiva).', proseFmt: 'Qs {v} ml/h' },
    { k: 'trrcTasaUf', grupo: 'trr', label: 'Tasa de UF', tipo: 'num', unidad: 'ml/h', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      pista: 'Cuánto volumen se retira al paciente por hora.', proseFmt: 'tasa de UF {v} ml/h' },
    { k: 'trrcQt', grupo: 'trr', label: 'Qt (tiempo efectivo)', tipo: 'text', dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      placeholder: 'Ej: 22 de 24 horas', pista: 'Horas efectivas de terapia en el día: las detenciones (cambio de set, traslados) descuentan.' },
    { k: 'trrcTolerancia', grupo: 'trr', label: 'Tolerancia', tipo: 'select', compactar: true, dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] },
      opciones: ['Bien tolerada', 'Hipotensión con la terapia', 'Mal tolerada'],
      notas: { 'Hipotensión con la terapia': { nivel: 'moderado', texto: 'Evalúa la tasa de UF con el equipo tratante, avisa.' } },
      // Mismo molde que la tolerancia de la HD; el valor legado "Buena
      // tolerancia a la terapia" conserva su prosa vía compactar.
      prose: { 'Bien tolerada': 'TRRC bien tolerada', 'Mal tolerada': 'TRRC mal tolerada' } },
    { k: 'crrtFiltro', grupo: 'trr', label: 'Filtro', tipo: 'select', compactar: true, dependeDe: { campo: 'trr', en: ['Terapia continua (TRRC)'] }, opciones: ['Filtro permeable', 'Filtro coagulando', 'Filtro cambiado en el turno'],
      notas: { 'Filtro coagulando': { nivel: 'moderado', texto: 'Evalúa anticoagulación y flujo; prepara cambio de set.' } } },
    // Común a toda TRR extracorpórea: el catéter y la anticoagulación del
    // circuito (¡SIEMPRE se pregunta si va con heparina!).
    { k: 'trrAcceso', grupo: 'trr', label: 'Catéter de diálisis', tipo: 'select', compactar: true,
      dependeDe: { campo: 'trr', en: ['Hemodiálisis intermitente', 'Terapia continua (TRRC)'] },
      opciones: ['Funcionando lineal', 'Funcionando invertido'],
      notas: { 'Funcionando invertido': { nivel: 'leve', texto: 'Ramas invertidas: más recirculación y menor rendimiento. Registra el motivo.' } },
      prose: { 'Funcionando lineal': 'catéter de diálisis funcionando lineal', 'Funcionando invertido': 'catéter de diálisis funcionando invertido' } },
    { k: 'trrAnticoag', grupo: 'trr', label: 'Anticoagulación del circuito', tipo: 'select', compactar: true,
      dependeDe: { campo: 'trr', en: ['Hemodiálisis intermitente', 'Terapia continua (TRRC)'] },
      opciones: ['Circuito sin anticoagulación', 'Circuito con heparina', 'Anticoagulación regional con citrato'],
      notas: {
        'Circuito con heparina':               { nivel: 'info', texto: 'Vigila sangrados y controla según protocolo de tu unidad.' },
        'Anticoagulación regional con citrato':{ nivel: 'info', texto: 'Controla calcio iónico seriado (riesgo de hipocalcemia por citrato).' },
      } },
  ],
  // Invasivos ya no tiene campos globales: CVC lúmenes/reflujo/push-stop/infunde
  // y débito/aspecto de drenaje vivían sueltos, desconectados de CUÁL acceso
  // describían cuando había más de uno. Todo ese detalle ahora vive por
  // dispositivo (ver DISPOSITIVO_LUMENES/ASPECTO y lineaDispositivo en
  // evolucionSistema.js) — un draft viejo con estas claves se ignora sin romper
  // (camposVisibles/compilarCampos devuelven [] / null para este sistema).
  invasivos:   [],
  infecto:     [
    { k: 'estadoFebril', grupo: 'fiebre', label: 'Estado febril', tipo: 'select', compactar: true, opciones: ['Afebril', 'Febrícula', 'Peaks febriles', 'Fiebre persistente'],
      notas: {
        'Afebril':            { nivel: 'normal',   texto: 'Sin fiebre.' },
        'Febrícula':          { nivel: 'leve',     texto: '37,5 a 38 °C: vigilar.' },
        'Peaks febriles':     { nivel: 'moderado', texto: 'Cultivos según indicación; registra patrón.' },
        'Fiebre persistente': { nivel: 'moderado', texto: 'Avisar; reevaluar foco y cobertura.' },
      } },
    // Hemocultivos y urocultivo salieron del manejo (2026-09-29): se pedían dos
    // veces (aquí y en "Cultivos tomados") y se leían "en manejo con
    // hemocultivos". Un valor viejo se sigue imprimiendo.
    { k: 'manejoFiebre', grupo: 'fiebre', label: 'Manejo', tipo: 'multi', proseFmt: 'en manejo con {v}', dependeDe: { campo: 'estadoFebril', en: ['Febrícula', 'Peaks febriles', 'Fiebre persistente'] }, opciones: ['Antipiréticos', 'Medios físicos'] },
    // Cultivos tomados en el turno y su seguimiento (deja rastro estructurado
    // de qué se tomó, no solo el chip suelto "Cultivos pendientes/positivos").
    { k: 'cultivoSitio', grupo: 'fiebre', label: 'Cultivos tomados', tipo: 'multi',
      opciones: ['Hemocultivo', 'Urocultivo', 'Cultivo de secreción respiratoria', 'Cultivo de arrastre', 'Punta de catéter', 'Cultivo de herida', 'Cultivo de secreción (otra)', 'LCR'],
      nota: { nivel: 'info', texto: 'Registra fecha de toma y controla resultados.' } },
    // Detalle y fecha (dictado del fundador): "de secreción: especificar cuál"
    // y "fecha y si está el informe".
    { k: 'cultivoDetalle', grupo: 'fiebre', label: 'Detalle', tipo: 'text', proseFmt: '{v}', dependeDe: { campo: 'cultivoSitio' },
      placeholder: 'Ej: secreción de herida operatoria, arrastre nasal' },
    { k: 'cultivoFecha', grupo: 'fiebre', label: 'Fecha de toma', tipo: 'text', dependeDe: { campo: 'cultivoSitio' },
      placeholder: 'Ej: tomados el 14-07', proseFmt: 'tomados el {v}' },
    { k: 'cultivoEstado', grupo: 'fiebre', label: '¿Está el informe?', tipo: 'select', compactar: true, dependeDe: { campo: 'cultivoSitio' }, opciones: ['Cultivos pendientes de resultado', 'Cultivos con resultado disponible'] },
    { k: 'cultivoResultado', grupo: 'fiebre', label: 'Resultado', tipo: 'text', dependeDe: { campo: 'cultivoEstado', en: ['Cultivos con resultado disponible'] }, placeholder: 'Ej: E. coli BLEE, sensible a meropenem' },
    // El foco se pregunta cuando hay una infección que ubicar: fiebre,
    // antibiótico o cultivos (2026-09-29, "solo si lo amerita").
    { k: 'foco', grupo: 'fiebre', label: 'Foco', tipo: 'select',
      dependeDe: [
        { campo: 'estadoFebril', en: ['Febrícula', 'Peaks febriles', 'Fiebre persistente'] },
        { campo: 'atbEsquema' }, { campo: 'cultivoSitio' },
      ],
      opciones: ['Sin foco claro', 'Respiratorio', 'Urinario', 'Abdominal', 'Asociado a catéter', 'Piel / partes blandas'],
      // Sin `prose`, el label (no es compactar) duplicaba la palabra:
      // "foco sin foco claro"; y "foco piel / partes blandas" perdía la
      // preposición. El resto ("foco respiratorio"…) queda con el default.
      prose: { 'Sin foco claro': 'sin foco claro', 'Piel / partes blandas': 'foco en piel / partes blandas' } },
    { k: 'parametros', grupo: 'fiebre', label: 'Parámetros inflamatorios', tipo: 'select', compactar: true, opciones: ['En descenso', 'Estables', 'En ascenso'],
      notas: { 'En ascenso': { nivel: 'moderado', texto: 'Reevaluar foco y cobertura, avisar.' } },
      // Las tres opciones son ambiguas solas ("Estables" ¿de qué?) — con
      // "parámetros inflamatorios" delante quedan autocontenidas.
      prose: {
        'En descenso': 'parámetros inflamatorios en descenso',
        'Estables':    'parámetros inflamatorios estables',
        'En ascenso':  'parámetros inflamatorios en ascenso',
      } },
    // Los VALORES de los parámetros (dictado del fundador, 2026-07-16): PCR,
    // procalcitonina, láctico y GB con sus cortes didácticos. La tendencia de
    // arriba sigue siendo la lectura rápida; estos son el número exacto.
    { k: 'pcr', grupo: 'fiebre', label: 'PCR', tipo: 'num', unidad: 'mg/L',
      pista: 'Proteína C reactiva: marcador general de inflamación, sube y baja lento (24 a 48 h).',
      bandas: [
        { hasta: 5, texto: 'En rango (hasta 5)', nivel: 'normal' },
        { hasta: 100, texto: 'Elevada: correlaciona con la clínica y la tendencia', nivel: 'leve' },
        { hasta: Infinity, texto: 'Muy elevada (sobre 100): inflamación o infección importante', nivel: 'moderado' },
      ] },
    { k: 'procalcitonina', grupo: 'fiebre', label: 'Procalcitonina', tipo: 'num', unidad: 'ng/mL',
      pista: 'Más específica de infección bacteriana que la PCR; también orienta cuándo suspender el ATB.',
      bandas: [
        { hasta: 0.49, texto: 'En rango (bajo 0,5)', nivel: 'normal' },
        { hasta: 2, texto: 'Elevada (0,5 a 2): posible infección bacteriana', nivel: 'leve' },
        { hasta: Infinity, texto: 'Muy elevada (sobre 2): sugiere infección bacteriana sistémica', nivel: 'moderado' },
      ] },
    { k: 'lactico', grupo: 'fiebre', label: 'Láctico', tipo: 'num', unidad: 'mmol/L',
      pista: 'En contexto infeccioso, un láctico que sube es bandera de sepsis: perfusión antes que fiebre.',
      bandas: [
        { hasta: 1.9, texto: 'En rango (bajo 2)', nivel: 'normal' },
        { hasta: 4, texto: 'Elevado (2 a 4): vigila perfusión y tendencia', nivel: 'leve' },
        { hasta: Infinity, texto: 'Muy elevado (sobre 4): hipoperfusión, avisa', nivel: 'severo' },
      ] },
    { k: 'gb', grupo: 'fiebre', label: 'Glóbulos blancos', tipo: 'num', unidad: '×10³/µL',
      bandas: [
        { hasta: 3.9, texto: 'Leucopenia (bajo 4): también es signo de infección grave', nivel: 'moderado' },
        { hasta: 11, texto: 'En rango (4 a 11)', nivel: 'normal' },
        { hasta: Infinity, texto: 'Leucocitosis (sobre 11)', nivel: 'leve' },
      ] },
    // Aislamiento: antes era un chip suelto en "Invasivos" (sin decir el tipo);
    // como campo de Infectología queda con más detalle y en su aparato
    // correcto. Notas pedagógicas por tipo (dictado del fundador: "explicar
    // si necesita"); la guía completa con secuencia de EPP vive en la
    // Enciclopedia (sección aislamiento).
    { k: 'aislamiento', grupo: 'fiebre', label: 'Aislamiento', tipo: 'select', compactar: true, opciones: ['Sin aislamiento', 'Aislamiento de contacto', 'Aislamiento de gotitas', 'Aislamiento aéreo', 'Aislamiento protector'],
      notas: {
        'Aislamiento de contacto': { nivel: 'info', texto: 'Multirresistentes, C. difficile, sarna: guantes y pechera; lo que toca al paciente se queda con el paciente.' },
        'Aislamiento de gotitas':  { nivel: 'info', texto: 'Influenza, meningococo: mascarilla quirúrgica a menos de 1 metro.' },
        'Aislamiento aéreo':       { nivel: 'info', texto: 'TBC, varicela, sarampión: pieza con presión negativa y mascarilla N95.' },
        'Aislamiento protector':   { nivel: 'info', texto: 'Protege al paciente (neutropenia): la barrera es para lo que TÚ le puedes llevar.' },
      } },
    { k: 'atbEsquema', grupo: 'antibiotico', label: 'Esquema antibiótico', tipo: 'text', placeholder: 'Ej: piperacilina-tazobactam',
      pista: 'La ficha completa del fármaco (dosis, dilución, RAM) está en Medicamentos, en la Enciclopedia.' },
    // ¿Empírico o dirigido? (dictado del fundador: "cual y dirigir, tb").
    { k: 'atbTipo', grupo: 'antibiotico', label: 'Cobertura', tipo: 'select', compactar: true, dependeDe: { campo: 'atbEsquema' },
      opciones: ['ATB empírico', 'ATB dirigido por cultivo'],
      notas: {
        'ATB empírico':            { nivel: 'info',   texto: 'Reevalúa con los cultivos: desescalar a cobertura dirigida es parte del manejo.' },
        'ATB dirigido por cultivo':{ nivel: 'normal', texto: 'Cobertura ajustada al microorganismo identificado.' },
      } },
    // proseFmt: sin ella salía "día de tratamiento 5 día" (label y unidad
    // repiten "día").
    { k: 'atbDia', grupo: 'antibiotico', label: 'Día de tratamiento', tipo: 'num', unidad: 'día', proseFmt: 'día {v} de tratamiento' },
    // Plan de duración (dictado del fundador): el día sin el plan es media
    // historia ("día 5... ¿de cuántos?").
    { k: 'atbPlan', grupo: 'antibiotico', label: 'Plan de duración', tipo: 'text', dependeDe: { campo: 'atbEsquema' },
      placeholder: 'Ej: 7 días de cobertura, se reevalúa con cultivos' },
    { k: 'atbVia', grupo: 'antibiotico', label: 'Vía ATB', tipo: 'select', compactar: true, dependeDe: { campo: 'atbEsquema' }, opciones: ['ATB endovenoso', 'ATB oral', 'ATB inhalado'] },
  ],
  // Hematología por ramas (dictado del fundador, 2026-07-16): tromboprofilaxis
  // con su porqué cuando falta y el tipo de compresión cuando es mecánica,
  // sangrado y anemia declarados, laboratorio desplegable (hb, plaquetas, GB y
  // RAN para el oncológico) y los fármacos de base hemato-endocrinos
  // centralizados aquí. Los chips de hallazgos se retiraron: todo se describe
  // estructurado.
  hemato:      [
    { k: 'tromboprofilaxis', grupo: 'tromboprofilaxis', label: 'Tromboprofilaxis', tipo: 'select', compactar: true, rama: true, opciones: ['Sin tromboprofilaxis', 'Farmacológica', 'Mecánica', 'Farmacológica + mecánica'],
      // "Farmacológica"/"Mecánica" solas no dicen de qué; "Sin tromboprofilaxis"
      // ya se lee autocontenida (queda con el default compactar).
      prose: {
        'Farmacológica': 'tromboprofilaxis farmacológica',
        'Mecánica': 'tromboprofilaxis mecánica',
        'Farmacológica + mecánica': 'tromboprofilaxis farmacológica y mecánica',
      } },
    // Sin tromboprofilaxis se DOCUMENTA el porqué (dictado del fundador): la
    // omisión sin motivo es lo que Calidad pregunta.
    { k: 'tromboSinMotivo', grupo: 'tromboprofilaxis', label: '¿Por qué?', tipo: 'text', dependeDe: { campo: 'tromboprofilaxis', en: ['Sin tromboprofilaxis'] },
      placeholder: 'Ej: sangrado activo, plaquetopenia, postoperatorio inmediato', proseFmt: 'sin tromboprofilaxis por {v}' },
    { k: 'tromboFarmaco', grupo: 'tromboprofilaxis', label: 'Fármaco', tipo: 'select', dependeDe: { campo: 'tromboprofilaxis', en: ['Farmacológica', 'Farmacológica + mecánica'] }, opciones: ['HBPM (enoxaparina)', 'HNF', 'Fondaparinux'],
      // "fármaco HBPM (enoxaparina)" era jerga de formulario — tras
      // "tromboprofilaxis farmacológica" la preposición encadena natural.
      prose: { 'HBPM (enoxaparina)': 'con HBPM (enoxaparina)', 'HNF': 'con HNF', 'Fondaparinux': 'con fondaparinux' } },
    { k: 'tromboMecanicaTipo', grupo: 'tromboprofilaxis', label: 'Tipo de compresión', tipo: 'select', compactar: true, dependeDe: { campo: 'tromboprofilaxis', en: ['Mecánica', 'Farmacológica + mecánica'] },
      opciones: ['Compresión neumática intermitente', 'Medias antitrombóticas'],
      prose: { 'Compresión neumática intermitente': 'con compresión neumática intermitente', 'Medias antitrombóticas': 'con medias antitrombóticas' } },
    // Anticoagulación terapéutica: distinta de la tromboprofilaxis de arriba
    // (dosis de tratamiento, no de prevención).
    { k: 'anticoagulado', grupo: 'tromboprofilaxis', label: 'Anticoagulación', tipo: 'select', compactar: true, opciones: ['Sin anticoagulación terapéutica', 'Con anticoagulación terapéutica'] },
    { k: 'anticoagulanteFarmaco', grupo: 'tromboprofilaxis', label: 'Fármaco anticoagulante', tipo: 'select', dependeDe: { campo: 'anticoagulado', en: ['Con anticoagulación terapéutica'] }, opciones: ['Warfarina', 'HNF en BIC', 'HBPM dosis terapéutica', 'NACO'],
      // "fármaco anticoagulante warfarina" era jerga de formulario.
      prose: { 'Warfarina': 'con warfarina', 'HNF en BIC': 'con HNF en BIC', 'HBPM dosis terapéutica': 'con HBPM en dosis terapéutica', 'NACO': 'con NACO' } },
    { k: 'controlCoagulacion', grupo: 'tromboprofilaxis', label: 'Control de coagulación', tipo: 'text', dependeDe: { campo: 'anticoagulado', en: ['Con anticoagulación terapéutica'] }, placeholder: 'Ej: INR 2,3 · TTPA 65 s' },
    // Laboratorio desplegable (dictado del fundador: "más detalle, dos puntos
    // y se despliega todo eso"): hb, plaquetas, GB y RAN para el oncológico.
    { k: 'labHemato', grupo: 'labhem', label: 'Laboratorio', tipo: 'select', compactar: true, rama: true,
      opciones: ['Sin laboratorio nuevo', 'Con laboratorio del día'],
      prose: { 'Sin laboratorio nuevo': 'sin laboratorio nuevo', 'Con laboratorio del día': 'con laboratorio del día' } },
    { k: 'hb', grupo: 'labhem', label: 'Hb', tipo: 'num', unidad: 'g/dL', dependeDe: { campo: 'labHemato', en: ['Con laboratorio del día'] }, bandas: [
      { hasta: 6.9, texto: 'Anemia severa (<7): umbral transfusional', nivel: 'severo' },
      { hasta: 9.9, texto: 'Anemia moderada (7 a 10)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Sobre umbral transfusional (≥10)', nivel: 'normal' },
    ] },
    { k: 'plaquetas', grupo: 'labhem', label: 'Plaquetas', tipo: 'num', unidad: '×10³/µL', dependeDe: { campo: 'labHemato', en: ['Con laboratorio del día'] }, bandas: [
      { hasta: 19, texto: 'Plaquetopenia severa (<20): riesgo de sangrado espontáneo', nivel: 'severo' },
      { hasta: 49, texto: 'Plaquetopenia (<50): precaución en procedimientos', nivel: 'moderado' },
      { hasta: 149, texto: 'Bajo lo normal (<150)', nivel: 'leve' },
      { hasta: Infinity, texto: 'Normal (≥150)', nivel: 'normal' },
    ] },
    { k: 'gb', grupo: 'labhem', label: 'Glóbulos blancos', tipo: 'num', unidad: '×10³/µL', dependeDe: { campo: 'labHemato', en: ['Con laboratorio del día'] }, bandas: [
      { hasta: 3.9, texto: 'Leucopenia (bajo 4): revisa el RAN', nivel: 'moderado' },
      { hasta: 11, texto: 'En rango (4 a 11)', nivel: 'normal' },
      { hasta: Infinity, texto: 'Leucocitosis (sobre 11)', nivel: 'leve' },
    ] },
    { k: 'ran', grupo: 'labhem', label: 'RAN', tipo: 'num', unidad: '×10³/µL', dependeDe: { campo: 'labHemato', en: ['Con laboratorio del día'] },
      pista: 'Recuento absoluto de neutrófilos: la defensa real del paciente oncológico. Bajo 0,5 (500/µL) es neutropenia severa.',
      bandas: [
        { hasta: 0.49, texto: 'Neutropenia severa (bajo 500/µL): aislamiento protector, avisa', nivel: 'severo' },
        { hasta: 0.99, texto: 'Neutropenia moderada (500 a 1.000/µL)', nivel: 'moderado' },
        { hasta: 1.49, texto: 'Neutropenia leve (1.000 a 1.500/µL)', nivel: 'leve' },
        { hasta: Infinity, texto: 'En rango (1.500/µL o más)', nivel: 'normal' },
      ] },
    // Sangrado y anemia declarados (dictado del fundador: "¿con sangrado
    // activo? ¿anémico? ¿requiere transfusión?"). El negativo usa las mismas
    // palabras que la basal ("Sin signos de sangrado"); el sitio es catálogo
    // cerrado y la cuantificación va aparte.
    { k: 'sangrado', grupo: 'transfusion', label: 'Sangrado', tipo: 'select', compactar: true,
      opciones: ['Sin signos de sangrado', 'Con sangrado activo'],
      notas: { 'Con sangrado activo': { nivel: 'severo', texto: 'Cuantifica, avisa y vigila hemodinamia.' } } },
    { k: 'sangradoSitio', grupo: 'transfusion', label: 'Sitio de sangrado', tipo: 'select', compactar: true, dependeDe: { campo: 'sangrado', en: ['Con sangrado activo'] }, opciones: ['Digestivo', 'Herida quirúrgica', 'Por drenaje', 'Sitio de punción', 'Epistaxis', 'Otro sitio'],
      // Ningún sitio dice "sangrado" por sí solo — con el prefijo quedan
      // autocontenidos.
      prose: {
        'Digestivo':          'sangrado digestivo',
        'Herida quirúrgica':  'sangrado de herida quirúrgica',
        'Por drenaje':        'sangrado por drenaje',
        'Sitio de punción':   'sangrado en sitio de punción',
        'Epistaxis':          'sangrado por epistaxis',
        'Otro sitio':         'sangrado en otro sitio',
      } },
    { k: 'sangradoDetalle', grupo: 'transfusion', label: '¿Cuánto?', tipo: 'text', dependeDe: { campo: 'sangrado', en: ['Con sangrado activo'] },
      proseFmt: 'con {v}', placeholder: 'Ej: gasa con escaso débito hemático, 50 ml en el turno' },
    { k: 'anemia', grupo: 'transfusion', label: 'Anemia', tipo: 'select', compactar: true,
      opciones: ['Sin anemia clínica', 'Con anemia clínica'],
      notas: { 'Con anemia clínica': { nivel: 'leve', texto: 'Correlaciona con la Hb y el umbral transfusional; registra si requiere transfusión.' } } },
    { k: 'transfusion', grupo: 'transfusion', label: '¿Transfusión?', tipo: 'select', compactar: true, opciones: ['No', 'Sí'],
      // "No"/"Sí" solas entre comas no dicen de qué — con sustantivo se leen
      // como evolución real (termina el bloque hemato, no en ", no.").
      prose: { 'No': 'sin transfusiones en el turno', 'Sí': 'con transfusión en el turno' } },
    { k: 'transfusionQue', grupo: 'transfusion', label: 'Hemocomponente', tipo: 'multi', proseFmt: 'de {v}', dependeDe: { campo: 'transfusion', en: ['Sí'] }, opciones: ['Glóbulos rojos', 'Plaquetas', 'Plasma fresco', 'Crioprecipitado'] },
    { k: 'transfusionMotivo', grupo: 'transfusion', label: 'Motivo', tipo: 'select', dependeDe: { campo: 'transfusion', en: ['Sí'] }, opciones: ['Anemia', 'Plaquetopenia', 'Sangrado activo', 'Coagulopatía'],
      // "motivo anemia" era jerga de formulario — con la preposición se
      // encadena tras "con transfusión en el turno".
      prose: { 'Anemia': 'por anemia', 'Plaquetopenia': 'por plaquetopenia', 'Sangrado activo': 'por sangrado activo', 'Coagulopatía': 'por coagulopatía' } },
    // Fármacos de base hemato-endocrinos centralizados aquí (dictado del
    // fundador: levotiroxina del hipotiroideo, filgrastim del oncológico,
    // antiagregantes y estatinas): no son indicación nueva del turno, pero la
    // evolución que los nombra le ahorra una sorpresa al que recibe.
    { k: 'farmacosBase', grupo: 'cronicos', label: 'Fármacos de base', tipo: 'multi',
      opciones: ['AAS (aspirina)', 'Clopidogrel (Plavix)', 'Estatinas', 'Levotiroxina', 'Filgrastim', 'Eritropoyetina'],
      nota: { nivel: 'info', texto: 'Los antiagregantes pesan en sangrados y procedimientos; el filgrastim se controla con el RAN; la levotiroxina va en ayunas.' } },
    { k: 'farmacosBaseDetalle', grupo: 'cronicos', label: 'Dosis y horario', tipo: 'text', dependeDe: { campo: 'farmacosBase' },
      placeholder: 'Ej: levotiroxina 75 mcg en ayunas, AAS 100 mg día' },
    { k: 'transfusionReaccion', grupo: 'transfusion', label: 'Reacción transfusional', tipo: 'select', compactar: true, dependeDe: { campo: 'transfusion', en: ['Sí'] }, opciones: ['Sin reacción transfusional', 'Reacción febril', 'Reacción urticarial', 'Disnea durante transfusión', 'Escalofríos durante transfusión'],
      notas: {
        'Reacción febril':                { nivel: 'severo', texto: 'Detener transfusión, avisar de inmediato y registrar según protocolo de hemovigilancia.' },
        'Reacción urticarial':            { nivel: 'severo', texto: 'Detener transfusión, avisar de inmediato y registrar según protocolo de hemovigilancia.' },
        'Disnea durante transfusión':      { nivel: 'severo', texto: 'Detener transfusión, avisar de inmediato y registrar según protocolo de hemovigilancia.' },
        'Escalofríos durante transfusión': { nivel: 'severo', texto: 'Detener transfusión, avisar de inmediato y registrar según protocolo de hemovigilancia.' },
      },
      // "Reacción febril"/"Reacción urticarial" no dicen que son POST-transfusión
      // solas — el resto ya lo dice ("...durante transfusión") y queda autocontenido.
      prose: {
        'Reacción febril':     'reacción febril post-transfusional',
        'Reacción urticarial': 'reacción urticarial post-transfusional',
      } },
  ],
  // Tegumentario FORTALECIDO (dictado del fundador, 2026-07-16): EL caballito
  // de batalla del piloto Bupa. La integridad es la rama; LPP, DAI y herida
  // operatoria despliegan descripción y manejo MUY detallados, con los MISMOS
  // catálogos del mapa de piel del examen (máxima coherencia: zonas y
  // coberturas se importan de pielLpp.js, una sola fuente). Todo con nota
  // didáctica: la app enseña mientras se documenta.
  tegumentario: [
    // Selección MÚLTIPLE desde 2026-09-29 (decisión del fundador): un paciente
    // con LPP y herida operatoria tenía que elegir una. "Piel indemne" es
    // exclusiva: marcarla quita las demás y marcar otra la quita a ella. Un
    // valor viejo guardado como texto suelto se lee como lista de uno (sin
    // migrar), y un solo valor imprime exactamente lo de antes.
    { k: 'integridad', grupo: 'piel', label: 'Integridad de la piel', tipo: 'multi', rama: true, exclusivas: ['Piel indemne'], proseFmt: '{v}',
      // "Otra lesión de piel" (2026-10-10): el destino de las lesiones del
      // mapa que no son LPP, DAI ni herida operatoria (desgarro, lesión por
      // adhesivo, quemadura, úlceras, equimosis, hematoma…). Su descripción
      // completa va en la nota que deja el mapa.
      opciones: ['Piel indemne', 'Eritema', 'Lesión por presión (LPP)', 'DAI', 'Herida operatoria', 'Otra lesión de piel'],
      notas: {
        'Piel indemne':              { nivel: 'normal',   texto: 'Sin lesiones: mantener prevención (superficie y cambios de posición).' },
        'Eritema':                   { nivel: 'leve',     texto: 'Que no blanquee no basta para llamarlo LPP. Mira si está sobre una zona de presión o cizalla, compáralo con la piel vecina (temperatura, consistencia, edema, dolor) y descarta otras causas como la DAI. Regístralo en la estación de piel.' },
        'Lesión por presión (LPP)':  { nivel: 'moderado', texto: 'Descríbela completa: zona, categoría, lecho, exudado, dimensiones y cobertura.' },
        'DAI':                       { nivel: 'moderado', texto: 'Dermatitis por orina o deposiciones. No se distingue de la LPP solo por el lugar: mira la exposición, la distribución y si hay presión. Pueden coexistir, y el manejo es proteger de la humedad.' },
        'Herida operatoria':         { nivel: 'info',     texto: 'Descríbela completa: apósito, bordes, secreción y curación del turno.' },
        'Otra lesión de piel':       { nivel: 'info',     texto: 'Desgarro, lesión por adhesivo, quemadura, úlcera, equimosis, hematoma u otra. Regístrala en la estación de piel con su tipo y ubicación.' },
      } },
    // ── LPP: descripción completa (mismas zonas del mapa de piel del examen) ──
    { k: 'lppLocalizacion', grupo: 'piel', label: 'Localización de LPP', tipo: 'multi', dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      opciones: ZONAS_PRESION.filter((z) => z.id !== 'otra').map((z) => z.label).concat(['Otra zona']),
      nota: { nivel: 'info', texto: 'Si ya estaba al recibir al paciente, regístrala TAMBIÉN en la estación de piel. Ese registro con hora es tu respaldo médico-legal.' } },
    { k: 'lppDimensiones', grupo: 'piel', label: 'Dimensiones', tipo: 'text', dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      placeholder: 'Ej: 2 x 3 cm, profundidad 0,5 cm', pista: 'Medir en cada curación permite ver si la lesión avanza o cierra.' },
    { k: 'lppLecho', grupo: 'piel', label: 'Lecho de la lesión', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      opciones: ['Lecho granulatorio', 'Lecho esfacelado', 'Lecho necrótico', 'Lecho mixto'],
      notas: {
        'Lecho granulatorio': { nivel: 'normal',   texto: 'Tejido rojo y vital: la lesión va cerrando.' },
        'Lecho esfacelado':   { nivel: 'leve',     texto: 'Tejido desvitalizado blando, amarillo, gris o café. Ojo: no todo lo amarillo es esfacelo; la dermis profunda de una categoría II se ve blanco amarillenta.' },
        'Lecho necrótico':    { nivel: 'moderado', texto: 'Tejido negro o café, duro o blando: avisa para su evaluación. En el talón, una escara seca, adherida y sin eritema ni fluctuación no se retira.' },
        'Lecho mixto':        { nivel: 'leve',     texto: 'Describe los porcentajes en la curación (ej: 70% granulatorio, 30% esfacelo).' },
      } },
    { k: 'lppExudado', grupo: 'piel', label: 'Exudado', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      opciones: ['Sin exudado', 'Exudado seroso', 'Exudado serohemático', 'Exudado purulento'],
      notas: { 'Exudado purulento': { nivel: 'moderado', texto: 'Sugiere infección: avisa y cultiva según indicación.' } } },
    { k: 'lppCobertura', grupo: 'piel', label: 'Cobertura de LPP', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      opciones: COBERTURAS_LESION,
      notas: {
        'Hidrocoloide':        { nivel: 'info', texto: 'Para lesiones superficiales con poco exudado; mantiene el ambiente húmedo.' },
        'Espuma hidrocelular': { nivel: 'info', texto: 'Absorbe exudado moderado y amortigua la presión.' },
        'Alginato':            { nivel: 'info', texto: 'Para exudado abundante o lecho sangrante; requiere apósito secundario.' },
        'Apósito de plata':    { nivel: 'info', texto: 'Cuando hay carga bacteriana o infección local.' },
      },
      // Los apósitos solos no dicen "cobertura" — ambiguos sueltos; "Sin
      // cobertura" ya se lee autocontenida (queda con el default compactar).
      prose: {
        'Hidrocoloide':         'cobertura con hidrocoloide',
        'Espuma hidrocelular':  'cobertura con espuma hidrocelular',
        'Alginato':             'cobertura con alginato',
        'Apósito de plata':     'cobertura con apósito de plata',
        'Gasa vaselinada':      'cobertura con gasa vaselinada',
      } },
    { k: 'lppEvolucionHoy', grupo: 'piel', label: '¿Cómo va hoy?', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Lesión por presión (LPP)'] },
      opciones: ['LPP en mejoría', 'LPP estacionaria', 'LPP empeorando'],
      notas: { 'LPP empeorando': { nivel: 'moderado', texto: 'Reevalúa cobertura, superficie y frecuencia de cambios de posición; avisa.' } } },
    // ── DAI: el manejo es de humedad, no de presión ──
    { k: 'daiManejo', grupo: 'piel', label: 'Manejo de la DAI', tipo: 'multi', dependeDe: { campo: 'integridad', en: ['DAI'] },
      opciones: ['Crema barrera (óxido de zinc)', 'Aseo y secado por turno', 'Dispositivo de contención de humedad'],
      nota: { nivel: 'info', texto: 'La clave es proteger la piel de la humedad; objetívala con la escala DAI de abajo.' } },
    // ── Herida operatoria: describir mucho (dictado del fundador) ──
    { k: 'herLocalizacion', grupo: 'piel', label: 'Localización de la herida', tipo: 'text', dependeDe: { campo: 'integridad', en: ['Herida operatoria'] },
      placeholder: 'Ej: mediana supraumbilical, lumbar, cara anterior de rodilla' },
    { k: 'herAposito', grupo: 'piel', label: 'Apósito', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Herida operatoria'] },
      opciones: ['Apósito seco y limpio', 'Apósito con manchado escaso', 'Apósito manchado, se cambia'],
      notas: { 'Apósito manchado, se cambia': { nivel: 'leve', texto: 'Registra el aspecto de lo que mancha (seroso, hemático, purulento).' } },
      prose: { 'Apósito manchado, se cambia': 'apósito manchado que se cambia en el turno' } },
    { k: 'herBordes', grupo: 'piel', label: 'Bordes', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Herida operatoria'] },
      opciones: ['Bordes afrontados', 'Eritema perilesional', 'Dehiscencia parcial', 'Dehiscencia total'],
      notas: {
        'Bordes afrontados':   { nivel: 'normal',   texto: 'Cierre esperado.' },
        'Eritema perilesional':{ nivel: 'leve',     texto: 'Vigila extensión y temperatura local: puede ser el primer signo de infección.' },
        'Dehiscencia parcial': { nivel: 'moderado', texto: 'Avisa y describe cuántos puntos o centímetros abrieron.' },
        'Dehiscencia total':   { nivel: 'severo',   texto: 'Urgencia quirúrgica: cubre con gasa estéril húmeda y avisa de inmediato.' },
      },
      prose: { 'Eritema perilesional': 'con eritema perilesional', 'Dehiscencia parcial': 'con dehiscencia parcial', 'Dehiscencia total': 'con dehiscencia total' } },
    { k: 'herSecrecion', grupo: 'piel', label: 'Secreción', tipo: 'select', compactar: true, dependeDe: { campo: 'integridad', en: ['Herida operatoria'] },
      opciones: ['Sin secreción', 'Secreción serosa', 'Secreción serohemática', 'Secreción purulenta'],
      notas: { 'Secreción purulenta': { nivel: 'moderado', texto: 'Sugiere infección de herida operatoria: avisa y cultiva según indicación.' } } },
    // ── Prevención y manejo (no depende de tener una lesión) ──
    // Superficies con el vocabulario de la guía internacional 2025 (cap.
    // Superficies de apoyo): el genérico "antiescaras estático/dinámico" se
    // reemplazó por las categorías reales (módulo LPP, 2026-08-18). Un
    // registro del mismo día con el string viejo solo pierde la selección
    // visible (los registros de turno son efímeros: mueren con el turno).
    { k: 'superficieManejo', grupo: 'prevencion', label: 'Superficie de apoyo', tipo: 'select', compactar: true, opciones: ['Colchón estándar', 'Colchón viscoelástico', 'Colchón de aire estático', 'Colchón de aire alternante'],
      notas: {
        'Colchón estándar': { nivel: 'info', texto: 'Con Braden 15 o menos (riesgo moderado o alto según la NT 234), gestiona una superficie con redistribución: viscoelástico como base, y de aire en riesgo alto. Y los cambios de posición siguen: ninguna superficie los reemplaza.' },
        'Colchón viscoelástico': { nivel: 'info', texto: 'Espuma con redistribución de presión: la recomendación de base para todo paciente en riesgo. Reparte el peso como arena húmeda que toma tu forma.' },
        'Colchón de aire estático': { nivel: 'info', texto: 'Flotación seca para riesgo alto. Chequea el inflado con la mano cada turno: si el cuerpo "toca fondo", no está protegiendo.' },
        'Colchón de aire alternante': { nivel: 'info', texto: 'Celdas que se inflan y desinflan por ciclos, para riesgo alto. Verifica que el motor funcione y chequea el inflado cada turno.' },
      } },
    { k: 'cambiosPosicion', grupo: 'prevencion', label: 'Cambios de posición', tipo: 'select', compactar: true, opciones: ['Cambios de posición c/2 h', 'Cambios de posición c/3 h', 'Cambios de posición c/4 h', 'Cambios según tolerancia', 'Cambios de posición no indicados'] },
    { k: 'curaciones', grupo: 'heridas', label: 'Curaciones', tipo: 'select', compactar: true, opciones: ['Sin curaciones', 'Curación realizada', 'Curación pendiente'] },
    { k: 'curacionDetalle', grupo: 'heridas', label: 'Detalle de curación', tipo: 'text', dependeDe: { campo: 'curaciones', en: ['Curación realizada', 'Curación pendiente'] }, placeholder: 'Ej: bordes macerados, 80% granulatorio, apósito usado' },
  ],
  // Procedimientos e intervenciones: el segmento de cierre B2B (ver SISTEMAS
  // en evolucionSistema.js) tenía CERO campos estructurados — traslados, toma
  // de muestras, retiro de dispositivos y exámenes solo cabían en nota libre.
  procedimientos: [
    { k: 'trasladoDestino', grupo: 'traslado', label: 'Traslado', tipo: 'select', compactar: true, opciones: ['Sin traslados en el turno', 'Traslado a pabellón', 'Traslado a imagenología', 'Traslado a UCI/UTI', 'Traslado a otro servicio', 'Alta del servicio'] },
    { k: 'muestraTipo', grupo: 'muestras', label: 'Toma de muestras', tipo: 'multi', opciones: ['Sangre venosa', 'Gases arteriales (GSA)', 'Orina completa', 'Deposiciones', 'Secreción', 'Biopsia'],
      nota: { nivel: 'info', texto: 'Los cultivos van en Infectología.' } },
    { k: 'retiroDispositivo', grupo: 'retiro', label: 'Retiro de dispositivo', tipo: 'select', compactar: true, opciones: ['VVP', 'CVC', 'Línea arterial', 'SNG / SOG', 'Catéter urinario permanente', 'Drenaje'],
      // Ningún dispositivo dice "retiro" por sí solo — ambiguos sueltos. El
      // nombre legado "CUP / Foley" conserva su prosa (drafts viejos).
      prose: {
        'VVP':          'retiro de VVP',
        'CVC':          'retiro de CVC',
        'Línea arterial': 'retiro de línea arterial',
        'SNG / SOG':    'retiro de SNG / SOG',
        'Catéter urinario permanente': 'retiro de catéter urinario permanente',
        'CUP / Foley':  'retiro de CUP / Foley',
        'Drenaje':      'retiro de drenaje',
      } },
    { k: 'retiroMotivo', grupo: 'retiro', label: 'Motivo del retiro', tipo: 'select', compactar: true, dependeDe: { campo: 'retiroDispositivo' }, opciones: ['Fin de indicación', 'Disfunción u obstrucción', 'Sospecha de infección del sitio', 'Retiro por el paciente'],
      notas: { 'Retiro por el paciente': { nivel: 'moderado', texto: 'Evalúa de inmediato al paciente y el sitio (sangrado, vía aérea, necesidad de reinstalación), avisa al médico y registra como evento adverso; refuerza vigilancia y evalúa contención solo con indicación médica.' } },
      prose: {
        'Fin de indicación':                'por fin de indicación',
        'Disfunción u obstrucción':         'por disfunción u obstrucción',
        'Sospecha de infección del sitio':  'por sospecha de infección del sitio',
        'Retiro por el paciente':            'porque el paciente se lo retiró',
      } },
    { k: 'examenRealizado', grupo: 'examenes', label: 'Exámenes del turno', tipo: 'multi', opciones: ['Rx de tórax', 'TAC', 'Ecografía', 'Endoscopia', 'ECG'] },
  ],
};

// Pista didáctica de cada escala (qué mide · rango).
export const ESCALA_PISTA = {
  glasgow: 'Conciencia · O+V+M · 3–15',
  four:    'Conciencia en UCI · 0–16 · válida en intubados',
  rass:    'Sedación–agitación · −5 a +4',
  sas:     'Sedación–agitación · 1–7',
  eva:     'Dolor autorreportado · 0–10',
  cpot:    'Dolor en no comunicante · 0–8',
  cam_icu: 'Tamizaje de delirium en UCI',
  cam:     'Tamizaje de delirium fuera de UCI',
  braden:  'Riesgo de LPP · 6–23 · NT 234: ≤12 alto, 13–15 moderado, ≥16 bajo',
  lpp:     'Estadio de lesión por presión',
  pam:     'Perfusión · meta ≥65 mmHg',
  sfo2:    'Oxigenación no invasiva (≈ PaFi)',
  mottling:'Hipoperfusión cutánea · 0–5',
  pafi:    'Oxigenación / SDRA · PaO₂ ÷ FiO₂',
  ritmo_diur: 'Diuresis ÷ peso ÷ horas · meta ≥0,5 ml/kg/h',
  dai:     'Dermatitis asociada a incontinencia (Ghent)',
  morse:   'Riesgo de caídas · 0–125',
  downton: 'Riesgo de caídas · ≥3 = riesgo alto',
};

// ── Despliegue condicional ────────────────────────────────────────────────────
// Un campo o escala puede declarar `dependeDe` para mostrarse solo según el
// valor de otro campo del mismo sistema:
//   { campo, en: [...] }   → visible si el valor está en la lista
//   { campo, noEn: [...] } → visible si el valor NO está en la lista
//   [dep, dep]             → O: basta que una se cumpla
//   { todas: [dep, dep] }  → Y: tienen que cumplirse todas (se anidan)
// Sin `dependeDe`, siempre visible.
export function cumpleDependencia(dependeDe, campos) {
  if (!dependeDe) return true;
  // Forma OR: un array de dependencias, basta que UNA se cumpla. Nació para
  // FOUR en neuro (aplica con sedación O con conciencia comprometida).
  if (Array.isArray(dependeDe)) return dependeDe.some((d) => cumpleDependencia(d, campos));
  // Forma Y: nació con el `amerita` de las escalas (2026-09-29: EVA amerita
  // si hay dolor respondido Y el paciente puede contarlo). Hoy ningún CAMPO la
  // usa; los lectores de `dependeDe` de campos asumen hojas o arrays.
  if (Array.isArray(dependeDe.todas)) return dependeDe.todas.every((d) => cumpleDependencia(d, campos));
  const v = campos?.[dependeDe.campo];
  if (Array.isArray(dependeDe.noEn)) return !dependeDe.noEn.includes(v);
  // Con un campo de selección múltiple basta que UNA opción marcada esté en la
  // lista (2026-09-29: el residuo gástrico se abre con el síntoma "Residuo
  // elevado"). Antes ninguna dependencia `en` colgaba de un multi.
  if (Array.isArray(dependeDe.en)) return Array.isArray(v) ? v.some((x) => dependeDe.en.includes(x)) : dependeDe.en.includes(v);
  return v !== undefined && v !== null && String(v).trim() !== '';
}

// Escalas de un sistema normalizadas a { id, dependeDe? }.
export function escalasDe(sistemaId) {
  return (ESCALAS_POR_SISTEMA[sistemaId] || []).map((e) => (typeof e === 'string' ? { id: e } : e));
}

// Escalas visibles del sistema según los campos actuales (resuelve dependeDe).
// Visibilidad TRANSITIVA de un campo: no basta con que su dependeDe se cumpla —
// el campo padre debe además ser visible él mismo. Sin esto, las cadenas de
// 2–3 niveles (hdTolerancia→hdSesionHoy→dialisis→funcionRenal; cultivo*) dejaban
// "fantasmas": el usuario cambia funcionRenal, el valor viejo de `dialisis`
// queda en el estado aunque su campo ya no se vea, y los nietos (crrt*, hd*)
// seguían visibles y compilando a la prosa.
function campoVisiblePorK(sistemaId, k, campos, vistos = new Set()) {
  const def = (CAMPOS_POR_SISTEMA[sistemaId] || []).find((c) => c.k === k);
  if (!def) return false;
  if (!def.dependeDe) return true;
  if (vistos.has(k)) return false; // guarda defensiva ante ciclos (hoy no existen)
  vistos.add(k);
  // Forma OR: visible si ALGUNA dependencia se cumple con su campo padre
  // visible. Cada rama explora con su propia copia de `vistos`: la guarda es
  // por ciclo dentro de una cadena, no entre ramas hermanas.
  const deps = Array.isArray(def.dependeDe) ? def.dependeDe : [def.dependeDe];
  return deps.some((d) => cumpleDependencia(d, campos) && campoVisiblePorK(sistemaId, d.campo, campos, new Set(vistos)));
}

// Una dependencia de ESCALA (dependeDe o amerita) evaluada con visibilidad
// transitiva: una hoja `en` o de presencia solo se cumple si su campo padre
// está a la vista (un valor viejo de un campo oculto no despliega nada). Una
// hoja `noEn` con el padre oculto SÍ se cumple: si "conciencia" no se pregunta
// porque el paciente está sedado, su valor viejo "Coma" no puede vetar nada.
// Las `noEn` que existían antes de esta regla cuelgan todas de campos raíz
// (siempre visibles), así que para ellas nada cambia.
function dependenciaVisible(sistemaId, dep, campos) {
  if (!dep) return true;
  if (Array.isArray(dep)) return dep.some((d) => dependenciaVisible(sistemaId, d, campos));
  if (Array.isArray(dep.todas)) return dep.todas.every((d) => dependenciaVisible(sistemaId, d, campos));
  const padreVisible = campoVisiblePorK(sistemaId, dep.campo, campos);
  if (Array.isArray(dep.noEn) && !padreVisible) return true;
  return padreVisible && cumpleDependencia(dep, campos);
}

// Qué se OFRECE: la escala tiene que ser válida (dependeDe) y ameritarse
// (amerita). Lo que se DIBUJA suma las que ya tienen valor (escalasDibujables).
export function escalasVisibles(sistemaId, campos) {
  return escalasDe(sistemaId).filter((e) => dependenciaVisible(sistemaId, e.dependeDe, campos)
    && dependenciaVisible(sistemaId, e.amerita, campos));
}

// Escalas que la Mesa debe DIBUJAR: las visibles por dependencia MÁS
// cualquiera que ya tenga valores. Un dato registrado siempre tiene que ser
// alcanzable para verlo, corregirlo o borrarlo.
//
// Sin esto, una escala puntuada que deja de ser visible se quedaba imprimiendo
// en el documento sin tener dónde tocarse: pasaba si la autora cambiaba el
// campo del que la escala colgaba (llenó Glasgow y después marcó "Con
// sedación"), y desde 2026-09-19 también si la guardó desde la calculadora
// rápida fuera de su condición (FOUR con "Sin sedación"). `escalasVisibles`
// sigue decidiendo qué se OFRECE sin datos, que es la regla de no preguntar
// lo que no corresponde; esto decide qué se MUESTRA, que no es lo mismo.
export function escalasDibujables(sistemaId, campos, escalas) {
  const visibles = new Set(escalasVisibles(sistemaId, campos).map((e) => e.id));
  // Se recorre el catálogo del sistema para conservar su ORDEN.
  return escalasDe(sistemaId).filter(
    (e) => visibles.has(e.id) || escalaConValores(e.id, escalas?.[e.id]),
  );
}

// ¿Guardar esta escala CONTRADICE algo que la autora ya firmó en un campo?
//
// La calculadora rápida deja MIRAR cualquier escala (es su razón de existir),
// pero guardarla puede escribir una línea que se pelea consigo misma en el
// documento que se copia a la ficha institucional: con `dolor = No evaluable`
// la EVA imprimía "Dolor no evaluable, EVA 8/10, dolor intenso". Y la EVA con
// dolor no evaluable se retiró a propósito el 2026-08-08 (feedback de Tania
// Sánchez, jefa de UPC de Bupa): la app no puede esconderla por una puerta y
// ofrecerla por la otra.
//
// Devuelve { campo, label, valor } del primer campo contradicho, o null. Solo
// habla cuando el campo TIENE valor: sin responder no hay nada que contradecir,
// es simplemente una escala que todavía no aplica.
//
// Mira SOLO `dependeDe`, jamás `amerita`: que una escala no se amerite (RASS
// en el vigil) no la vuelve falsa, y avisar ahí sería ruido.
export function escalaContradiceCampo(sistemaId, escalaId, campos) {
  const def = escalasDe(sistemaId).find((e) => e.id === escalaId);
  if (!def?.dependeDe) return null;
  if (cumpleDependencia(def.dependeDe, campos)) return null;
  const deps = Array.isArray(def.dependeDe) ? def.dependeDe : [def.dependeDe];
  const defs = CAMPOS_POR_SISTEMA[sistemaId] || [];
  for (const d of deps) {
    const valor = campos?.[d.campo];
    if (valor === undefined || valor === null || String(valor).trim() === '') continue;
    return { campo: d.campo, label: defs.find((c) => c.k === d.campo)?.label || d.campo, valor: String(valor) };
  }
  return null;
}

// Campos visibles del sistema según los valores actuales (resuelve dependeDe
// de forma transitiva: el hijo de un campo oculto también está oculto).
export function camposVisibles(sistemaId, campos) {
  return (CAMPOS_POR_SISTEMA[sistemaId] || []).filter(
    (c) => !c.dependeDe || campoVisiblePorK(sistemaId, c.k, campos),
  );
}

// ── Secciones (agrupación visible de la valoración) ───────────────────────────
// Los campos/escalas pueden declarar `grupo`; un sistema "seccionado" se muestra
// inline (sin acordeón) con un título por grupo, en orden clínico.
export const GRUPO_LABEL = {
  conciencia: 'Estado de conciencia',
  delirium: 'Delirium',
  dolor: 'Dolor',
  movilidad: 'Movilidad y reposo',
  examen: 'Examen físico',
  osmoterapia: 'Terapia osmótica y natremia',
  // Hemodinamia
  estado: 'Estado hemodinámico',
  drogas: 'Drogas vasoactivas',
  perfusion: 'Examen físico y perfusión',
  micro: 'Microhemodinamia avanzada',
  soporteExtra: 'Soporte extracorpóreo',
  // Respiratorio
  soporte: 'Vía aérea y soporte',
  mecanica: 'Mecánica y oxigenación',
  secreciones: 'Secreciones',
  gases: 'Gases arteriales',
  // Gastro-metabólico
  regimen: 'Régimen y nutrición',
  glicemia: 'Metabolismo / glicemia',
  digestivo: 'Tránsito y abdomen',
  // Renal
  diuresis: 'Diuresis',
  funcionRenal: 'Función renal',
  trr: 'Terapia de reemplazo renal',
  // Infeccioso
  fiebre: 'Estado febril e infección',
  antibiotico: 'Antibióticos',
  // Hematología
  tromboprofilaxis: 'Tromboprofilaxis',
  labhem: 'Laboratorio hematológico',
  transfusion: 'Sangrado y transfusión',
  cronicos: 'Fármacos de base (hemato y endocrino)',
  // Tegumentario
  piel: 'Integridad de la piel',
  prevencion: 'Prevención y manejo',
  heridas: 'Heridas y curaciones',
  // Invasivos
  accesos: 'Accesos y dispositivos',
  // Procedimientos e intervenciones
  traslado: 'Traslados',
  muestras: 'Toma de muestras',
  retiro: 'Retiro de dispositivos',
  examenes: 'Exámenes e imágenes',
};

// ¿El sistema usa la vista por secciones? (alguno de sus campos define grupo).
export function sistemaSeccionado(sistemaId) {
  return (CAMPOS_POR_SISTEMA[sistemaId] || []).some((c) => c.grupo);
}

// Grupos que SON el examen físico del sistema (dictado del fundador
// 2026-07-31: cada sistema TERMINA con el examen físico, narrado como en la
// evolución real). gruposDe los mueve al final — UI y documento comparten el
// mismo orden — y la prosa del grupo abre con "Al examen físico".
export const GRUPO_EXAMEN_FISICO = { neuro: 'examen', hemodinamia: 'perfusion' };

// Orden NARRATIVO dentro de la oración de un grupo (dictado del fundador,
// 2026-09-07): la escala va pegada a lo que describe, no al final de la
// oración. Ids de campo y de escala mezclados, en el orden de lectura. Lo que
// no está en la lista va después, en su orden de declaración. Solo los grupos
// listados cambian; el resto del catálogo imprime igual que siempre.
//  neuro/conciencia: sedación → RASS/SAS → objetivo e infusiones (con dosis)
//    → BIS/BNM → conciencia → Glasgow/FOUR → conexión → orientación → órdenes.
//  neuro/dolor: dolor → EVA/CPOT → ubicación → manejo (el manejo con su problema).
export const ORDEN_PROSA_GRUPO = {
  neuro: {
    conciencia: ['sedacion', 'rass', 'sas', 'objetivoSedacion', 'sedoanalgesia', 'sedoDetalle', 'bis', 'bnm', 'tof', 'conciencia', 'glasgow', 'four', 'conexion', 'orientacion', 'ordenes', 'ordenesDe5'],
    dolor: ['dolor', 'eva', 'cpot', 'dolorUbicacion', 'dolorManejo'],
  },
};

// Grupos del sistema en orden de declaración (campos primero, luego escalas),
// con el grupo de examen físico SIEMPRE al cierre.
export function gruposDe(sistemaId) {
  const orden = [];
  const push = (g) => { if (g && !orden.includes(g)) orden.push(g); };
  (CAMPOS_POR_SISTEMA[sistemaId] || []).forEach((c) => push(c.grupo));
  escalasDe(sistemaId).forEach((e) => push(e.grupo));
  const examen = GRUPO_EXAMEN_FISICO[sistemaId];
  if (examen && orden.includes(examen)) {
    return [...orden.filter((g) => g !== examen), examen];
  }
  return orden;
}

// Resultado calculado de una escala (o null si faltan datos).
export function scoreEscala(escalaId, vals) {
  const def = ESCALAS_DEF[escalaId];
  if (!def || !vals) return null;
  return def.score(vals);
}

// ¿La escala tiene al menos un valor ingresado? (aunque aún no compute).
export function escalaConValores(escalaId, vals) {
  const def = ESCALAS_DEF[escalaId];
  if (!def || !vals) return false;
  if (def.tipo === 'items') return def.items.some((i) => vals[i.k] !== undefined && vals[i.k] !== null);
  if (def.tipo === 'calc') return def.inputs.some((i) => vals[i.k] !== undefined && String(vals[i.k]).trim() !== '');
  return vals[def.campo] !== undefined && vals[def.campo] !== null && String(vals[def.campo]).trim() !== '';
}

// ── Campo `porDroga` (dosis y tendencia POR droga) ───────────────────────────
// Valor: { [droga]: { dosis?: string, tendencia?: string } }. La lista de
// drogas válidas vive en el campo `fuente` (un multi del mismo sistema): las
// entradas de drogas des-marcadas quedan huérfanas y NO se imprimen.
// Unidad de titulación por droga (dictado del fundador 2026-08-08, a partir
// del feedback de Tania Sánchez, jefa de UPC: "¿por qué dejarlo abierto?, por
// qué no dejar la mg/kg/hr"). El campo sigue siendo texto libre — la dosis
// real la escribe el enfermero — pero el ejemplo deja de ser genérico y trae
// LA unidad con la que se titula esa droga, que es lo que evita el registro
// ambiguo ("30" ¿de qué?).
//
// Fuente única: la consumen el chequeo de recepción y la Mesa de evolución.
// Solo entran drogas con unidad CONFIRMADA por el fundador, porque inventar
// la unidad de una droga vasoactiva no es un detalle de presentación. Desde
// 2026-09-29 están TODAS las de los catálogos de sedoanalgesia y DVA (fijado
// por test): una droga nueva en esos catálogos exige preguntarle su unidad
// antes de agregarla.
export const UNIDAD_DROGA = {
  // Vasoactivas
  Noradrenalina: 'mcg/kg/min',
  Adrenalina:    'mcg/kg/min',
  Dobutamina:    'mcg/kg/min',
  Dopamina:      'mcg/kg/min',
  // Era 'gamma/kg/min': es la misma unidad (1 gamma = 1 mcg) y la unidad pasó
  // a ser FIJA en pantalla (2026-09-29), así que se escribe como las demás.
  Milrinona:     'mcg/kg/min',
  Vasopresina:   'U/min',
  // Vasodilatadores y antihipertensivos EV: labetalol, urapidilo y
  // nitroglicerina se titulan por hora o por minuto, no por peso.
  // Nitroprusiato y esmolol sí van por peso (confirmados 2026-09-29).
  Labetalol:      'mg/hr',
  Urapidilo:      'mg/hr',
  Nitroglicerina: 'mcg/min',
  Nitroprusiato:  'mcg/kg/min',
  Esmolol:        'mcg/kg/min',
  // Sedoanalgesia
  Dexmedetomidina: 'mcg/kg/hr',
  Propofol:        'mg/kg/hr',
  Midazolam:       'mg/kg/hr',
  Fentanilo:       'mcg/kg/hr',
  // Remifentanilo y ketamina, confirmados 2026-09-29.
  Remifentanilo:   'mcg/kg/min',
  Ketamina:        'mg/kg/hr',
  Esketamina:      'mcg/kg/hr',
  Morfina:         'mg/hr',
};

/** Ejemplo de dosis para una droga: con su unidad si está confirmada, y si no
 *  el ejemplo genérico que ya existía. */
export function placeholderDosis(droga, generico = 'Dosis. Ej: 0,05 mcg/kg/min') {
  const u = UNIDAD_DROGA[droga];
  return u ? `Dosis en ${u}` : generico;
}

// ── Unidad FIJA de la dosis (dictado del fundador, 2026-09-29: "dejar fijo
// los apellidos: mg/kg/hr, mcg/kg/min, U/min según el fármaco") ─────────────
// Para las drogas de UNIDAD_DROGA la pantalla pide SOLO el número y muestra la
// unidad al lado, fija. Lo guardado sigue siendo el mismo texto de siempre
// ("0,1 mcg/kg/min"), así que la prosa, el pulido, sus redes y los puentes no
// cambian. Las drogas sin unidad confirmada siguen con texto libre, y un dato
// viejo escrito a mano con otra cosa ("30 ml/h") se respeta tal cual: la
// pantalla jamás reescribe lo que la autora escribió.

// Grafías de la misma unidad: "gamma" es mcg, "hr" es h, y con o sin barras
// ("0.5 mg kg hr" es una captura real de turno, 2026-09-12).
function normalizarUnidad(u) {
  return String(u || '').toLowerCase()
    .replace(/gammas?|γ|µg|μg|\bug\b/g, 'mcg')
    .replace(/minutos?/g, 'min')
    .replace(/horas?|hrs?/g, 'h')
    .replace(/\bui\b/g, 'u')
    .replace(/[\s/]+/g, '');
}

/** El número de una dosis guardada, si la droga tiene unidad fija y lo
 *  guardado es un número solo o un número con ESA unidad: "0,1 mcg/kg/min" →
 *  "0,1", "" → "". Devuelve null si la droga no tiene unidad fija o lo
 *  guardado es otra cosa: ahí se muestra el texto libre tal cual. */
export function numeroDeDosis(droga, dosis) {
  const u = UNIDAD_DROGA[droga];
  if (!u) return null;
  const t = String(dosis ?? '').trim();
  if (t === '') return '';
  const m = t.match(/^(\d+(?:[.,]\d*)?)\s*(.*)$/);
  if (!m) return null;
  const resto = normalizarUnidad(m[2]);
  return resto === '' || resto === normalizarUnidad(u) ? m[1] : null;
}

/** Lo que se guarda al escribir el número: solo dígitos y UNA coma decimal
 *  (el punto se lee como coma, como se escribe en Chile), con la unidad fija
 *  pegada. Vacío guarda vacío. `alCerrar` quita una coma colgando al final
 *  (mientras se escribe "0," tiene que poder quedar). */
export function componerDosis(droga, texto, { alCerrar = false } = {}) {
  const u = UNIDAD_DROGA[droga];
  let n = String(texto ?? '').replace(/\./g, ',').replace(/[^\d,]/g, '');
  const [entero, ...dec] = n.split(',');
  n = dec.length ? `${entero || '0'},${dec.join('')}` : entero;
  if (alCerrar) n = n.replace(/,$/, '');
  if (!n) return '';
  return u ? `${n} ${u}` : n;
}

/** La dosis como se imprime: a un número solo (dato viejo o pegado sin
 *  unidad) se le suma la unidad fija de su droga, la misma que la pantalla le
 *  mostró al lado. Todo lo demás, tal cual lo escribió la autora. */
export function dosisConUnidad(droga, dosis) {
  const t = String(dosis ?? '').trim();
  const u = UNIDAD_DROGA[droga];
  if (!u || !/^\d+(?:[.,]\d*)?$/.test(t)) return t;
  return `${t.replace(/[.,]$/, '')} ${u}`;
}

export const TENDENCIAS_DROGA = [
  { v: 'subiendo', label: '↑ Titulando al alza' },
  { v: 'estable',  label: 'Estable' },
  { v: 'bajando',  label: '↓ Titulando a la baja' },
  { v: 'retirada', label: 'En retirada' },
];
const TENDENCIA_PROSE = {
  subiendo: 'titulando al alza',
  estable:  'en dosis estable',
  bajando:  'titulando a la baja',
  retirada: 'en retirada',
};
function detalleDrogaConContenido(det) {
  return Boolean(det && ((det.dosis && String(det.dosis).trim()) || det.tendencia));
}
// Entradas imprimibles de un campo porDroga, en el orden del multi fuente.
// `campos` filtra las huérfanas; sin él (llamadas legacy) imprime todas.
function porDrogaEntradas(campo, valor, campos) {
  if (!valor || typeof valor !== 'object') return [];
  const fuente = campos ? (Array.isArray(campos[campo.fuente]) ? campos[campo.fuente] : []) : Object.keys(valor);
  return fuente
    .filter((d) => detalleDrogaConContenido(valor[d]))
    .map((d) => ({ droga: d, ...valor[d] }));
}

// ¿El campo tiene un valor real? (los objetos porDroga vacíos no cuentan).
// Exportada: la UI la usa para encender el semáforo por sección.
export function campoConValor(campo, v) {
  if (v === undefined || v === null) return false;
  if (campo.tipo === 'porDroga') {
    return typeof v === 'object' && Object.values(v).some(detalleDrogaConContenido);
  }
  if (Array.isArray(v)) return v.length > 0;
  return String(v).trim() !== '';
}

// Interpretación didáctica en vivo de un campo (o null).
export function interpretarCampo(campo, valor) {
  if (!campo || valor === undefined || valor === null) return null;
  if (campo.tipo === 'num') {
    if (!campo.bandas) return null;
    const n = parseFloat(valor);
    if (isNaN(n)) return null;
    const b = campo.bandas.find((x) => n <= x.hasta) || campo.bandas[campo.bandas.length - 1];
    return { texto: b.texto, nivel: b.nivel };
  }
  if (campo.tipo === 'multi') {
    valor = comoArrayMultiEscala(valor);
    if (valor.length === 0) return null;
    // Didáctica por opción también en multi (auscultación del examen): las
    // notas de las opciones marcadas se juntan y el nivel es el más severo.
    if (campo.notas) {
      const marcadas = valor.map((v) => campo.notas[v]).filter(Boolean);
      if (marcadas.length) {
        const peso = { severo: 3, moderado: 2, leve: 1, info: 0, normal: 0 };
        const nivel = marcadas.reduce((a, b) => ((peso[b.nivel] || 0) > (peso[a.nivel] || 0) ? b : a)).nivel;
        return { nivel, texto: marcadas.map((n) => n.texto).join(' ') };
      }
    }
    return campo.nota || null;
  }
  if (campo.tipo === 'porDroga') {
    return campoConValor(campo, valor) ? (campo.nota || null) : null;
  }
  if (campo.tipo === 'select' || campo.tipo === 'cards') {
    if (String(valor).trim() === '') return null;
    return campo.notas?.[valor] || null;
  }
  return null; // text: sin interpretación
}

// Un valor con letras en un campo numérico es un dato viejo de cuando el campo
// era texto (ver camposProse).
const TIENE_LETRAS = /[a-záéíóúñ]/i;

function formatCampo(campo, valor) {
  if (valor === undefined || valor === null) return null;
  if (campo.tipo === 'num') {
    const v = String(valor).trim();
    if (v !== '' && TIENE_LETRAS.test(v)) return `${campo.label}: ${v}`;
    return v === '' ? null : `${campo.label} ${v}${campo.unidad ? ` ${campo.unidad}` : ''}`;
  }
  if (campo.tipo === 'porDroga') {
    const entradas = porDrogaEntradas(campo, valor);
    if (!entradas.length) return null;
    return entradas
      .map((e) => [e.droga, dosisConUnidad(e.droga, e.dosis), TENDENCIA_PROSE[e.tendencia]].filter(Boolean).join(' '))
      .join('; ');
  }
  if (campo.tipo === 'multi') {
    const lista = comoArrayMultiEscala(valor).filter((x) => typeof x === 'string' && x.trim());
    return lista.length ? `${campo.label}: ${lista.join(', ')}` : null;
  }
  if (campo.tipo === 'text') {
    const v = String(valor).trim();
    return v === '' ? null : `${campo.label}: ${v}`;
  }
  // select
  if (String(valor).trim() === '') return null;
  return campo.compactar ? `${valor}` : `${campo.label} ${valor}`;
}

// Línea compilada de campos estructurados de un sistema (o null). Respeta el
// despliegue condicional: los campos ocultos no entran a la salida.
export function compilarCampos(sistemaId, campos) {
  if (!campos) return null;
  const partes = camposVisibles(sistemaId, campos).map((c) => formatCampo(c, campos[c.k])).filter(Boolean);
  return partes.length ? partes.join(' · ') : null;
}

// Líneas de escalas (bullets) de un sistema, ya calculadas (o []). `campos`
// resuelve qué escalas condicionales aplican.
export function compilarEscalas(sistemaId, escalas, campos) {
  if (!escalas) return [];
  return escalasVisibles(sistemaId, campos)
    .map((e) => scoreEscala(e.id, escalas[e.id]))
    .filter(Boolean)
    .map((r) => `• ${r.texto}`);
}

// Escalas de un sistema en forma corta para prosa ("GCS 15/15", "RASS 0"…).
export function compilarEscalasInline(sistemaId, escalas, campos) {
  if (!escalas) return [];
  return escalasVisibles(sistemaId, campos)
    .map((e) => scoreEscala(e.id, escalas[e.id]))
    .filter(Boolean)
    .map((r) => r.texto.split(' — ')[0].split(': ')[0]);
}

// Etiqueta en minúscula para prosa, salvo acrónimos — evaluado POR PALABRA
// (antes miraba la etiqueta completa: "Dosis DVA" y "Modo VMI" quedaban con
// "Dosis"/"Modo" en mayúscula a mitad de oración porque "DVA"/"VMI" hacían
// mirar toda la etiqueta como abreviatura). Ahora solo se deja tal cual la
// PRIMERA palabra cuando ELLA MISMA es el acrónimo (mayúscula interna, ej.
// "PAM" en "PAM objetivo"); si no, se minusculiza solo esa primera palabra y el
// resto de la etiqueta queda igual (para no tocar acrónimos más adelante).
function labelProse(label) {
  const t = label || '';
  const palabras = t.split(' ');
  const primera = palabras[0] || '';
  if (EPONIMOS_PRESERVADOS.has(primera)) return t; // ej: "Vt" → queda igual
  const primeraEsAcronimo = /[A-ZÁÉÍÓÚÑ]/.test(primera.slice(1));
  if (primeraEsAcronimo) return t; // ej: "PAM objetivo" → queda igual
  if (palabras.length === 1) return primera.toLowerCase();
  return [primera.charAt(0).toLowerCase() + primera.slice(1), ...palabras.slice(1)].join(' ');
}

// Epónimos y abreviaturas con mayúscula fija que NO se minusculizan aunque no
// sean todo mayúsculas (nombres propios de escalas/dispositivos: "CUP / Foley",
// "Bristol", "Glasgow", "Rx"…). Sin esta lista, "Foley" salía en minúscula en
// Renal pero no en Invasivos (que arma su línea aparte, sin pasar por
// lowerProse) — inconsistencia.
// "Vt"/"Vt/kg" (volumen corriente) se escriben con V mayúscula por convención
// clínica aunque no sean todo mayúsculas.
const EPONIMOS_PRESERVADOS = new Set(['Foley', 'Bristol', 'Glasgow', 'Rx', 'Vt', 'Vt/kg', 'Hb', 'Pmax', 'Pmedia', 'Plavix', 'Nexiva', 'Qb', 'Qd', 'Qs', 'Qt']);

// Minúscula para prosa, pero conservando acrónimos en mayúsculas (VMI, CNAF,
// HLE, VNI, CPAP…): si un token es todo mayúsculas (≥2 letras) se deja igual.
// Así "Sin trastornos hidroelectrolíticos" → minúscula inicial y "Hipotenso" → "hipotenso".
// El acrónimo se evalúa sobre el NÚCLEO del token (sin puntuación), para que
// "(GSA)" o "Foley," también se preserven — antes el paréntesis/coma hacía
// fallar el test de mayúsculas y el acrónimo se minusculizaba.
function lowerProse(valor) {
  return String(valor)
    .split(' ')
    .map((w) => {
      const core = w.replace(/[^0-9A-Za-z/ÁÉÍÓÚÑáéíóúñ]/g, '');
      if (EPONIMOS_PRESERVADOS.has(core)) return w;
      return /^[A-ZÁÉÍÓÚÑ0-9/]{2,}$/.test(core) && /[A-ZÁÉÍÓÚÑ]/.test(core) ? w : w.toLowerCase();
    })
    .join(' ');
}

// Saneo de texto libre para prosa: quita el/los punto(s) final(es) que el
// usuario haya tecleado — la línea que lo envuelve ya pone su propio punto, y
// sin este saneo salían dobles puntos ("…reagudizado..") o puntos a mitad de
// oración cuando el fragmento se concatena con otros campos por coma. Nunca se
// aplica a la nota libre (esa es su propia línea, con la puntuación del autor).
function sanearTextoLibre(v) {
  return String(v).trim().replace(/\.+$/, '');
}

// Lista en prosa: coma entre ítems y conjunción antes del último ("a, b y c").
// "y" → "e" ante i-/hi- (regla ortográfica; "hie-" conserva la "y").
// Exportada: el examen físico la reusa para la línea de soportes al recibir
// (misma regla en todas las superficies, no duplicar).
export function joinProsaLista(items) {
  if (items.length <= 1) return items.join('');
  const ultimo = items[items.length - 1];
  const conj = /^(?:i|hi(?!e))/i.test(ultimo.trim()) ? 'e' : 'y';
  return `${items.slice(0, -1).join(', ')} ${conj} ${ultimo}`;
}

// Fragmento en prosa de un campo, o null. `campos` (opcional) da contexto a
// los tipos que dependen de un campo hermano (porDroga filtra por su fuente).
function camposProse(campo, valor, campos) {
  if (valor === undefined || valor === null) return null;
  if (campo.tipo === 'porDroga') {
    // Una frase por droga, con punto y coma entre drogas: "noradrenalina
    // 0,05 mcg/kg/min, titulando a la baja; vasopresina en dosis estable".
    // Caso especial (evolución real del fundador, 2026-07-30): varias drogas
    // SIN dosis con la MISMA tendencia se agrupan en una sola frase —
    // "propofol, en dosis estable; fentanilo, en dosis estable" suena a
    // formulario; "propofol y fentanilo en dosis estables" suena a evolución.
    const entradas = porDrogaEntradas(campo, valor, campos);
    if (!entradas.length) return null;
    const prefijoSoporte = campo.k === 'dvaDetalle' && entradas.length === 3 ? 'con triple DVA: ' : '';
    const sinDosis = entradas.filter((e) => !String(e.dosis || '').trim());
    const tendenciasSinDosis = new Set(sinDosis.map((e) => e.tendencia));
    if (sinDosis.length === entradas.length && sinDosis.length > 1 && tendenciasSinDosis.size === 1) {
      const t = TENDENCIA_PROSE[entradas[0].tendencia];
      const lista = joinProsaLista(entradas.map((e) => lowerProse(e.droga)));
      // Plural de la tendencia compartida ("en dosis estables"); las demás
      // tendencias ya leen bien en plural ("titulando al alza", "en retirada").
      const tPlural = entradas[0].tendencia === 'estable' ? 'en dosis estables' : t;
      return prefijoSoporte + (t ? `${lista} ${tPlural}` : lista);
    }
    return prefijoSoporte + entradas
      .map((e) => {
        const dosis = dosisConUnidad(e.droga, e.dosis);
        const partes = [lowerProse(e.droga)];
        if (dosis) partes.push(` ${dosis}`);
        if (TENDENCIA_PROSE[e.tendencia]) partes.push(`, ${TENDENCIA_PROSE[e.tendencia]}`);
        return partes.join('');
      })
      .join('; ');
  }
  if (campo.tipo === 'num') {
    const v = String(valor).trim();
    if (v === '') return null;
    // Un campo que antes era texto y pasó a número (Qt y BH final de la HD,
    // 2026-09-29) puede traer un valor viejo con palabras ("4 horas"): se
    // imprime como se imprimía el texto, sin sumarle la unidad otra vez.
    if (TIENE_LETRAS.test(v)) return `${labelProse(campo.label)}: ${v}`;
    // Coma decimal: las escalas calculadas ya imprimen "0,29"; los inputs HTML
    // numéricos traen punto ("6.2") — se unifica la IMPRESIÓN (el parseo
    // interno de bandas/interpretarCampo sigue usando parseFloat con punto).
    const vComa = v.replace('.', ',');
    // `proseFmt`: plantilla con {v} para números cuya forma "label valor unidad"
    // queda mal armada (ej: "día de tratamiento 5 día" — label y unidad repiten
    // "día"). La plantilla imprime la frase clínica completa: "día 5 de
    // tratamiento". Solo afecta la prosa; la UI (label + sufijo) no cambia.
    if (campo.proseFmt) return campo.proseFmt.replace('{v}', vComa);
    // El % va pegado al número ("SpO₂ 96%", como se escribe a mano); el resto
    // de unidades con su espacio.
    const sep = campo.unidad === '%' ? '' : ' ';
    return `${labelProse(campo.label)} ${vComa}${campo.unidad ? `${sep}${campo.unidad}` : ''}`;
  }
  if (campo.tipo === 'multi') {
    // En prosa los ítems van en minúscula (salvo acrónimos/epónimos) y con
    // conjunción final — "sedoanalgesia: propofol, esketamina y fentanilo",
    // no "Sedoanalgesia: Propofol, Esketamina, Fentanilo" a mitad de oración.
    // Un campo que antes fue select (integridad de la piel, 2026-09-29) puede
    // traer un texto suelto: se lee como lista de uno.
    valor = comoArrayMultiEscala(valor).filter((x) => typeof x === 'string' && x.trim());
    if (!valor.length) return null;
    const lista = joinProsaLista(valor.map(lowerProse));
    // proseFmt con {v}: para los multi cuyo label es pregunta de formulario
    // ("Cuáles") y no debe aparecer en la prosa ("con noradrenalina y…").
    if (campo.k === 'dva' && new Set(valor).size === 3) return `con triple DVA: ${lista}`;
    if (campo.proseFmt) return campo.proseFmt.replace('{v}', lista);
    return `${labelProse(campo.label)}: ${lista}`;
  }
  if (campo.tipo === 'text') {
    const v = sanearTextoLibre(valor);
    if (v === '') return null;
    // proseFmt con {v}: misma plantilla que num/multi, para textos cuyo label
    // es pregunta de formulario ("Fecha de toma" → "tomados el 14-07").
    if (campo.proseFmt) return campo.proseFmt.replace('{v}', v);
    return `${labelProse(campo.label)}: ${v}`;
  }
  if (String(valor).trim() === '') return null;
  // Frase autocontenida con prioridad: algunos valores ("Estable", "Progresando"…)
  // son ambiguos sueltos entre comas — `prose` los reemplaza por una frase que
  // se lee sola, sin depender del label del campo (que en `compactar` no sale).
  if (campo.prose && campo.prose[valor]) return campo.prose[valor];
  return campo.compactar ? lowerProse(valor) : `${labelProse(campo.label)} ${lowerProse(valor)}`;
}

// El campo `deposiciones` aporta "deposiciones presentes" a la prosa — pero
// si además hay Bristol marcado, su frase YA es una característica de la
// deposición ("deposiciones líquidas"...) y repetir "deposiciones presentes"
// justo al lado es redundante ("deposiciones presentes. deposiciones
// líquidas"). Cuando hay Bristol, su característica reemplaza a esa frase
// genérica — se filtra `deposiciones` de la lista antes de armar la prosa.
function esDeposicionesRedundantePorBristol(campo, campos) {
  return campo.k === 'deposiciones' && campos?.deposiciones === 'Presentes' && !!campos?.bristol;
}

// Mismo patrón para "obedece órdenes": si el usuario cuantificó el conteo
// ("obedece 3 de 5 órdenes simples"), la frase genérica "obedece algunas
// órdenes" justo al lado es redundante — el número ES el dato y la reemplaza.
function esOrdenesRedundantePorConteo(campo, campos) {
  const conteo = campos?.ordenesDe5;
  return campo.k === 'ordenes' && campos?.ordenes === 'Obedece algunas órdenes'
    && conteo !== undefined && conteo !== null && String(conteo).trim() !== '';
}

// Mismo patrón para la rama de drogas: "con drogas vasoactivas" genérico es
// redundante cuando la lista ya nombra cuáles ("con noradrenalina y
// vasopresina" lo prueba solo). "Sin drogas vasoactivas" siempre imprime.
function esDvaSoporteRedundantePorLista(campo, campos) {
  return campo.k === 'dvaSoporte' && campos?.dvaSoporte === 'Con drogas vasoactivas'
    && Array.isArray(campos?.dva) && campos.dva.length > 0;
}

// Genérico "rama" que un detalle más específico ya prueba: la tabla declara
// qué campo se suprime cuando OTRO campo (o cualquiera de una lista) tiene
// valor con contenido. Mismo espíritu que Bristol/órdenes: el dato específico
// reemplaza a la etiqueta genérica, nunca conviven en la misma oración.
const GENERICO_SUPRIMIDO_POR = [
  // "con vía aérea artificial" es redundante cuando el dispositivo ya se nombra
  { campo: 'viaAerea', si: 'Vía aérea artificial', porCampos: ['viaArtificial'] },
  // "con monitoreo hemodinámico avanzado" lo prueban sus valores
  { campo: 'monitoreoAvanzado', si: 'Con monitoreo avanzado', porCampos: ['lactato', 'pvc', 'gastoCardiaco', 'indiceCardiaco', 'evlwi', 'svcO2'] },
  // "con laboratorio del día" lo prueban los valores
  { campo: 'labHemato', si: 'Con laboratorio del día', porCampos: ['hb', 'plaquetas', 'gb', 'ran'] },
  // "en TRR continua" es redundante cuando la modalidad ya lo dice (en HDVVC…)
  { campo: 'trr', si: 'Terapia continua (TRRC)', porCampos: ['trrcModalidad'] },
];
function esGenericoSuprimido(campo, campos) {
  const regla = GENERICO_SUPRIMIDO_POR.find((r) => r.campo === campo.k);
  if (!regla || campos?.[campo.k] !== regla.si) return false;
  return regla.porCampos.some((k) => {
    const v = campos?.[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
}

// La lista "cuáles" de un multi con detalle porDroga: si TODAS las drogas
// marcadas ya tienen su tarjeta con contenido, la lista duplica los nombres
// ("con noradrenalina y vasopresina" + "Noradrenalina 0,12…; vasopresina…").
function esListaRedundantePorDetalle(campo, campos, kDetalle) {
  const lista = campos?.[campo.k];
  const det = campos?.[kDetalle];
  if (!Array.isArray(lista) || !lista.length || !det || typeof det !== 'object') return false;
  return lista.every((d) => det[d] && ((det[d].dosis && String(det[d].dosis).trim()) || det[d].tendencia));
}

// Filtro común de redundancias valor-genérico vs detalle cuantificado.
function esCampoRedundante(campo, campos) {
  return esDeposicionesRedundantePorBristol(campo, campos)
    || esOrdenesRedundantePorConteo(campo, campos)
    || esDvaSoporteRedundantePorLista(campo, campos)
    || esGenericoSuprimido(campo, campos)
    || (campo.k === 'dva' && esListaRedundantePorDetalle(campo, campos, 'dvaDetalle'))
    || (campo.k === 'sedoanalgesia' && esListaRedundantePorDetalle(campo, campos, 'sedoDetalle'));
}

// Campos de un sistema en prosa, unidos con coma (o null). Respeta el
// despliegue condicional. Se conserva para compatibilidad/tests — el armado
// real de la evolución usa `compilarSistemaDetalle` (prosa agrupada, más abajo).
export function compilarCamposProse(sistemaId, campos) {
  if (!campos) return null;
  const partes = camposVisibles(sistemaId, campos)
    .filter((c) => !esCampoRedundante(c, campos))
    .map((c) => camposProse(c, campos[c.k], campos))
    .filter(Boolean);
  return partes.length ? partes.join(', ') : null;
}

// Campos de texto libre que cierran su GRUPO como oración propia en vez de
// encadenarse por coma con el resto: son relatos ("se baja PEEP 10→8"), no
// datos puntuales — mezclados en la misma oración con comas leían como un
// run-on. `compilarSistemaDetalle` los saca del bloque corto y los cierra aparte.
const CAMPOS_TEXTO_LARGO = new Set(['ventCambios', 'curacionDetalle', 'heCorreccion', 'dolorManejo', 'dvaDetalle', 'sedoDetalle', 'ritmoManejo', 'tempManejo', 'toleranciaManejo', 'auscultacion']);

// Mayúscula inicial de una oración de prosa (comparte el heurístico de
// evolucionSistema: no toca acrónimos con mayúscula interna en la 1ª palabra).
function upperFirstProse(t) {
  if (!t) return t;
  const primera = t.split(' ')[0];
  const esAcronimo = /[A-ZÁÉÍÓÚÑ]/.test(primera.slice(1));
  return esAcronimo ? t : t.charAt(0).toUpperCase() + t.slice(1);
}

// Minúscula inicial (espejo de upperFirstProse: mismo cuidado con acrónimos)
// para la clasificación de una escala cuando se encadena por coma a media
// oración ("…, riesgo alto de LPP" y no "…, Riesgo alto de LPP").
function lowerFirstProse(t) {
  if (!t) return t;
  const primera = t.split(' ')[0];
  const esAcronimo = /[A-ZÁÉÍÓÚÑ]/.test(primera.slice(1));
  return esAcronimo ? t : t.charAt(0).toLowerCase() + t.slice(1);
}

// Fragmento de prosa de una escala YA calculada (`scoreEscala`), para
// integrarse a la oración de su grupo en vez de colgar como viñeta suelta
// ("• RASS -3: Sedación moderada" → "…, RASS -3, sedación moderada"). Ola "El
// estándar Bupa" (2026-08-15): el NORTE de nivel y la regla #541-#545 ya
// integraban la escala a la prosa; la viñeta fue implementación nuestra.
//
// Puntaje y clasificación viajan EXACTOS — se reusan `label`/`clasificacion`
// de `scoreEscala`, cero texto paralelo. Sin `clasificacion` (Glasgow, CAM,
// CAM-ICU: el valor ES el texto completo, sin desglose aparte) el fragmento es
// `texto` tal cual. Con `clasificacion`, esta ya viene SIN la coletilla de
// conducta genérica que algunas escalas agregan tras el puntaje
// ("activar medidas de prevención", "avisar" — pista de la app, no dato del
// autor; ver scorePam/scoreRitmoDiur/scoreDownton/scoreMorse arriba y
// SYSTEM_PULIR:147, que documenta la misma regla para el collage pegado a
// mano). Se recorta el punto final de la clasificación (definiciones como la
// de LPP lo traen) para no partir la oración a medio camino.
function escalaEnProsa(r, yaDichas = null) {
  if (!r) return null;
  if (r.clasificacion === undefined || r.clasificacion === null) return r.texto;
  // `r.prosa` (opcional, por scorer) manda sobre el prefijo derivado de
  // `texto`: es la forma pensada para vivir a media oración (minúscula si su
  // primera palabra no es sigla ni epónimo). upperFirstProse del join la
  // recapitaliza cuando abre la oración.
  const idx = r.texto.indexOf(': ');
  const prefijo = r.prosa || (idx >= 0 ? r.texto.slice(0, idx) : r.texto);
  // String() defensivo: RASS cae a `vals.rass` crudo (número) cuando el valor
  // no matchea ninguna opción de RASS_OPTS (draft con formato viejo); no debe
  // reventar la compilación, solo imprimirse tal cual.
  const clasificacion = lowerFirstProse(String(r.clasificacion).replace(/\.\s*$/, ''));
  // Clasificación VACÍA solo llega con jsonb adulterado (la UI es de catálogo:
  // p. ej. mottling fuera de 0-5): se imprime solo el puntaje, jamás una coma
  // colgante (misma regla que fraseLesion #642: jsonb adulterado no imprime).
  if (!clasificacion) return prefijoProsa(prefijo);
  // La clasificación de un valor NORMAL no viaja al documento (panel ciego del
  // 2026-08-16: tres de cinco jueces señalaron "en rango" y "PAM 77 mmHg, en
  // rango" como la firma de la máquina, "el texto valida su propio número como
  // si lo imprimiera una calculadora"). "En rango" no es algo que la enfermera
  // constató: es el veredicto de la app sobre su propio cálculo. Misma frontera
  // que el PR E trazó al sacar la conducta genérica, corrida un escalón. Lo que
  // NO es normal sí viaja completo, porque ahí la categoría agrega información
  // real ("riesgo alto" de Braden, "dolor significativo"). El NORTE lo hace
  // igual: cuando el ritmo diurético está mal escribe "oligúrico con ritmo
  // bajo, de 0.26 ml kg hr"; cuando está bien, no escribe nada.
  if (r.nivel === 'normal') return prefijoProsa(prefijo);
  // Si la clasificación repite lo ya dicho por un campo de la misma oración
  // ("Sedado, SAS 3, sedado"), se queda solo el puntaje: mismo criterio de
  // esCampoRedundante (dedupe de la frase repetida, jamás del dato). La
  // comparación es por CONTENCIÓN y no por igualdad, porque el caso real del
  // panel era "con objetivo de sedación moderada, RASS -3, sedación moderada":
  // el campo dice más que la clasificación y la contiene. El puntaje jamás se
  // omite.
  const clave = clasificacion.toLowerCase();
  if (yaDichas && [...yaDichas].some((dicho) => dicho.includes(clave))) {
    return prefijoProsa(prefijo);
  }
  return `${prefijoProsa(prefijo)}, ${clasificacion}`;
}

// El prefijo de un score puede traer el separador visual " · " de las
// etiquetas de UI (ritmo diurético: "Diuresis 100 ml · ritmo 0,10 ml/kg/h");
// en prosa integrada ese punto medio es artefacto de etiqueta, no puntuación
// de evolución: se normaliza a coma (norma de coherencia). Los valores no se
// tocan.
function prefijoProsa(prefijo) {
  return String(prefijo).replace(/\s+·\s+/g, ', ');
}

// Prosa agrupada de un sistema: recorre `gruposDe(sistemaId)` EN ORDEN y arma,
// por cada grupo, UNA oración con los campos cortos y las escalas de ESE
// grupo, todos unidos por coma (antes las escalas eran viñetas sueltas al
// final del bloque, lejos del tema que miden — y desde la ola "El estándar
// Bupa" 2026-08-15, lejos incluso de la oración de su propio grupo: "la
// escala va en la oración del grupo al que pertenece", como el NORTE de nivel
// integra "RASS -5, CPOT menor de 3 puntos"). Si el grupo no tiene campos
// respondidos pero sí escala, la oración del grupo ES la escala en prosa (sin
// viñeta). Los campos de texto largo (CAMPOS_TEXTO_LARGO) cierran el grupo
// como oración propia, para no encadenarse por coma a lo anterior.
// Campos sin `grupo` (si los hay) se compilan al final, en una oración suelta.
// Devuelve { lineas: string[] } — cada línea es una oración, en el orden en
// que deben imprimirse dentro del bloque del sistema.
//
// `opts.grupos` (opcional, ola "El estándar Bupa"): compila SOLO esos grupos,
// para que una superficie pueda mostrar la línea viva del pedacito que acaba de
// contestar (el chequeo general enseña "En tu evolución (Neurológico): sedado…"
// bajo su sección de Conciencia). Es la MISMA función del build recortada, no un
// texto paralelo: preview y documento no pueden divergir. Sin `opts`, el
// comportamiento y la salida son idénticos a los de siempre.
export function compilarSistemaDetalle(sistemaId, campos, escalas, opts = {}) {
  const lineas = [];
  if (!campos && !escalas) return { lineas };
  const soloGrupos = Array.isArray(opts.grupos) ? opts.grupos : null;
  // Campos que NO se narran aunque tengan valor: una fuente MÁS específica ya
  // los dijo mejor en el mismo bloque. Nació con el puente lesión de piel →
  // campos del tegumentario (ola "El estándar Bupa"): la lesión escribe su
  // frase completa en la nota ("LPP categoría II en sacro, de 3 x 2 cm,
  // presente al recibir, cobertura con hidrocoloide") y los campos que ese
  // mismo puente sembró repetirían el dato en la línea de arriba. El campo se
  // llena igual (la Mesa no vuelve a preguntarlo y el mínimo clínico queda
  // cerrado); lo que se omite es el ECO en la prosa. Quien llama decide: sin
  // `omitir`/`omitirEscalas`, la salida es byte a byte la de siempre.
  //
  // `omitirEscalas` es lo mismo para una escala: la severidad de la DAI se
  // puntúa dentro de la lesión y su frase ya dice la categoría, así que narrar
  // la escala encima la repetía en dos estilos. La Braden NO se omite nunca:
  // la nota no la dice en ninguna parte y es la única portadora de su riesgo.
  const omitir = new Set(Array.isArray(opts.omitir) ? opts.omitir : []);
  const omitirEscalas = new Set(Array.isArray(opts.omitirEscalas) ? opts.omitirEscalas : []);

  // Oración de un grupo: campos cortos + escalas del grupo (o sin grupo,
  // cuando `grupo` es null), todos unidos por coma en UNA sola oración; los
  // campos de texto largo cierran aparte.
  const compilarGrupo = (camposDelGrupo, grupo) => {
    const cortos = camposDelGrupo.filter((c) => !CAMPOS_TEXTO_LARGO.has(c.k)
      && !omitir.has(c.k) && !esCampoRedundante(c, campos));
    const largos = camposDelGrupo.filter((c) => CAMPOS_TEXTO_LARGO.has(c.k) && !omitir.has(c.k));
    // Orden narrativo dentro de la oración (ORDEN_PROSA_GRUPO): cada escala
    // junto a lo que describe (Glasgow con la conciencia, RASS con la
    // sedación, EVA/CPOT con el dolor). Sin tabla, el orden de declaración.
    const ordenGrupo = ORDEN_PROSA_GRUPO[sistemaId]?.[grupo] || null;
    const rango = (id) => {
      if (!ordenGrupo) return 0;
      const i = ordenGrupo.indexOf(id);
      return i >= 0 ? i : ordenGrupo.length + 1;
    };
    const cortosConProsa = cortos.map((c) => ({ id: c.k, texto: camposProse(c, campos[c.k], campos) })).filter((x) => x.texto);
    const proseCortos = cortosConProsa.map((x) => x.texto);
    // El grupo de examen físico cierra el sistema NARRADO, como la evolución
    // real del fundador: "Al examen físico: pupilas isocóricas, fotorreactivas."
    // (dictado 2026-07-31; gruposDe ya lo puso al final).
    const esExamen = grupo && GRUPO_EXAMEN_FISICO[sistemaId] === grupo;
    // Lo ya dicho por los campos del grupo, para que la clasificación de una
    // escala no lo repita en la misma oración (ver escalaEnProsa).
    const yaDichas = new Set(proseCortos.map((p) => String(p).toLowerCase()));
    // Una escala PUNTUADA se imprime aunque su campo general no esté respondido
    // (revisión 2026-09-07: el CPOT documentado desaparecía sin la selección
    // de dolor). No se completa el campo general por ella.
    const visibles = new Set(escalasVisibles(sistemaId, campos).map((e) => e.id));
    const escalasGrupo = escalasDe(sistemaId)
      .filter((e) => visibles.has(e.id) || escalaConValores(e.id, escalas?.[e.id]))
      .filter((e) => (grupo ? e.grupo === grupo : !e.grupo))
      .filter((e) => !omitirEscalas.has(e.id))
      .map((e) => ({ id: e.id, texto: escalaEnProsa(scoreEscala(e.id, escalas?.[e.id]), yaDichas) }))
      .filter((x) => x.texto);
    // Sin tabla: campos y luego escalas, como siempre (sort estable).
    const partes = [...cortosConProsa, ...escalasGrupo]
      .map((x, i) => ({ ...x, i }))
      .sort((a, b) => rango(a.id) - rango(b.id) || a.i - b.i)
      .map((x) => x.texto);
    if (partes.length) {
      lineas.push(esExamen
        ? `Al examen físico: ${partes.join(', ')}.`
        : `${upperFirstProse(partes.join(', '))}.`);
    }
    largos.forEach((c) => {
      const p = camposProse(c, campos[c.k], campos);
      if (p) lineas.push(`${upperFirstProse(p)}.`);
    });
  };

  for (const grupo of gruposDe(sistemaId)) {
    if (soloGrupos && !soloGrupos.includes(grupo)) continue;
    const camposDelGrupo = camposVisibles(sistemaId, campos).filter((c) => c.grupo === grupo);
    compilarGrupo(camposDelGrupo, grupo);
  }

  // Con recorte por grupos, lo que no tiene grupo no pertenece a ninguno de los
  // pedidos: sale del recorte.
  if (soloGrupos) return { lineas };

  // Campos sin grupo (sistemas no seccionados, o campos sueltos) → oración
  // final. Defensivo: si además hubiera una escala sin `grupo` (hoy todas lo
  // declaran), compilarGrupo la recoge en la MISMA oración — nunca desaparece
  // en silencio aunque no haya campos sueltos que la acompañen.
  const sinGrupo = camposVisibles(sistemaId, campos).filter((c) => !c.grupo);
  const hayEscalaSinGrupo = escalasVisibles(sistemaId, campos).some((e) => !e.grupo);
  if (sinGrupo.length || hayEscalaSinGrupo) compilarGrupo(sinGrupo, null);

  return { lineas };
}

// ── Ecos entre sistemas ───────────────────────────────────────────────────────
// Un dato registrado en UN sistema que OTRO sistema necesita decir (dictado
// del fundador 2026-07-31: "si en neuro inicio SHT con objetivo de natremia
// 150, en renal debe aparecer que está con sodio de 150"). Reglas duras:
// deterministas, SOLO hechos ya registrados (jamás conducta ni inferencia
// clínica nueva), y el eco calla si el sistema destino ya declara el concepto
// por su cuenta (no duplicar ni pisar la voz del autor). La tabla es
// extensible: cada eco nuevo se agrega acá y se fija por test.
const comaDec = (v) => String(v).trim().replace('.', ',');
export function ecosEntreSistemas(sistemas = {}) {
  const campos = (id) => sistemas?.[id]?.campos || {};
  const out = {};
  const push = (id, linea) => { (out[id] = out[id] || []).push(linea); };

  // Neuro → Renal: terapia osmótica con objetivo de natremia. El manejo vive
  // en neuro; renal documenta el trastorno hidroelectrolítico INTENCIONAL.
  const n = campos('neuro');
  if (n.osmoterapia === 'Suero hipertónico (SHT)' || n.osmoterapia === 'Manitol') {
    const con = n.osmoterapia === 'Manitol' ? 'manitol' : (n.oshtConcentracion || 'suero hipertónico');
    let linea = `En manejo con terapia osmótica (${con})`;
    if (String(n.osmoNatremiaObjetivo ?? '').trim() !== '') linea += `, con objetivo de natremia de ${comaDec(n.osmoNatremiaObjetivo)} mEq/L`;
    if (String(n.osmoNatremiaControl ?? '').trim() !== '') linea += `; última natremia ${comaDec(n.osmoNatremiaControl)} mEq/L`;
    push('renal', `${linea}. Detalle en neurológico.`);
  }

  // Renal → Hemodinamia: una TRR en curso afecta la volemia y la presión.
  // Calla si hemodinamia ya declaró su soporte extracorpóreo a mano.
  const r = campos('renal');
  const h = campos('hemodinamia');
  const TRR_ECO = {
    'Hemodiálisis intermitente': 'En hemodiálisis intermitente',
    'Terapia continua (TRRC)': 'En TRR continua',
    'Peritoneodiálisis': 'En peritoneodiálisis',
  };
  if (TRR_ECO[r.trr] && !h.soporteExtracorporeo) {
    push('hemodinamia', `${TRR_ECO[r.trr]}; detalle de la terapia en renal.`);
  }

  return out;
}

// ¿Hay escalas o campos visibles con contenido en este sistema?
export function sistemaTieneDetalle(sistemaId, escalas, campos) {
  if (escalasDe(sistemaId).some((e) => escalaConValores(e.id, escalas?.[e.id]))) return true;
  return camposVisibles(sistemaId, campos).some((c) => campoConValor(c, campos?.[c.k]));
}

// ¿Este valor DECLARA normalidad? Lo decide el CATÁLOGO mismo (la `nota.nivel`
// del campo: 'Sin sedación', 'Sin dolor', 'Aire ambiente', 'Vía aérea nativa' y
// 'Sin drogas vasoactivas' declaran nivel 'normal') — cero catálogo paralelo,
// norma de máxima coherencia. Un valor sin nota (dosis, BIS, texto de manejo)
// NO es normal: solo acompaña a una rama positiva.
//
// Fuente ÚNICA de dos consumidores: el puente de soportes (examenFisico.js,
// que no siembra sobre una basal firmada si su aporte es todo normal) y el
// reconocimiento de "esto lo sembró el puente y sigue diciendo normal" de la
// Mesa (evolucionSistema.js). Si se separaran, el chip y el documento podrían
// discrepar sobre el mismo sistema.
export function valorNormalDeCampo(sistemaId, k, v) {
  const def = (CAMPOS_POR_SISTEMA[sistemaId] || []).find((c) => c.k === k);
  return def?.notas?.[String(v)]?.nivel === 'normal';
}

// Espejo del anterior para una ESCALA: ¿su resultado declara normalidad? Lo
// decide el propio `score` de la escala (el `nivel` que ya calcula: Braden 20
// es 'normal', Braden 10 es 'severo'), cero catálogo paralelo. Una escala a
// medio llenar no computa y por lo tanto NO es normal: no se afirma nada de un
// dato incompleto.
//
// Nació con el puente escalas del examen → escalas de la Mesa (ola "El estándar
// Bupa"): desde que un puente puede sembrar escalas, la Mesa necesita
// distinguir "esto lo sembró el puente y sigue diciendo normal" de "esto lo
// puntuó la enfermera", igual que ya lo hace con los campos.
export function valorNormalDeEscala(escalaId, vals) {
  return scoreEscala(escalaId, vals)?.nivel === 'normal';
}

